// ============================================================
// SUMMER DAY CAMP — STAFF PORTAL  v4
// Firebase Auth + Firestore + Storage · pdf-lib · Signature
// Doc types: upload | typed form | fillable PDF
// ============================================================

import React, { useState, useEffect, useCallback, useRef } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore, collection, doc, addDoc, setDoc, getDoc, getDocs,
  updateDoc, onSnapshot, query, orderBy, serverTimestamp, where,
} from "firebase/firestore";
import {
  getStorage, ref, uploadBytesResumable, getDownloadURL, getBytes,
} from "firebase/storage";
import SignatureCanvas from "react-signature-canvas";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import * as XLSX from "xlsx";

// ─── FIREBASE ─────────────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyA-1mmcBP8aBgHZorHxFLJ-yS-rtZ72SO4",
  authDomain: "sdc-employment.firebaseapp.com",
  projectId: "sdc-employment",
  storageBucket: "sdc-employment.firebasestorage.app",
  messagingSenderId: "284401599826",
  appId: "1:284401599826:web:b2e338e3c6eaf987cc668b",
  measurementId: "G-BC79FL71SG",
};
const firebaseApp = initializeApp(firebaseConfig);
const auth    = getAuth(firebaseApp);
const db      = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const GAS_URL      = "https://script.google.com/macros/s/AKfycbwl6ECxOmwbcV3oCecBXJT9ha12mBZFcuR7JlNqXUb4-VWBbmRAqJPLGVmT3i2mBT1y8w/exec";
const ADMIN_EMAIL  = "cody@rogersrecreation.com";
const CALENDAR_URL = "https://calendar.app.google/s6Nd4G8uVvozj9Po8";

// ─── EMAIL ────────────────────────────────────────────────────────────────────
async function sendEmail(to, subject, body) {
  try {
    await fetch(GAS_URL, { method:"POST", mode:"no-cors",
      headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ to, subject, body }) });
  } catch(e) { console.warn("Email:", subject); }
}

// ─── FILE UPLOAD ──────────────────────────────────────────────────────────────
async function uploadFile(path, file, onProgress) {
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(ref(storage, path), file);
    task.on("state_changed",
      s => onProgress?.(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
      reject,
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });
}

async function uploadBlob(path, blob, onProgress) {
  return uploadFile(path, blob, onProgress);
}

// Load a PDF from Firebase Storage using the Firebase SDK (no CORS issues)
// Falls back to XHR if the URL isn't a Firebase Storage URL
async function loadPdfBytes(url) {
  // Strategy 1: Firebase SDK getBytes — authenticated, no CORS config needed
  // Works for any firebasestorage.googleapis.com or firebasestorage.app URL
  if (url && (url.includes("firebasestorage.googleapis.com") || url.includes("firebasestorage.app"))) {
    try {
      // Extract the storage path from the download URL
      // URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encoded-path}?alt=media&token=...
      const urlObj = new URL(url);
      const pathMatch = urlObj.pathname.match(/\/o\/(.+)$/);
      if (pathMatch) {
        const storagePath = decodeURIComponent(pathMatch[1]);
        const storageRef = ref(storage, storagePath);
        const arrayBuffer = await getBytes(storageRef);
        return arrayBuffer;
      }
    } catch(e) {
      console.warn("Firebase SDK fetch failed, trying XHR:", e.message);
    }
  }

  // Strategy 2: XHR fallback (requires CORS config on bucket)
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.responseType = "arraybuffer";
    xhr.timeout = 15000;
    xhr.onload = () => xhr.status === 200
      ? resolve(xhr.response)
      : reject(new Error(`HTTP ${xhr.status}`));
    xhr.onerror   = () => reject(new Error("CORS_BLOCKED"));
    xhr.ontimeout = () => reject(new Error("timeout"));
    xhr.send();
  });
}

// ─── PDF GENERATION — typed form ─────────────────────────────────────────────
async function generateFormPDF(docName, fields, values, sigDataUrl, applicantName) {
  const pdfDoc = await PDFDocument.create();
  const page   = pdfDoc.addPage([612, 792]);
  const font   = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold   = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const { width, height } = page.getSize();

  // Header
  page.drawRectangle({ x:0, y:height-70, width, height:70, color: rgb(0.06,0.13,0.15) });
  page.drawText("Summer Day Camp", { x:30, y:height-30, size:18, font:bold, color:rgb(1,1,1) });
  page.drawText(docName, { x:30, y:height-52, size:11, font, color:rgb(0.96,0.87,0.84) });

  // Meta
  let y = height - 100;
  const drawLabel = (label, value) => {
    page.drawText(label+":", { x:40, y, size:9, font:bold, color:rgb(0.4,0.4,0.4) });
    page.drawText(value||"", { x:160, y, size:10, font, color:rgb(0.06,0.13,0.15) });
    y -= 22;
  };
  drawLabel("Applicant", applicantName);
  drawLabel("Date", new Date().toLocaleDateString());
  y -= 10;
  page.drawLine({ start:{x:40,y}, end:{x:width-40,y}, thickness:0.5, color:rgb(0.85,0.85,0.85) });
  y -= 20;

  // Fields
  for (const f of fields) {
    if (f.type === "info" || f.type === "link") continue; // display-only, not employee responses
    if (y < 120) { page.drawText("(continued on next page)", {x:40,y,size:9,font,color:rgb(0.6,0.6,0.6)}); break; }
    page.drawText(f.label, { x:40, y, size:10, font:bold, color:rgb(0.15,0.25,0.3) });
    y -= 16;
    const val = values[f.id] !== undefined && values[f.id] !== null
      ? String(values[f.id] === true ? "[X] Yes" : values[f.id] === false ? "[ ] No" : values[f.id])
      : "-";
    // wrap long values
    const words = val.split(" ");
    let line = "";
    for (const w of words) {
      if ((line+" "+w).length > 72) {
        page.drawText(line, { x:54, y, size:10, font, color:rgb(0.15,0.15,0.15) });
        y -= 15; line = w;
      } else { line = line ? line+" "+w : w; }
    }
    if (line) { page.drawText(line, { x:54, y, size:10, font, color:rgb(0.15,0.15,0.15) }); y -= 15; }
    y -= 10;
    page.drawLine({ start:{x:40,y:y+4}, end:{x:width-40,y:y+4}, thickness:0.3, color:rgb(0.92,0.92,0.92) });
    y -= 14;
  }

  // Signature
  if (sigDataUrl) {
    y = Math.min(y, 180);
    page.drawText("Signature", { x:40, y:y+10, size:9, font:bold, color:rgb(0.4,0.4,0.4) });
    const sigImg  = await pdfDoc.embedPng(sigDataUrl);
    const sigDims = sigImg.scaleToFit(220, 60);
    page.drawImage(sigImg, { x:40, y:y-sigDims.height, width:sigDims.width, height:sigDims.height });
    page.drawLine({ start:{x:40,y:y-sigDims.height-4}, end:{x:260,y:y-sigDims.height-4}, thickness:0.5, color:rgb(0.3,0.3,0.3) });
    y = y - sigDims.height - 20;
    page.drawText(`Signed: ${new Date().toLocaleString()}`, { x:40, y, size:8, font, color:rgb(0.5,0.5,0.5) });
  }

  const bytes = await pdfDoc.save();
  return new Blob([bytes], { type:"application/pdf" });
}

// ─── STATUS CONFIG ────────────────────────────────────────────────────────────
const STATUSES = {
  applied:             { label:"Applied",             color:"#3B82F6", light:"#EFF6FF", icon:"📄" },
  interview_requested: { label:"Interview Requested", color:"#8B5CF6", light:"#F5F3FF", icon:"📨" },
  interview_scheduled: { label:"Interview Scheduled", color:"#F59E0B", light:"#FFFBEB", icon:"📅" },
  interviewed:         { label:"Interviewed",         color:"#6366F1", light:"#EEF2FF", icon:"🎤" },
  offer_sent:          { label:"Offer Sent",          color:"#0EA5E9", light:"#F0F9FF", icon:"💼" },
  offer_accepted:      { label:"Offer Accepted",      color:"#10B981", light:"#ECFDF5", icon:"✅" },
  offer_declined:      { label:"Offer Declined",      color:"#F97316", light:"#FFF7ED", icon:"↩️" },
  hired:               { label:"Hired / Onboarding",  color:"#10B981", light:"#ECFDF5", icon:"🎉" },
  rejected:            { label:"Not Selected",        color:"#EF4444", light:"#FEF2F2", icon:"✗"  },
};

// ─── FACTORY DEFAULTS ─────────────────────────────────────────────────────────
// docType: "upload" | "form" | "pdf_fill"
const FACTORY_DOCS = [
  { id:"d0", name:"Emergency Contact Form",               docType:"form",     templateUrl:null, fields:[
    {id:"f0",label:"Emergency Contact Name",      type:"text"},
    {id:"f1",label:"Relationship to Employee",    type:"text"},
    {id:"f2",label:"Primary Phone",               type:"tel"},
    {id:"f3",label:"Secondary Phone",             type:"tel"},
    {id:"f4",label:"Email Address",               type:"email"},
    {id:"f5",label:"Home Address",                type:"textarea"},
    {id:"f6",label:"Medical Conditions / Allergies to be aware of", type:"textarea"},
  ], requireSignature:true },
  { id:"d1", name:"W-4 Federal Tax Withholding",          docType:"pdf_fill", templateUrl:null, fields:[], requireSignature:true },
  { id:"d2", name:"I-9 Employment Eligibility",           docType:"pdf_fill", templateUrl:null, fields:[], requireSignature:true },
  { id:"d4", name:"Staff Handbook Acknowledgment",        docType:"form",     templateUrl:null, fields:[
    {id:"f0",label:"I have read and understand the Summer Day Camp Staff Handbook", type:"checkbox"},
    {id:"f1",label:"I agree to follow all policies and procedures",                type:"checkbox"},
    {id:"f2",label:"Any questions or concerns", type:"textarea"},
  ], requireSignature:true },
  { id:"d5", name:"High School Diploma or GED Copy",      docType:"upload",   templateUrl:null, fields:[] },
  { id:"d7", name:"Reference Check Verification Form",    docType:"upload",   templateUrl:null, fields:[] },
];

const FACTORY_QUESTIONS = [
  { id:"q1", label:"Tell us about your experience working with children ages 5–12.", type:"textarea" },
  { id:"q2", label:"How would you handle a conflict between two campers?", type:"textarea" },
  { id:"q3", label:"Describe a time you handled an emergency or unexpected situation with youth.", type:"textarea" },
  { id:"q4", label:"What activities or programs would you bring to our camp?", type:"textarea" },
  { id:"q5", label:"How do you ensure the safety and wellbeing of children in your care?", type:"textarea" },
  { id:"q6", label:"Are you available for the full camp season (June 16 – August 15)?", type:"yesno", options:["Yes","No","Partially"] },
  { id:"q7", label:"What is your philosophy on discipline and positive reinforcement?", type:"textarea" },
  { id:"q8", label:"Upload your resume (PDF or Word)", type:"upload" },
];

// ─── DESIGN ───────────────────────────────────────────────────────────────────
const C = {
  sand:"#F5EDD6", amber:"#F59E0B", amber2:"#D97706",
  sky:"#0EA5E9",  navy:"#0F2027",  navy2:"#1A3545",
  green:"#10B981", red:"#EF4444", mid:"#6B7280",
  light:"#F9FAFB", border:"#E5E7EB",
};
const H = "'Playfair Display', Georgia, serif";
const B = "'DM Sans', system-ui, sans-serif";

const DOC_TYPE_META = {
  upload:      { label:"File Upload",      icon:"📎", color:"#6366F1", desc:"Applicant uploads a file (diploma, certificate, etc.)" },
  form:        { label:"Typed Form",       icon:"✏️", color:"#10B981", desc:"Applicant fills custom fields on-screen; saves as PDF" },
  pdf_fill:    { label:"Fillable PDF",    icon:"📋", color:"#0EA5E9", desc:"Upload a PDF template; applicant fills it in-browser and signs" },
  bg_workflow: { label:"BG Check Workflow", icon:"🔍", color:"#7C3AED", desc:"Multi-step background check, fingerprinting & maltreatment clearance workflow" },
};

// Background check workflow step definitions
const injectFonts = () => {
  if (document.getElementById("sdc-fonts")) return;
  const l = document.createElement("link");
  l.id="sdc-fonts"; l.rel="stylesheet";
  l.href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@400;500;600&display=swap";
  document.head.appendChild(l);
  const s = document.createElement("style");
  s.textContent=`
    *{box-sizing:border-box;margin:0;padding:0}
    ::-webkit-scrollbar{width:6px} ::-webkit-scrollbar-track{background:${C.navy}}
    ::-webkit-scrollbar-thumb{background:${C.amber};border-radius:3px}
    @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
    @keyframes slideIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
    .fu{animation:fadeUp .4s ease forwards} .si{animation:slideIn .3s ease forwards}
    input,textarea,select{font-family:${B}} button{cursor:pointer;font-family:${B}}
    a{color:${C.sky}} iframe{border:none}
    canvas{touch-action:none}
  `;
  document.head.appendChild(s);
};

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function Btn({children,onClick,bg=C.amber,color=C.navy,style={},disabled=false,size="md",full=false}){
  const pad=size==="sm"?"7px 14px":size==="lg"?"13px 30px":"10px 20px";
  const fs=size==="sm"?12:size==="lg"?15:13;
  return(
    <button onClick={onClick} disabled={disabled} style={{
      background:disabled?C.border:bg,color:disabled?C.mid:color,
      border:"none",borderRadius:8,padding:pad,fontSize:fs,fontWeight:600,
      transition:"filter .15s,opacity .15s",opacity:disabled?.65:1,
      whiteSpace:"nowrap",width:full?"100%":"auto",textAlign:"center",...style}}
      onMouseEnter={e=>!disabled&&(e.currentTarget.style.filter="brightness(1.1)")}
      onMouseLeave={e=>!disabled&&(e.currentTarget.style.filter="none")}>
      {children}
    </button>
  );
}

function Inp({label,value,onChange,type="text",placeholder="",required=false,rows,note,inputStyle}){
  // fontSize:16 minimum on mobile prevents iOS from auto-zooming into inputs
  const base={width:"100%",background:"#fff",border:`1.5px solid ${C.border}`,borderRadius:8,
    padding:"10px 13px",fontSize:14,color:C.navy,outline:"none",transition:"border-color .2s",
    WebkitAppearance:"none",...(inputStyle||{})};
  return(
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      {label&&<label style={{fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.06em"}}>
        {label}{required&&<span style={{color:C.amber2}}> *</span>}
      </label>}
      {rows
        ?<textarea rows={rows} value={value} onChange={onChange} placeholder={placeholder}
            style={{...base,resize:"vertical"}}
            onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
        :<input type={type} value={value} onChange={onChange} placeholder={placeholder} style={base}
            onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
      }
      {note&&<p style={{fontSize:11,color:C.mid,lineHeight:1.5}}>{note}</p>}
    </div>
  );
}

function Badge({status}){
  const s=STATUSES[status]||{label:status,color:C.mid,light:C.light,icon:"•"};
  return(
    <span style={{display:"inline-flex",alignItems:"center",gap:5,background:s.light,color:s.color,
      border:`1px solid ${s.color}33`,borderRadius:20,padding:"4px 11px",fontSize:11,fontWeight:600}}>
      {s.icon} {s.label}
    </span>
  );
}

function Card({children,style={},className=""}){
  return(
    <div className={className} style={{background:"#fff",borderRadius:14,border:`1px solid ${C.border}`,
      boxShadow:"0 2px 16px rgba(0,0,0,.07)",overflow:"hidden",...style}}>
      {children}
    </div>
  );
}

function Toast({msg,type,onClose}){
  useEffect(()=>{const t=setTimeout(onClose,4200);return()=>clearTimeout(t)},[onClose]);
  const bg=type==="error"?C.red:type==="info"?C.sky:C.green;
  return(
    <div className="si" style={{position:"fixed",top:24,right:24,zIndex:9999,background:bg,color:"#fff",
      padding:"13px 18px",borderRadius:10,fontSize:13,fontFamily:B,fontWeight:500,
      boxShadow:"0 8px 32px rgba(0,0,0,.22)",display:"flex",alignItems:"center",gap:10,maxWidth:360}}>
      <span style={{flex:1}}>{msg}</span>
      <button onClick={onClose} style={{background:"none",border:"none",color:"#fff",fontSize:20,lineHeight:1}}>×</button>
    </div>
  );
}

function Sun({size=36}){
  return(
    <div style={{width:size,height:size,borderRadius:"50%",
      background:`radial-gradient(circle at 35% 35%,#FDE68A,${C.amber})`,
      boxShadow:`0 0 ${size/2}px ${C.amber}66`,display:"flex",alignItems:"center",
      justifyContent:"center",fontSize:size*.52,flexShrink:0,userSelect:"none"}}>☀</div>
  );
}

function ProgressBar({pct,color=C.amber}){
  return(
    <div style={{height:7,background:C.border,borderRadius:4}}>
      <div style={{height:"100%",background:pct===100?C.green:color,borderRadius:4,
        width:pct+"%",transition:"width .5s ease"}}/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SIGNATURE PAD — draw or type
// ═══════════════════════════════════════════════════════════════════════════════
function SignaturePad({ onSave, onCancel }) {
  const [mode, setMode] = useState("draw"); // "draw" | "type"
  const [typed, setTyped] = useState("");
  const sigRef = useRef(null);

  // Patch the internal canvas context to set willReadFrequently after mount
  useEffect(() => {
    if (!sigRef.current) return;
    const canvas = sigRef.current.getCanvas();
    if (canvas) {
      // Re-get the context with willReadFrequently so getTrimmedCanvas doesn't warn
      canvas.getContext("2d", { willReadFrequently: true });
    }
  }, []);

  function getDrawnDataUrl() {
    if (!sigRef.current || sigRef.current.isEmpty()) return null;
    // getTrimmedCanvas creates a new canvas — get its context with willReadFrequently
    const trimmed = sigRef.current.getTrimmedCanvas();
    trimmed.getContext("2d", { willReadFrequently: true });
    return trimmed.toDataURL("image/png");
  }

  function getTypedDataUrl() {
    if (!typed.trim()) return null;
    const canvas = document.createElement("canvas");
    canvas.width = 400; canvas.height = 80;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, 400, 80);
    ctx.font = "italic 36px 'Brush Script MT', cursive";
    ctx.fillStyle = "#1a1a3a";
    ctx.textBaseline = "middle";
    ctx.fillText(typed, 20, 42);
    return canvas.toDataURL("image/png");
  }

  function handleSave() {
    const dataUrl = mode === "draw" ? getDrawnDataUrl() : getTypedDataUrl();
    if (!dataUrl) return;
    onSave(dataUrl);
  }

  return (
    <div style={{background:"#fff",borderRadius:12,border:`1.5px solid ${C.border}`,padding:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <p style={{fontWeight:600,color:C.navy,fontSize:14}}>Add Your Signature</p>
        <div style={{display:"flex",gap:6}}>
          {["draw","type"].map(m=>(
            <button key={m} onClick={()=>setMode(m)} style={{
              padding:"5px 14px",borderRadius:6,border:`1.5px solid ${mode===m?C.amber:C.border}`,
              background:mode===m?`${C.amber}15`:"#fff",
              color:mode===m?C.amber2:C.mid,fontFamily:B,fontWeight:600,fontSize:12}}>
              {m==="draw"?"✏️ Draw":"⌨️ Type"}
            </button>
          ))}
        </div>
      </div>

      {mode==="draw" && (
        <div>
          <div style={{border:`1.5px solid ${C.border}`,borderRadius:8,overflow:"hidden",background:"#FAFAFA"}}>
            <SignatureCanvas
              ref={sigRef}
              penColor={C.navy}
              canvasProps={{
                width:520, height:140,
                style:{display:"block",width:"100%",height:140},
                willReadFrequently: true,
              }}
            />
          </div>
          <button onClick={()=>sigRef.current?.clear()}
            style={{background:"none",border:"none",color:C.mid,fontSize:12,marginTop:6,cursor:"pointer"}}>
            Clear
          </button>
        </div>
      )}

      {mode==="type" && (
        <div>
          <input value={typed} onChange={e=>setTyped(e.target.value)}
            placeholder="Type your full name…"
            style={{width:"100%",padding:"12px 16px",border:`1.5px solid ${C.border}`,borderRadius:8,
              fontSize:28,fontStyle:"italic",fontFamily:"'Brush Script MT',cursive",color:C.navy,
              background:"#FAFAFA",outline:"none"}}
            onFocus={e=>e.target.style.borderColor=C.amber}
            onBlur={e=>e.target.style.borderColor=C.border}/>
          {typed && (
            <p style={{fontSize:11,color:C.mid,marginTop:6}}>Preview: your name will be rendered as a signature style</p>
          )}
        </div>
      )}

      <div style={{display:"flex",gap:10,marginTop:16}}>
        <Btn onClick={handleSave} bg={C.green} color="#fff">✓ Save Signature</Btn>
        <Btn onClick={onCancel} bg={C.border} color={C.mid}>Cancel</Btn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TYPED FORM WIDGET
// Renders custom fields, captures signature, generates + uploads PDF
// ═══════════════════════════════════════════════════════════════════════════════
function TypedFormWidget({ docDef, appId, applicantName, onComplete, toast }) {
  const [values,   setValues]   = useState({});
  const [sigUrl,   setSigUrl]   = useState(null);
  const [showSig,  setShowSig]  = useState(false);
  const [busy,     setBusy]     = useState(false);
  const [progress, setProgress] = useState(null);

  const set = (id, val) => setValues(p => ({ ...p, [id]: val }));

  async function handleSubmit() {
    if (docDef.requireSignature && !sigUrl) {
      toast("Please add your signature before submitting.", "error"); return;
    }
    setBusy(true); setProgress(0);
    try {
      // Generate PDF
      const blob = await generateFormPDF(docDef.name, docDef.fields||[], values, sigUrl, applicantName);
      const path = `onboarding/${appId}/${docDef.id}_${Date.now()}.pdf`;
      const url  = await uploadBlob(path, blob, p => setProgress(p));
      await onComplete({
        uploadedUrl: url, uploadedName: docDef.name+".pdf",
        received: true, receivedDate: new Date().toISOString().split("T")[0],
        uploadedBy: "applicant", formData: values,
      });
      toast("✅ " + docDef.name + " submitted!");
    } catch(e) { toast("Error: " + e.message, "error"); }
    setBusy(false); setProgress(null);
  }

  const allFilled = (docDef.fields||[]).every(f =>
    ["checkbox","info","link"].includes(f.type) ? true : (values[f.id]||"").trim()
  );

  return (
    <div>
      {(docDef.fields||[]).map(f => (
        <div key={f.id} style={{marginBottom:16}}>
          {f.type === "info" ? (
            <div style={{padding:"11px 14px",background:`${C.sky}0d`,border:`1px solid ${C.sky}33`,
              borderRadius:8,display:"flex",gap:10,alignItems:"flex-start"}}>
              <span style={{fontSize:16,flexShrink:0}}>ℹ️</span>
              <p style={{fontSize:13,color:C.navy2,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{f.label}</p>
            </div>
          ) : f.type === "link" ? (
            <div style={{padding:"11px 14px",background:`${C.amber}0d`,border:`1px solid ${C.amber}33`,
              borderRadius:8,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontSize:16,flexShrink:0}}>🔗</span>
              <div style={{flex:1}}>
                <p style={{fontSize:13,color:C.navy,fontWeight:600,marginBottom:f.url?4:0}}>{f.label}</p>
                {f.url && (
                  <a href={f.url} target="_blank" rel="noopener noreferrer"
                    style={{fontSize:12,color:C.sky,wordBreak:"break-all"}}>
                    {f.url}
                  </a>
                )}
              </div>
              <span style={{fontSize:11,padding:"3px 10px",background:`${C.amber}22`,color:C.amber2,
                borderRadius:20,fontWeight:600,whiteSpace:"nowrap"}}>
                Click to open ↗
              </span>
            </div>
          ) : f.type === "checkbox" ? (
            <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
              <input type="checkbox" checked={!!values[f.id]} onChange={e=>set(f.id,e.target.checked)}
                style={{width:17,height:17,marginTop:2,accentColor:C.green,cursor:"pointer"}}/>
              <span style={{fontSize:14,color:C.navy,lineHeight:1.5}}>{f.label}</span>
            </label>
          ) : f.type === "textarea" ? (
            <Inp label={f.label} value={values[f.id]||""} onChange={e=>set(f.id,e.target.value)} rows={3}/>
          ) : (
            <Inp label={f.label} value={values[f.id]||""} onChange={e=>set(f.id,e.target.value)}
              type={f.type||"text"} required/>
          )}
        </div>
      ))}

      {/* Signature */}
      {docDef.requireSignature && (
        <div style={{marginTop:8,marginBottom:16}}>
          {sigUrl ? (
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",
              background:"#ECFDF5",border:`1px solid #A7F3D0`,borderRadius:8}}>
              <img src={sigUrl} alt="signature" style={{height:40,maxWidth:220}}/>
              <button onClick={()=>setSigUrl(null)} style={{background:"none",border:"none",color:C.red,fontSize:12,cursor:"pointer"}}>
                Remove
              </button>
            </div>
          ) : showSig ? (
            <SignaturePad onSave={url=>{setSigUrl(url);setShowSig(false);}} onCancel={()=>setShowSig(false)}/>
          ) : (
            <button onClick={()=>setShowSig(true)} style={{display:"flex",alignItems:"center",gap:8,
              padding:"10px 16px",border:`1.5px dashed ${C.border}`,borderRadius:8,
              background:"#FAFAFA",color:C.mid,fontFamily:B,fontSize:13,cursor:"pointer",width:"100%"}}>
              ✍️ Add Signature (required)
            </button>
          )}
        </div>
      )}

      {progress !== null && (
        <div style={{marginBottom:12}}><ProgressBar pct={progress}/></div>
      )}

      <Btn onClick={handleSubmit} bg={C.green} color="#fff"
        disabled={busy || (!allFilled && (docDef.fields||[]).some(f=>f.type!=="checkbox"))}>
        {busy ? `Generating PDF… ${progress||0}%` : "Submit & Generate PDF"}
      </Btn>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// PDF FILL WIDGET — PDF.js canvas render + overlaid HTML inputs
// ═══════════════════════════════════════════════════════════════════════════════
// Friendly labels for common W-4 / I-9 AcroForm field names
const FIELD_LABELS = {
  // W-4 2020+ field names
  "topmostSubform[0].Page1[0].f1_1[0]": "First Name & Middle Initial",
  "topmostSubform[0].Page1[0].f1_2[0]": "Last Name",
  "topmostSubform[0].Page1[0].f1_3[0]": "Social Security Number",
  "topmostSubform[0].Page1[0].f1_4[0]": "Address",
  "topmostSubform[0].Page1[0].f1_5[0]": "City, State, ZIP",
  "topmostSubform[0].Page1[0].c1_1[0]": "Filing Status: Single / MFS",
  "topmostSubform[0].Page1[0].c1_2[0]": "Filing Status: Married Filing Jointly",
  "topmostSubform[0].Page1[0].c1_3[0]": "Filing Status: Head of Household",
  "topmostSubform[0].Page1[0].f1_6[0]": "Step 3: Qualifying Children Amount",
  "topmostSubform[0].Page1[0].f1_7[0]": "Step 3: Other Dependents Amount",
  "topmostSubform[0].Page1[0].f1_8[0]": "Step 3: Total",
  "topmostSubform[0].Page1[0].f1_9[0]": "Step 4a: Other Income",
  "topmostSubform[0].Page1[0].f1_10[0]": "Step 4b: Deductions",
  "topmostSubform[0].Page1[0].f1_11[0]": "Step 4c: Extra Withholding",
  "topmostSubform[0].Page1[0].f1_13[0]": "Employer Name & Address",
  "topmostSubform[0].Page1[0].f1_14[0]": "First Date of Employment",
  "topmostSubform[0].Page1[0].f1_15[0]": "Employer EIN",
};

function friendlyLabel(rawName) {
  if (FIELD_LABELS[rawName]) return FIELD_LABELS[rawName];
  // Strip AcroForm path noise for unknown fields
  const clean = rawName
    .replace(/^.*\]\./,"")          // remove leading path
    .replace(/\[\d+\]$/,"")         // remove trailing [0]
    .replace(/([a-z])([A-Z])/g,"$1 $2") // camelCase → words
    .replace(/_/g," ")
    .trim();
  return clean || rawName;
}

function isSignatureField(name) {
  return /sig|sign/i.test(name);
}

// Load PDF.js from CDN dynamically
let pdfjsLib = null;
async function getPdfjsLib() {
  if (pdfjsLib) return pdfjsLib;
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) { pdfjsLib = window.pdfjsLib; resolve(pdfjsLib); return; }
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      pdfjsLib = window.pdfjsLib;
      resolve(pdfjsLib);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function PdfFillWidget({ docDef, appId, applicantName, onComplete, toast }) {
  const [sigUrl,   setSigUrl]   = useState(null);
  const [showSig,  setShowSig]  = useState(false);
  const [busy,     setBusy]     = useState(false);
  const [progress, setProgress] = useState(null);
  const [values,   setValues]   = useState({});
  const [pages,    setPages]    = useState([]); // [{canvas, fields:[{name,type,rect,page}]}]
  const [loading,  setLoading]  = useState(false);
  const [loadErr,  setLoadErr]  = useState(null);
  const [scale,    setScale]    = useState(1);
  const containerRef = useRef();

  const set = (name, val) => setValues(p => ({ ...p, [name]: val }));

  // Render all pages + extract field positions using PDF.js
  useEffect(() => {
    if (!docDef.templateUrl) return;
    setLoading(true); setLoadErr(null); setPages([]);
    (async () => {
      try {
        const pdfjs  = await getPdfjsLib();
        const bytes  = await loadPdfBytes(docDef.templateUrl);
        const pdfDoc = await pdfjs.getDocument({ data: bytes }).promise;
        const numPages = pdfDoc.numPages;

        // Determine display scale based on container width
        const containerW = containerRef.current?.offsetWidth || 700;
        const rendered = [];

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const page = await pdfDoc.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1 });
          const displayScale = Math.min((containerW - 4) / viewport.width, 1.6);
          const scaledVp = page.getViewport({ scale: displayScale });

          // Render page to canvas
          const canvas = document.createElement("canvas");
          canvas.width  = scaledVp.width;
          canvas.height = scaledVp.height;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          await page.render({ canvasContext: ctx, viewport: scaledVp }).promise;

          // Extract annotations (form fields)
          const annotations = await page.getAnnotations();

          // Radio buttons share a fieldName but each annotation has its own exportValue.
          // Flag bit 15 (0-indexed) = Radio; bit 16 = Pushbutton.

          const fields = annotations
            .filter(a => a.subtype === "Widget" && a.fieldName && !isSignatureField(a.fieldName))
            .map(a => {
              const [x1, y1, x2, y2] = a.rect;
              const pdfH = viewport.height;
              // Base name = everything before the last [N] index
              // e.g. "topmostSubform[0].Page1[0].c1_1[0]" → base "topmostSubform[0].Page1[0].c1_1"
              const baseName = a.fieldName.replace(/\[\d+\]$/, "");
              return {
                name:        a.fieldName,
                baseName,
                type:        a.fieldType,
                flags:       a.fieldFlags || 0,
                isRadio:     !!a.radioButton,
                isCheckbox:  !!a.checkBox,
                exportValue: a.exportValue ?? a.buttonValue ?? "Yes",
                options:     a.options || [],
                label:       FIELD_LABELS[a.fieldName] || a.alternativeText || a.fieldName,
                rect: {
                  left:   x1 * displayScale,
                  top:    (pdfH - y2) * displayScale,
                  width:  (x2 - x1) * displayScale,
                  height: (y2 - y1) * displayScale,
                },
                page: pageNum,
              };
            });

          // Detect "pseudo radio groups": multiple checkboxes sharing the same baseName
          // (like W-4 filing status c1_1[0], c1_1[1], c1_1[2])
          const baseNameCounts = {};
          fields.forEach(f => {
            if (f.isCheckbox) baseNameCounts[f.baseName] = (baseNameCounts[f.baseName] || 0) + 1;
          });
          fields.forEach(f => {
            if (f.isCheckbox && baseNameCounts[f.baseName] > 1) {
              f.isPseudoRadio = true;
            }
          });

          rendered.push({ canvas, fields, scale: displayScale, pageNum });
        }

        setScale(rendered[0]?.scale || 1);
        setPages(rendered);

        // Pre-fill applicant name into likely name fields
        const init = {};
        rendered.forEach(p => p.fields.forEach(f => {
          if (/f1_1\[0\]|firstname|first.name/i.test(f.name)) init[f.name] = applicantName;
        }));
        setValues(init);

      } catch(e) {
        setLoadErr(e.message);
      }
      setLoading(false);
    })();
  }, [docDef.templateUrl, applicantName]);

  // Build + submit the final PDF
  async function handleSubmit() {
    if (!docDef.templateUrl) { toast("No PDF template uploaded yet.", "error"); return; }
    if (docDef.requireSignature && !sigUrl) { toast("Please draw your signature.", "error"); return; }
    setBusy(true); setProgress(10);
    try {
      const bytes  = await loadPdfBytes(docDef.templateUrl);
      const pdfDoc = await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false });
      const pdfPages = pdfDoc.getPages();
      const font   = await pdfDoc.embedFont(StandardFonts.Helvetica);

      // Detect XFA vs AcroForm — pdf-lib cannot fill XFA fields
      let isXfa = false;
      let acroFields = [];
      try {
        const form = pdfDoc.getForm();
        acroFields = form.getFields();
        isXfa = acroFields.length === 0;
        if (!isXfa) {
          // ACROFORM MODE: fill native PDF fields directly
          const allPageFields = [];
          pages.forEach(pg => pg.fields.forEach(f => allPageFields.push(f)));

          acroFields.forEach(field => {
            const name = field.getName();
            if (isSignatureField(name)) return;
            const meta = allPageFields.find(f => f.name === name);
            const val  = meta?.isPseudoRadio
              ? (values[meta.baseName] === meta.exportValue ? true : false)
              : values[name];
            try {
              // Duck-type each field by which methods it has — survives minification
              if (typeof field.check === "function" && typeof field.uncheck === "function") {
                // Checkbox
                val ? field.check() : field.uncheck();
              } else if (typeof field.select === "function" && typeof field.getOptions === "function") {
                // RadioGroup or Dropdown
                if (val) field.select(String(val));
              } else if (typeof field.setText === "function") {
                // TextField
                if (val !== undefined && val !== null && val !== "") field.setText(String(val));
              }
            } catch(e) { console.warn("Skip field:", name, e.message); }
          });
          try { form.flatten(); } catch(e) { console.warn("Flatten skipped:", e.message); }
        }
      } catch(e) {
        console.warn("getForm() failed, using draw-text mode:", e.message);
        isXfa = true;
      }

      if (isXfa) {
        // XFA / DRAW-TEXT MODE: stamp values directly onto page canvas
        for (const pg of pages) {
          const pdfPage = pdfPages[pg.pageNum - 1];
          if (!pdfPage) continue;
          const { height: pdfH } = pdfPage.getSize();
          for (const f of pg.fields) {
            if (f.type === "Btn") {
              const isChecked = f.isPseudoRadio
                ? values[f.baseName] === f.exportValue
                : !!values[f.name];
              if (!isChecked) continue;
              const pdfX  = f.rect.left / pg.scale;
              const pdfY  = pdfH - (f.rect.top + f.rect.height) / pg.scale;
              const pdfW  = f.rect.width  / pg.scale;
              const pdfHt = f.rect.height / pg.scale;
              const cx = pdfX + pdfW / 2;
              const cy = pdfY + pdfHt / 2;
              const r  = Math.min(pdfW, pdfHt) * 0.38;
              if (f.isPseudoRadio) {
                pdfPage.drawEllipse({ x:cx, y:cy, xScale:r, yScale:r, color:rgb(0,0,0), borderWidth:0 });
              } else {
                const pad = Math.min(pdfW, pdfHt) * 0.15;
                pdfPage.drawLine({ start:{x:pdfX+pad,y:pdfY+pad}, end:{x:pdfX+pdfW-pad,y:pdfY+pdfHt-pad}, thickness:1.5, color:rgb(0,0,0) });
                pdfPage.drawLine({ start:{x:pdfX+pdfW-pad,y:pdfY+pad}, end:{x:pdfX+pad,y:pdfY+pdfHt-pad}, thickness:1.5, color:rgb(0,0,0) });
              }
            } else {
              const val = values[f.name];
              if (!val && val !== 0) continue;
              const pdfX  = f.rect.left / pg.scale;
              const pdfY  = pdfH - (f.rect.top + f.rect.height) / pg.scale;
              const pdfW  = f.rect.width  / pg.scale;
              const pdfHt = f.rect.height / pg.scale;
              const fontSize = Math.max(7, Math.min(11, pdfHt * 0.62));
              pdfPage.drawText(String(val), {
                x: pdfX + 2, y: pdfY + (pdfHt - fontSize) / 2 + 1,
                size: fontSize, font, color: rgb(0,0,0), maxWidth: pdfW - 4,
              });
            }
          }
        }
      }

      setProgress(50);

      // Stamp signature image (both modes)
      if (sigUrl) {
        const sigPageIdx = typeof docDef.signaturePage === "number"
          ? Math.min(docDef.signaturePage, pdfPages.length - 1)
          : pdfPages.length - 1;
        const sigPage = pdfPages[sigPageIdx];
        const sigX = typeof docDef.signatureX === "number" ? docDef.signatureX : 30;
        const sigY = typeof docDef.signatureY === "number" ? docDef.signatureY : 55;
        const sigW = typeof docDef.sigWidth   === "number" ? docDef.sigWidth   : 200;
        const sigH = typeof docDef.sigHeight  === "number" ? docDef.sigHeight  : 40;
        sigPage.drawText(`Date: ${new Date().toLocaleDateString()}`,
          { x: sigX + sigW + 16, y: sigY + 4, size: 9, font, color: rgb(0.2,0.2,0.2) });
        const sigImg  = await pdfDoc.embedPng(sigUrl);
        const sigDims = sigImg.scaleToFit(sigW, sigH);
        sigPage.drawImage(sigImg, { x: sigX, y: sigY, width: sigDims.width, height: sigDims.height });
      }

      setProgress(65);
      const blob = new Blob([await pdfDoc.save()], { type:"application/pdf" });
      const path = `onboarding/${appId}/${docDef.id}_signed_${Date.now()}.pdf`;
      const url  = await uploadBlob(path, blob, p => setProgress(65 + Math.round(p * 0.35)));
      await onComplete({
        uploadedUrl: url, uploadedName: docDef.name + "_signed.pdf",
        received: true, receivedDate: new Date().toISOString().split("T")[0],
        uploadedBy: "applicant",
      });
      toast("✅ " + docDef.name + " signed and submitted!");
    } catch(e) {
      console.error("PdfFillWidget error:", e);
      toast("Error: " + e.message, "error");
    }
    setBusy(false); setProgress(null);
  }

  if (!docDef.templateUrl) {
    return (
      <div style={{padding:20,background:C.light,borderRadius:8,textAlign:"center"}}>
        <p style={{color:C.mid,fontSize:14}}>⏳ No PDF template uploaded yet. Check back soon.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <p style={{fontSize:13,color:C.mid,marginBottom:12,lineHeight:1.6}}>
        Fill in the form below, then draw your signature and click Submit. Your completed PDF will be saved automatically.
      </p>

      {loading && (
        <div style={{padding:24,textAlign:"center",color:C.mid}}>
          <div style={{fontSize:28,marginBottom:8}}>⏳</div>
          <p>Loading form…</p>
        </div>
      )}

      {loadErr && (
        loadErr === "CORS_BLOCKED" ? (
          <div style={{padding:"18px 20px",background:"#FFF7ED",border:`1.5px solid ${C.amber}`,borderRadius:10,marginBottom:14}}>
            <p style={{color:C.amber2,fontSize:14,fontWeight:700,marginBottom:8}}>⚠️ One-time setup needed: Firebase Storage CORS</p>
            <p style={{color:C.navy,fontSize:13,marginBottom:12,lineHeight:1.7}}>
              Firebase is blocking the PDF from loading due to a CORS policy. Fix it in 2 minutes using the Google Cloud Shell — no software to install:
            </p>
            <ol style={{paddingLeft:20,color:C.navy,fontSize:13,lineHeight:2.2,marginBottom:14}}>
              <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" style={{color:C.sky}}>console.cloud.google.com</a> and select your <strong>sdc-employment</strong> project</li>
              <li>Click the <strong>&gt;_ Cloud Shell</strong> button in the top-right toolbar</li>
              <li>Paste and run this command:</li>
            </ol>
            <div style={{position:"relative",marginBottom:12}}>
              <pre style={{background:"#1E293B",color:"#94A3B8",padding:"12px 16px",borderRadius:8,
                fontSize:12,fontFamily:"monospace",lineHeight:1.7,overflowX:"auto",margin:0}}>
{`echo '[{"origin":["https://employment.rogersrecreation.org","https://sdc-employment.web.app","http://localhost:3000"],"method":["GET","HEAD"],"maxAgeSeconds":3600,"responseHeader":["Content-Type","Authorization","Range"]}]' > cors.json
gsutil cors set cors.json gs://sdc-employment.firebasestorage.app`}
              </pre>
            </div>
            <p style={{color:C.mid,fontSize:12,lineHeight:1.6}}>
              After running, refresh this page and the PDF will load. This only needs to be done once.
            </p>
          </div>
        ) : (
          <div style={{padding:"12px 16px",background:"#FEF2F2",border:`1px solid #FECACA`,borderRadius:8,marginBottom:14}}>
            <p style={{color:C.red,fontSize:13,fontWeight:600,marginBottom:4}}>Could not load PDF</p>
            <p style={{color:C.red,fontSize:12,marginBottom:6}}>{loadErr}</p>
            <p style={{color:C.mid,fontSize:12}}>
              Make sure the PDF isn't encrypted. Download a fresh W-4 from{" "}
              <a href="https://www.irs.gov/pub/irs-pdf/fw4.pdf" target="_blank" rel="noopener noreferrer">irs.gov</a>.
            </p>
          </div>
        )
      )}

      {/* Canvas pages with overlaid HTML inputs */}
      {pages.map(({ canvas, fields, pageNum }) => (
        <div key={pageNum} style={{ position:"relative", marginBottom:12,
          border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden",
          lineHeight:0, boxShadow:"0 2px 12px rgba(0,0,0,.1)" }}>

          {/* Rendered PDF page */}
          <img
            src={canvas.toDataURL("image/png")}
            alt={`Page ${pageNum}`}
            style={{ display:"block", width:"100%", height:"auto", userSelect:"none" }}
          />

          {/* Overlay inputs positioned exactly on each field */}
          {fields.map(f => {
            const baseStyle = {
              position: "absolute",
              left:   f.rect.left,
              top:    f.rect.top,
              width:  f.rect.width,
              height: f.rect.height,
              fontFamily: B,
              fontSize:   Math.max(9, Math.min(13, f.rect.height * 0.62)),
              color:      C.navy,
              background: "rgba(255,252,230,0.85)",
              border:     "1.5px solid transparent",
              borderRadius: 2,
              padding:    "0 3px",
              outline:    "none",
              boxSizing:  "border-box",
              transition: "border-color .15s, background .15s",
            };

            // Pseudo-radio: multiple checkboxes sharing a baseName (e.g. W-4 filing status)
            // Store the selected exportValue under baseName so siblings auto-deselect
            if (f.type === "Btn" && f.isPseudoRadio) {
              const isSelected = values[f.baseName] === f.exportValue;
              return (
                <div
                  key={f.name}
                  onClick={() => setValues(p => ({ ...p, [f.baseName]: f.exportValue }))}
                  title={f.label}
                  style={{
                    position: "absolute",
                    left: f.rect.left, top: f.rect.top,
                    width: f.rect.width, height: f.rect.height,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <div style={{
                    width:  Math.min(f.rect.width, f.rect.height) * 0.78,
                    height: Math.min(f.rect.width, f.rect.height) * 0.78,
                    borderRadius: "50%",
                    border: `2px solid ${isSelected ? C.amber : "#aaa"}`,
                    background: isSelected ? C.amber : "rgba(255,255,255,0.75)",
                    transition: "all .15s",
                    boxShadow: isSelected ? `0 0 0 2px ${C.amber}55` : "none",
                  }}/>
                </div>
              );
            }

            // True radio button (radioButton: true in PDF.js)
            if (f.type === "Btn" && f.isRadio) {
              const isSelected = values[f.name] === f.exportValue;
              return (
                <div
                  key={f.name + "_" + f.exportValue}
                  onClick={() => set(f.name, f.exportValue)}
                  title={f.label}
                  style={{
                    position: "absolute",
                    left: f.rect.left, top: f.rect.top,
                    width: f.rect.width, height: f.rect.height,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <div style={{
                    width:  Math.min(f.rect.width, f.rect.height) * 0.78,
                    height: Math.min(f.rect.width, f.rect.height) * 0.78,
                    borderRadius: "50%",
                    border: `2px solid ${isSelected ? C.amber : "#aaa"}`,
                    background: isSelected ? C.amber : "rgba(255,255,255,0.75)",
                    transition: "all .15s",
                    boxShadow: isSelected ? `0 0 0 2px ${C.amber}55` : "none",
                  }}/>
                </div>
              );
            }

            // Standalone checkbox
            if (f.type === "Btn") {
              const checked = !!values[f.name];
              return (
                <div
                  key={f.name}
                  onClick={() => set(f.name, !checked)}
                  title={f.label}
                  style={{
                    position: "absolute",
                    left: f.rect.left, top: f.rect.top,
                    width: f.rect.width, height: f.rect.height,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <div style={{
                    width:  Math.min(f.rect.width, f.rect.height) * 0.76,
                    height: Math.min(f.rect.width, f.rect.height) * 0.76,
                    borderRadius: 3,
                    border: `2px solid ${checked ? C.amber : "#aaa"}`,
                    background: checked ? C.amber : "rgba(255,255,255,0.75)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all .15s",
                    fontSize: Math.min(f.rect.width, f.rect.height) * 0.55,
                    color: "#fff", fontWeight: 700, lineHeight: 1,
                  }}>
                    {checked ? "✓" : ""}
                  </div>
                </div>
              );
            }

            // Dropdown / select
            if (f.type === "Ch" && f.options?.length > 0) {
              return (
                <select
                  key={f.name}
                  value={values[f.name] || ""}
                  onChange={e => set(f.name, e.target.value)}
                  title={f.label}
                  style={{ ...baseStyle, cursor:"pointer" }}
                  onFocus={e => { e.target.style.borderColor = C.amber; e.target.style.background = "#fffde7"; }}
                  onBlur={e  => { e.target.style.borderColor = "transparent"; e.target.style.background = "rgba(255,252,230,0.85)"; }}
                >
                  <option value=""/>
                  {f.options.map(o => (
                    <option key={o.exportValue} value={o.exportValue}>{o.displayValue}</option>
                  ))}
                </select>
              );
            }

            // Text field (possibly multiline)
            const multiline = (f.flags & (1 << 12)) !== 0; // bit 13 = multiline
            if (multiline) {
              return (
                <textarea
                  key={f.name}
                  value={values[f.name] || ""}
                  onChange={e => set(f.name, e.target.value)}
                  title={f.label}
                  style={{ ...baseStyle, resize:"none", lineHeight:1.3, paddingTop:2 }}
                  onFocus={e => { e.target.style.borderColor = C.amber; e.target.style.background = "#fffde7"; }}
                  onBlur={e  => { e.target.style.borderColor = "transparent"; e.target.style.background = "rgba(255,252,230,0.85)"; }}
                />
              );
            }

            return (
              <input
                key={f.name}
                type="text"
                value={values[f.name] || ""}
                onChange={e => set(f.name, e.target.value)}
                title={f.label}
                placeholder={f.label}
                style={baseStyle}
                onFocus={e => { e.target.style.borderColor = C.amber; e.target.style.background = "#fffde7"; }}
                onBlur={e  => { e.target.style.borderColor = "transparent"; e.target.style.background = "rgba(255,252,230,0.85)"; }}
              />
            );
          })}
        </div>
      ))}

      {/* Signature */}
      {!loading && pages.length > 0 && docDef.requireSignature && (
        <div style={{marginBottom:16,marginTop:4}}>
          <p style={{fontSize:12,fontWeight:600,color:C.mid,textTransform:"uppercase",
            letterSpacing:"0.05em",marginBottom:8}}>Your Signature</p>
          {sigUrl ? (
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",
              background:"#ECFDF5",border:`1px solid #A7F3D0`,borderRadius:8}}>
              <img src={sigUrl} alt="signature" style={{height:44,maxWidth:240,objectFit:"contain"}}/>
              <button onClick={()=>setSigUrl(null)}
                style={{background:"none",border:"none",color:C.red,fontSize:12,cursor:"pointer"}}>
                Remove &amp; redo
              </button>
            </div>
          ) : showSig ? (
            <SignaturePad
              onSave={url => { setSigUrl(url); setShowSig(false); }}
              onCancel={() => setShowSig(false)}
            />
          ) : (
            <button onClick={() => setShowSig(true)} style={{
              display:"flex",alignItems:"center",gap:8,width:"100%",
              padding:"12px 16px",border:`1.5px dashed ${C.border}`,borderRadius:8,
              background:"#FAFAFA",color:C.mid,fontFamily:B,fontSize:13,cursor:"pointer"}}>
              ✍️ Draw Signature (required)
            </button>
          )}
        </div>
      )}

      {progress !== null && <div style={{marginBottom:12}}><ProgressBar pct={progress}/></div>}

      {!loading && (pages.length > 0 || loadErr) && (
        <Btn onClick={handleSubmit} bg={C.green} color="#fff" disabled={busy || loading} size="lg">
          {busy ? `Generating PDF… ${progress||0}%` : "✓ Sign & Submit"}
        </Btn>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// UPLOAD WIDGET — simple file upload
// ═══════════════════════════════════════════════════════════════════════════════
function UploadWidget({ docDef, appId, onComplete, toast }) {
  const [prog,  setProg]  = useState(null);
  const [busy,  setBusy]  = useState(false);
  const fileRef = useRef();

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20*1024*1024) { toast("File must be under 20 MB.", "error"); return; }
    setBusy(true); setProg(0);
    try {
      const path = `onboarding/${appId}/${docDef.id}_${Date.now()}_${file.name}`;
      const url  = await uploadFile(path, file, p => setProg(p));
      await onComplete({
        uploadedUrl: url, uploadedName: file.name,
        received: true, receivedDate: new Date().toISOString().split("T")[0],
        uploadedBy: "applicant",
      });
      toast("✅ " + docDef.name + " uploaded!");
    } catch(e) { toast("Upload failed: " + e.message, "error"); }
    setBusy(false); setProg(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <div>
      {docDef.templateUrl && (
        <div style={{marginBottom:14,padding:"10px 14px",background:`${C.sky}11`,border:`1px solid ${C.sky}33`,borderRadius:8}}>
          <a href={docDef.templateUrl} target="_blank" rel="noopener noreferrer"
            style={{fontSize:13,color:C.sky,fontWeight:600}}>
            📥 Download template / instructions
          </a>
        </div>
      )}
      <p style={{fontSize:13,color:C.mid,marginBottom:12}}>
        Upload your file below. Accepted: PDF, JPG, PNG, DOC (max 20 MB).
      </p>
      {prog !== null && <div style={{marginBottom:12}}><ProgressBar pct={prog}/></div>}
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        style={{display:"none"}} onChange={handleFile}/>
      <Btn onClick={()=>fileRef.current?.click()} disabled={busy} bg={C.amber}>
        {busy ? `Uploading ${prog||0}%…` : "📎 Choose File & Upload"}
      </Btn>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DOC CARD — used in applicant portal, expands to show widget
// ═══════════════════════════════════════════════════════════════════════════════
function ApplicantDocCard({ docDef, docState, appId, applicantName, onUpdated, toast }) {
  const [expanded, setExpanded] = useState(false);
  const uploaded = !!docState?.received;
  const meta = DOC_TYPE_META[docDef.docType] || DOC_TYPE_META.upload;

  async function handleComplete(updates) {
    await onUpdated(docDef.id, updates);
    setExpanded(false);
  }

  return (
    <div style={{borderRadius:10,marginBottom:10,border:`1.5px solid ${uploaded?"#A7F3D0":C.border}`,
      background:uploaded?"#ECFDF5":"#fff",overflow:"hidden"}}>
      {/* Header row */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",
        cursor:"pointer"}}
        onClick={()=>setExpanded(v=>!v)}>
        <div style={{width:10,height:10,borderRadius:"50%",flexShrink:0,
          background:uploaded?C.green:C.border}}/>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <p style={{fontSize:14,fontWeight:600,color:C.navy}}>{docDef.name}</p>
            <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:20,
              background:`${meta.color}15`,color:meta.color,border:`1px solid ${meta.color}33`}}>
              {meta.icon} {meta.label}
            </span>
          </div>
          {uploaded && docDef.docType!=="bg_workflow" && (
            <div style={{display:"flex",gap:12,marginTop:4,flexWrap:"wrap"}}>
              <p style={{fontSize:11,color:C.mid}}>Submitted {docState.receivedDate}</p>
              {docState.uploadedUrl && (
                <a href={docState.uploadedUrl} target="_blank" rel="noopener noreferrer"
                  style={{fontSize:11,color:C.sky,fontWeight:500}}>View submitted file</a>
              )}
            </div>
          )}
          {docDef.docType==="bg_workflow" && (
            <p style={{fontSize:11,color:C.mid,marginTop:3}}>
              {docState?.bgWorkflow?.step>=4?"⏳ Awaiting final clearance":
               docState?.bgWorkflow?.step===3?"🖐️ Fingerprints scheduled":
               docState?.bgWorkflow?.step===2?"🔢 Confirmation # received":
               docState?.bgWorkflow?.step>=1?"📋 Form submitted":"Not started"}
              {docState?.bgWorkflow?.finalApproved?" · ✅ Fully Cleared":""}
            </p>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {(()=>{
            const isBg = docDef.docType==="bg_workflow";
            const done = isBg ? !!docState?.bgWorkflow?.finalApproved : uploaded;
            const inProg = isBg && (docState?.bgWorkflow?.step||1) > 1 && !done;
            return (
              <span style={{fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:20,
                color:done?C.green:inProg?C.amber2:C.red,
                background:done?"#ECFDF5":inProg?`${C.amber}15`:"#FEF2F2",
                border:`1px solid ${done?"#A7F3D0":inProg?C.amber:"#FECACA"}`}}>
                {done?"✓ Cleared":inProg?"In Progress":"Pending"}
              </span>
            );
          })()}
          <span style={{fontSize:16,color:C.mid,transform:expanded?"rotate(90deg)":"",transition:"transform .2s"}}>›</span>
        </div>
      </div>

      {/* Widget panel — bg_workflow always shows (multi-step, tracks partial state) */}
      {expanded && docDef.docType==="bg_workflow" && (
        <div style={{padding:"0 16px 16px",borderTop:`1px solid ${C.border}`}}>
          <div style={{height:12}}/>
          <BgWorkflowApplicant docDef={docDef} docState={docState} appId={appId}
            applicantName={applicantName} onComplete={handleComplete} toast={toast}/>
        </div>
      )}

      {/* Widget panel — other types only show when not complete */}
      {expanded && !uploaded && docDef.docType!=="bg_workflow" && (
        <div style={{padding:"0 16px 16px",borderTop:`1px solid ${C.border}`}}>
          <div style={{height:12}}/>
          {docDef.docType==="form" && (
            <TypedFormWidget docDef={docDef} appId={appId} applicantName={applicantName}
              onComplete={handleComplete} toast={toast}/>
          )}
          {docDef.docType==="pdf_fill" && (
            <PdfFillWidget docDef={docDef} appId={appId} applicantName={applicantName}
              onComplete={handleComplete} toast={toast}/>
          )}
          {docDef.docType==="upload" && (
            <UploadWidget docDef={docDef} appId={appId} onComplete={handleComplete} toast={toast}/>
          )}
        </div>
      )}

      {/* Replace option for completed non-workflow docs */}
      {uploaded && expanded && docDef.docType!=="bg_workflow" && (
        <div style={{padding:"0 16px 14px",borderTop:`1px solid ${C.border}`}}>
          <div style={{height:10}}/>
          {docDef.docType==="upload" && (
            <UploadWidget docDef={docDef} appId={appId} onComplete={handleComplete} toast={toast}/>
          )}
          <p style={{fontSize:12,color:C.mid,marginTop:8}}>Submitting again will replace your existing file.</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BACKGROUND CHECK WORKFLOW — applicant-facing
// Steps: 1=form submitted → 2=confirmation# received → 3=fingerprints scheduled → 4=final clearance
// ═══════════════════════════════════════════════════════════════════════════════
function BgWorkflowApplicant({ docDef, docState, appId, applicantName, onComplete, toast }) {
  const bgState   = docState?.bgState || {};
  const step      = bgState.currentStep || "s1";
  const txnId     = bgState.transactionId || null;
  const [form,    setForm]    = useState({ f0:"",f1:"",f2:"",f3:"",f4:"",f5:false, ...(bgState.authForm||{}) });
  const [sigUrl,  setSigUrl]  = useState(bgState.authSig||null);
  const [showSig, setShowSig] = useState(false);
  const [busy,    setBusy]    = useState(false);
  const set = k => e => setForm(p=>({...p,[k]:typeof e==="boolean"?e:e.target.value}));

  // Step progress indicator
  function StepDots() {
    return (
      <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:20}}>
        {BG_STEPS.map((s,i)=>{
          const done  = BG_STEPS.indexOf(BG_STEPS.find(x=>x.id===step)) > i;
          const active= s.id===step;
          const color = done?C.green:active?"#8B5CF6":C.border;
          return (
            <React.Fragment key={s.id}>
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",flex:1}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:done?C.green:active?"#8B5CF6":C.border,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  border:`2px solid ${color}`,marginBottom:4}}>
                  <span style={{fontSize:done?11:13,color:done||active?"#fff":C.mid}}>
                    {done?"✓":s.icon}
                  </span>
                </div>
                <p style={{fontSize:9,fontWeight:600,color:active?"#8B5CF6":done?C.green:C.mid,
                  textAlign:"center",lineHeight:1.3,maxWidth:60}}>{s.label}</p>
              </div>
              {i<3&&<div style={{flex:0,width:20,height:2,background:done?C.green:C.border,marginBottom:14}}/>}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // STEP 1: Authorization form
  if (step==="s1") return (
    <div>
      <StepDots/>
      <div style={{padding:"11px 14px",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:8,marginBottom:16}}>
        <p style={{fontSize:12,color:"#5B21B6",lineHeight:1.6}}>
          <strong>Step 1 of 4:</strong> Please complete the background check authorization form below.
          This authorizes Summer Day Camp to submit your information to the State of Arkansas.
        </p>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:16}}>
        {[
          {id:"f0",label:"Full Legal Name",type:"text",placeholder:"As it appears on your ID"},
          {id:"f1",label:"Date of Birth",type:"date"},
          {id:"f2",label:"Social Security # (last 4 digits only)",type:"text",placeholder:"XXXX"},
          {id:"f3",label:"Current Address",type:"textarea",placeholder:"Street, City, State, ZIP"},
          {id:"f4",label:"Previous Addresses (past 5 years)",type:"textarea",placeholder:"List any addresses from the past 5 years"},
        ].map(f=>(
          <div key={f.id}>
            <p style={{fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>{f.label} *</p>
            {f.type==="textarea"
              ?<textarea rows={3} value={form[f.id]} onChange={set(f.id)} placeholder={f.placeholder||""}
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,
                    fontFamily:B,fontSize:13,color:C.navy,resize:"vertical",outline:"none"}}
                  onFocus={e=>e.target.style.borderColor="#8B5CF6"} onBlur={e=>e.target.style.borderColor=C.border}/>
              :<input type={f.type} value={form[f.id]} onChange={set(f.id)} placeholder={f.placeholder||""}
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,
                    fontFamily:B,fontSize:14,color:C.navy,outline:"none"}}
                  onFocus={e=>e.target.style.borderColor="#8B5CF6"} onBlur={e=>e.target.style.borderColor=C.border}/>
            }
          </div>
        ))}
        <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",
          padding:"12px 14px",background:"#F5F3FF",borderRadius:8,border:"1px solid #DDD6FE"}}>
          <input type="checkbox" checked={!!form.f5} onChange={e=>set("f5")(e.target.checked)}
            style={{width:16,height:16,accentColor:"#8B5CF6",cursor:"pointer",marginTop:1,flexShrink:0}}/>
          <span style={{fontSize:12,color:"#5B21B6",lineHeight:1.6}}>
            I authorize Summer Day Camp to conduct a background check and maltreatment registry check on my behalf with the State of Arkansas. *
          </span>
        </label>
      </div>

      {/* Signature */}
      <div style={{marginBottom:16,padding:"14px 16px",background:C.light,borderRadius:10,border:`1px solid ${C.border}`}}>
        <p style={{fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>Your Signature *</p>
        {sigUrl?(
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <img src={sigUrl} alt="sig" style={{height:44,background:"#fff",border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 10px"}}/>
            <button onClick={()=>setSigUrl(null)} style={{fontSize:12,color:C.red,background:"none",border:"none",cursor:"pointer"}}>Remove</button>
          </div>
        ):showSig?(
          <SignaturePad onSave={url=>{setSigUrl(url);setShowSig(false);}} onCancel={()=>setShowSig(false)}/>
        ):(
          <Btn onClick={()=>setShowSig(true)} bg="#8B5CF6" color="#fff">✍️ Sign Authorization</Btn>
        )}
      </div>

      <Btn disabled={busy} bg="#8B5CF6" color="#fff" onClick={async()=>{
        if(!form.f0.trim()||!form.f1||!form.f2.trim()||!form.f3.trim()||!form.f4.trim())
          { toast("Please fill all required fields.","error"); return; }
        if(!form.f5) { toast("Please check the authorization checkbox.","error"); return; }
        if(!sigUrl)  { toast("Please add your signature.","error"); return; }
        setBusy(true);
        await onComplete({ bgState:{ currentStep:"s2", authForm:form, authSig:sigUrl, authSubmittedAt:new Date().toISOString() }, received:false });
        toast("✅ Authorization submitted! We'll notify you when your transaction ID is ready.");
        setBusy(false);
      }}>
        {busy?"Submitting…":"Submit Authorization →"}
      </Btn>
    </div>
  );

  // STEP 2: Waiting for admin to submit to state and send transaction ID
  if (step==="s2") return (
    <div>
      <StepDots/>
      <div style={{padding:"16px 18px",background:"#FFF7ED",border:"1.5px solid #FCD34D",borderRadius:10,marginBottom:16,textAlign:"center"}}>
        <p style={{fontSize:24,marginBottom:8}}>⏳</p>
        <p style={{fontWeight:700,color:"#92400E",fontSize:14,marginBottom:6}}>Authorization Received!</p>
        <p style={{fontSize:13,color:"#78350F",lineHeight:1.7}}>
          Your authorization has been submitted to the hiring team. We are processing your background check with the State of Arkansas and will email you a transaction ID once it's ready. You'll need that ID to schedule your fingerprint appointment.
        </p>
      </div>
      <p style={{fontSize:12,color:C.mid,textAlign:"center"}}>No action needed — we'll email you when it's time for your next step.</p>
    </div>
  );

  // STEP 3: Employee schedules fingerprinting
  if (step==="s3") return (
    <div>
      <StepDots/>
      <div style={{padding:"16px 18px",background:"#ECFDF5",border:"1.5px solid #6EE7B7",borderRadius:10,marginBottom:16}}>
        <p style={{fontWeight:700,color:"#065F46",fontSize:14,marginBottom:10}}>👆 Time to Schedule Your Fingerprinting</p>
        <p style={{fontSize:13,color:"#064E3B",lineHeight:1.7,marginBottom:12}}>
          Your transaction ID has been sent to your email. Use it to schedule your fingerprint appointment at the Arkansas DHS fingerprinting site.
        </p>
        {txnId&&(
          <div style={{background:"#fff",borderRadius:8,padding:"10px 14px",border:"1px solid #A7F3D0",marginBottom:12}}>
            <p style={{fontSize:11,fontWeight:600,color:C.mid,marginBottom:4}}>YOUR TRANSACTION ID</p>
            <p style={{fontFamily:"monospace",fontSize:20,fontWeight:700,color:"#065F46",letterSpacing:"0.1em"}}>{txnId}</p>
          </div>
        )}
        <a href="https://telegov.egov.com/dhsfingerprint" target="_blank" rel="noopener noreferrer"
          style={{display:"inline-block",padding:"11px 20px",background:"#065F46",color:"#fff",
            borderRadius:8,fontSize:13,fontWeight:700,textDecoration:"none"}}>
          📅 Schedule Fingerprint Appointment ↗
        </a>
      </div>
      <div style={{padding:"11px 14px",background:C.light,border:`1px solid ${C.border}`,borderRadius:8}}>
        <p style={{fontSize:12,color:C.mid,lineHeight:1.6}}>
          After your appointment, return here and click the button below so we know you're done.
        </p>
        <div style={{marginTop:10}}>
          <Btn disabled={busy} bg={C.green} color="#fff" onClick={async()=>{
            setBusy(true);
            await onComplete({ bgState:{...bgState, currentStep:"s4", fingerprintDoneAt:new Date().toISOString()}, received:false });
            toast("✅ Fingerprint appointment marked complete!");
            setBusy(false);
          }}>
            {busy?"Saving…":"✅ I've Completed My Fingerprint Appointment"}
          </Btn>
        </div>
      </div>
    </div>
  );

  // STEP 4: Waiting for final clearance from state
  if (step==="s4") return (
    <div>
      <StepDots/>
      <div style={{padding:"16px 18px",background:"#F5F3FF",border:"1.5px solid #DDD6FE",borderRadius:10,textAlign:"center"}}>
        <p style={{fontSize:28,marginBottom:8}}>🔍</p>
        <p style={{fontWeight:700,color:"#5B21B6",fontSize:14,marginBottom:6}}>Fingerprinting Complete — Awaiting Final Clearance</p>
        <p style={{fontSize:13,color:"#6D28D9",lineHeight:1.7}}>
          The State of Arkansas is processing your background check and maltreatment registry check. This typically takes a few days to several weeks. We'll notify you once the final clearance is received.
        </p>
      </div>
      {bgState.clearedAt&&(
        <div style={{marginTop:14,padding:"14px 16px",background:"#ECFDF5",border:`1px solid ${C.green}`,borderRadius:10}}>
          <p style={{fontWeight:700,color:C.green,fontSize:14}}>🎉 All Clearances Received!</p>
          <p style={{fontSize:12,color:C.mid,marginTop:4}}>Background check and maltreatment registry check complete. You're cleared!</p>
        </div>
      )}
    </div>
  );

  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// BACKGROUND CHECK WORKFLOW — ADMIN VIEW (inside AdminDocRow)
// ═══════════════════════════════════════════════════════════════════════════════
function BgWorkflowAdmin({ docDef, docState, appId, applicantName, appEmail, onUpdated, toast }) {
  const bgState  = docState?.bgState || {};
  const step     = bgState.currentStep || "s1";
  const [txnInput, setTxnInput] = useState(bgState.transactionId||"");
  const [busy,     setBusy]     = useState(false);
  const [bgProg,   setBgProg]   = useState(null);
  const [mtProg,   setMtProg]   = useState(null);
  const bgFileRef = useRef();
  const mtFileRef = useRef();

  const stepIdx = BG_STEPS.findIndex(s=>s.id===step);

  function StepTrack() {
    return (
      <div style={{marginBottom:20}}>
        {BG_STEPS.map((s,i)=>{
          const done   = stepIdx > i || (step==="s4"&&bgState.clearedAt&&i===3);
          const active = s.id===step;
          const locked = stepIdx < i;
          return (
            <div key={s.id} style={{display:"flex",gap:12,alignItems:"flex-start",
              paddingBottom:i<3?16:0,marginLeft:8,paddingLeft:16,
              borderLeft:i<3?`2px solid ${done?C.green:active?"#8B5CF6":C.border}`:"none",
              position:"relative"}}>
              <div style={{position:"absolute",left:-10,top:2,width:18,height:18,borderRadius:"50%",
                background:done?C.green:active?"#8B5CF6":C.border,
                display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{fontSize:9,color:"#fff"}}>{done?"✓":s.icon}</span>
              </div>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <p style={{fontSize:13,fontWeight:600,color:done?C.green:active?"#6D28D9":C.mid}}>{s.label}</p>
                  <span style={{fontSize:10,padding:"1px 7px",borderRadius:20,
                    background:s.who==="admin"?"#EFF6FF":"#F5F3FF",
                    color:s.who==="admin"?"#2563EB":"#6D28D9",fontWeight:600}}>
                    {s.who==="admin"?"👤 Admin":"👤 Employee"}
                  </span>
                  {done&&<span style={{fontSize:10,color:C.green,fontWeight:600}}>Complete</span>}
                  {active&&<span style={{fontSize:10,color:"#6D28D9",fontWeight:600}}>● In Progress</span>}
                </div>
                <p style={{fontSize:11,color:C.mid,marginTop:2,lineHeight:1.5}}>{s.desc}</p>

                {/* Step-specific admin content */}
                {active&&step==="s1"&&(
                  <div style={{marginTop:8,padding:"10px 12px",background:"#FFF7ED",border:"1px solid #FCD34D",borderRadius:7}}>
                    <p style={{fontSize:12,color:"#92400E"}}>⏳ Waiting for employee to complete authorization form.</p>
                    {bgState.authForm&&(
                      <div style={{marginTop:8}}>
                        <p style={{fontSize:11,fontWeight:600,color:C.mid,marginBottom:4}}>Submitted Info Preview:</p>
                        <p style={{fontSize:11,color:C.navy}}><strong>Name:</strong> {bgState.authForm.f0}</p>
                        <p style={{fontSize:11,color:C.navy}}><strong>DOB:</strong> {bgState.authForm.f1}</p>
                        <p style={{fontSize:11,color:C.navy}}><strong>SSN Last 4:</strong> {bgState.authForm.f2}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Admin: step s2 — enter transaction ID and mark submitted */}
                {active&&step==="s2"&&(
                  <div style={{marginTop:10,padding:"14px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8}}>
                    <p style={{fontSize:12,fontWeight:600,color:"#1D4ED8",marginBottom:10}}>
                      Enter the transaction/confirmation ID from the State of Arkansas, then click Send to notify the employee.
                    </p>
                    <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
                      <div style={{flex:1,minWidth:180}}>
                        <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>Transaction ID *</p>
                        <input value={txnInput} onChange={e=>setTxnInput(e.target.value)}
                          placeholder="e.g. ECE004827974"
                          style={{width:"100%",padding:"10px 13px",borderRadius:8,border:"1.5px solid #93C5FD",
                            fontFamily:"monospace",fontSize:14,fontWeight:600,color:"#1E3A8A",outline:"none"}}
                          onFocus={e=>e.target.style.borderColor="#2563EB"}
                          onBlur={e=>e.target.style.borderColor="#93C5FD"}/>
                      </div>
                      <Btn disabled={busy||!txnInput.trim()} bg="#2563EB" color="#fff"
                        onClick={async()=>{
                          if(!txnInput.trim()){toast("Please enter the transaction ID.","error");return;}
                          setBusy(true);
                          const updated = {...bgState, currentStep:"s3", transactionId:txnInput.trim(), submittedAt:new Date().toISOString()};
                          await onUpdated(docDef.id, { bgState:updated, received:false });
                          // Email employee with transaction ID and fingerprint scheduling link
                          await sendEmail(appEmail, "Your Fingerprinting Transaction ID — Summer Day Camp",
                            `Hi ${applicantName},\n\nGreat news! Your background check has been submitted to the State of Arkansas.\n\nHere is the number you will need for when you go for your fingerprinting:\n\nTransaction ID: ${txnInput.trim()}\n\nSchedule your appointment at:\nhttps://telegov.egov.com/dhsfingerprint\n\nPlease complete your fingerprint appointment as soon as possible and mark it complete in your employee portal.\n\nIf you have any questions, contact us at ${ADMIN_EMAIL}.\n\nSummer Day Camp Hiring Team`);
                          toast("📨 Transaction ID sent to " + appEmail);
                          setBusy(false);
                        }}>
                        📨 Send Transaction ID to Employee
                      </Btn>
                    </div>
                    <div style={{marginTop:10,padding:"8px 10px",background:"#fff",borderRadius:6,border:"1px solid #BFDBFE"}}>
                      <p style={{fontSize:11,color:"#1D4ED8",lineHeight:1.6}}>
                        This will email the employee: their transaction ID, the fingerprinting scheduling link
                        (<a href="https://telegov.egov.com/dhsfingerprint" target="_blank" rel="noopener noreferrer" style={{color:"#2563EB"}}>telegov.egov.com/dhsfingerprint</a>),
                        and instructions to mark it complete in their portal.
                      </p>
                    </div>
                  </div>
                )}

                {/* Step s3 — waiting for employee fingerprint */}
                {active&&step==="s3"&&(
                  <div style={{marginTop:8,padding:"10px 12px",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:7}}>
                    <p style={{fontSize:12,color:"#5B21B6",marginBottom:4}}>
                      ⏳ Waiting for employee to schedule and complete fingerprinting.
                    </p>
                    {bgState.transactionId&&(
                      <p style={{fontSize:11,color:C.mid}}>Transaction ID sent: <strong style={{fontFamily:"monospace",color:"#1E3A8A"}}>{bgState.transactionId}</strong></p>
                    )}
                    <div style={{marginTop:8}}>
                      <Btn size="sm" bg="#6D28D9" color="#fff" disabled={busy}
                        onClick={async()=>{
                          // Manual override — admin can advance if they confirm fingerprint done
                          setBusy(true);
                          await onUpdated(docDef.id, { bgState:{...bgState, currentStep:"s4", fingerprintDoneAt:new Date().toISOString(), fingerprintMarkedByAdmin:true}, received:false });
                          toast("Fingerprint step marked complete by admin.");
                          setBusy(false);
                        }}>
                        Mark Fingerprint Complete (Admin Override)
                      </Btn>
                    </div>
                  </div>
                )}

                {/* Step s4 — upload final clearance docs */}
                {step==="s4"&&i===3&&(
                  <div style={{marginTop:10}}>
                    {/* Background clearance upload */}
                    <div style={{padding:"14px",background:"#F0FDF4",border:"1px solid #A7F3D0",borderRadius:8,marginBottom:10}}>
                      <p style={{fontSize:12,fontWeight:600,color:"#065F46",marginBottom:8}}>Background Check Clearance Letter</p>
                      {bgState.bgClearanceUrl?(
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <a href={bgState.bgClearanceUrl} target="_blank" rel="noopener noreferrer"
                            style={{fontSize:12,color:C.sky,fontWeight:600}}>📄 View Clearance Letter ↗</a>
                          <button onClick={()=>bgFileRef.current?.click()}
                            style={{fontSize:11,color:C.mid,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>
                            Replace
                          </button>
                        </div>
                      ):(
                        <div>
                          {bgProg!==null&&<div style={{marginBottom:8}}><ProgressBar pct={bgProg}/></div>}
                          <Btn size="sm" bg={C.green} color="#fff" onClick={()=>bgFileRef.current?.click()}>
                            + Upload Background Clearance
                          </Btn>
                        </div>
                      )}
                      <input ref={bgFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{display:"none"}}
                        onChange={async e=>{
                          const file=e.target.files?.[0]; if(!file)return;
                          setBgProg(0);
                          const url=await uploadFile(`admin-results/${appId}/bg_clearance_${Date.now()}_${file.name}`,file,p=>setBgProg(p));
                          const updated={...bgState,bgClearanceUrl:url,bgClearanceName:file.name,bgClearedAt:new Date().toISOString()};
                          const allDone=!!updated.bgClearanceUrl&&!!updated.mtClearanceUrl;
                          if(allDone) updated.clearedAt=new Date().toISOString();
                          await onUpdated(docDef.id,{bgState:updated,received:allDone,receivedDate:allDone?new Date().toISOString().split("T")[0]:null});
                          setBgProg(null);
                          toast(allDone?"🎉 All clearances received! Process complete!":"✅ Background clearance uploaded.");
                          if(e.target)e.target.value="";
                        }}/>
                    </div>

                    {/* Maltreatment clearance upload */}
                    <div style={{padding:"14px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8}}>
                      <p style={{fontSize:12,fontWeight:600,color:"#1D4ED8",marginBottom:8}}>Maltreatment Registry Clearance Letter</p>
                      {bgState.mtClearanceUrl?(
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <a href={bgState.mtClearanceUrl} target="_blank" rel="noopener noreferrer"
                            style={{fontSize:12,color:C.sky,fontWeight:600}}>📄 View Clearance Letter ↗</a>
                          <button onClick={()=>mtFileRef.current?.click()}
                            style={{fontSize:11,color:C.mid,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>
                            Replace
                          </button>
                        </div>
                      ):(
                        <div>
                          {mtProg!==null&&<div style={{marginBottom:8}}><ProgressBar pct={mtProg}/></div>}
                          <Btn size="sm" bg="#2563EB" color="#fff" onClick={()=>mtFileRef.current?.click()}>
                            + Upload Maltreatment Clearance
                          </Btn>
                        </div>
                      )}
                      <input ref={mtFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{display:"none"}}
                        onChange={async e=>{
                          const file=e.target.files?.[0]; if(!file)return;
                          setMtProg(0);
                          const url=await uploadFile(`admin-results/${appId}/mt_clearance_${Date.now()}_${file.name}`,file,p=>setMtProg(p));
                          const updated={...bgState,mtClearanceUrl:url,mtClearanceName:file.name,mtClearedAt:new Date().toISOString()};
                          const allDone=!!updated.bgClearanceUrl&&!!updated.mtClearanceUrl;
                          if(allDone) updated.clearedAt=new Date().toISOString();
                          await onUpdated(docDef.id,{bgState:updated,received:allDone,receivedDate:allDone?new Date().toISOString().split("T")[0]:null});
                          setMtProg(null);
                          toast(allDone?"🎉 All clearances received! Process complete!":"✅ Maltreatment clearance uploaded.");
                          if(e.target)e.target.value="";
                        }}/>
                    </div>

                    {bgState.clearedAt&&(
                      <div style={{marginTop:10,padding:"12px 14px",background:"#ECFDF5",border:`1px solid ${C.green}`,borderRadius:8}}>
                        <p style={{fontWeight:700,color:C.green}}>🎉 All clearances received and on file!</p>
                        <p style={{fontSize:11,color:C.mid,marginTop:3}}>Completed {new Date(bgState.clearedAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // View submitted auth form data
  const authData = bgState.authForm;

  return (
    <div>
      <StepTrack/>
      {authData&&(
        <div style={{marginTop:4,padding:"12px 14px",background:C.light,border:`1px solid ${C.border}`,borderRadius:8}}>
          <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>Authorization Form — Submitted Data</p>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {[["Name",authData.f0],["DOB",authData.f1],["SSN Last 4",authData.f2]].map(([l,v])=>(
              <div key={l}>
                <p style={{fontSize:10,color:C.mid}}>{l}</p>
                <p style={{fontSize:12,fontWeight:600,color:C.navy}}>{v||"—"}</p>
              </div>
            ))}
          </div>
          {authData.f3&&<p style={{fontSize:11,color:C.mid,marginTop:6}}><strong>Address:</strong> {authData.f3}</p>}
          {bgState.authSig&&(
            <div style={{marginTop:8,display:"flex",alignItems:"center",gap:8}}>
              <p style={{fontSize:10,color:C.mid}}>Signature:</p>
              <img src={bgState.authSig} alt="sig" style={{height:30,background:"#fff",border:`1px solid ${C.border}`,borderRadius:4,padding:"2px 6px"}}/>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AdminDocRow({ docDef, docState, appId, onUpdated, toast, applicantName:nameProp, appEmail:emailProp }) {
  const applicantName = nameProp || docState?.applicantName || "";
  const appEmail = emailProp || docState?.applicantEmail || "";
  const [expanded,   setExpanded]   = useState(false);
  const [prog,       setProg]       = useState(null);
  const [adminProg,  setAdminProg]  = useState(null);
  const [adminNotes, setAdminNotes] = useState(docState?.adminNotes||"");
  const [savingNote, setSavingNote] = useState(false);
  const fileRef      = useRef();
  const adminFileRef = useRef();
  const uploaded   = !!docState?.received;
  const adminFiles = docState?.adminFiles || (docState?.adminUrl ? [{url:docState.adminUrl,name:docState.adminName||"Result file",uploadedAt:docState.adminUploadedAt}] : []);
  const meta = DOC_TYPE_META[docDef.docType] || DOC_TYPE_META.upload;
  const isBgWorkflow = docDef.docType === "bg_workflow";

  // bg_workflow gets its own card inside the standard header
  async function handleApplicantUpload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 20*1024*1024) { toast("File must be under 20 MB.", "error"); return; }
    setProg(0);
    try {
      const path = `onboarding/${appId}/${docDef.id}_admin_${Date.now()}_${file.name}`;
      const url  = await uploadFile(path, file, p => setProg(p));
      await onUpdated(docDef.id, {uploadedUrl:url,uploadedName:file.name,received:true,receivedDate:new Date().toISOString().split("T")[0],uploadedBy:"admin"});
      toast("Uploaded for " + docDef.name);
    } catch(ex) { toast("Upload failed: "+ex.message,"error"); }
    setProg(null);
    if (fileRef.current) fileRef.current.value="";
  }

  async function handleAdminResultUpload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 20*1024*1024) { toast("File must be under 20 MB.", "error"); return; }
    setAdminProg(0);
    try {
      const path = `admin-results/${appId}/${docDef.id}_result_${Date.now()}_${file.name}`;
      const url  = await uploadFile(path, file, p => setAdminProg(p));
      const newFile = { url, name:file.name, uploadedAt:new Date().toISOString() };
      await onUpdated(docDef.id, { adminFiles: [...adminFiles, newFile] });
      toast("Result uploaded: " + file.name);
    } catch(ex) { toast("Upload failed: "+ex.message,"error"); }
    setAdminProg(null);
    if (adminFileRef.current) adminFileRef.current.value="";
  }

  async function removeAdminFile(idx) {
    await onUpdated(docDef.id, { adminFiles: adminFiles.filter((_,i)=>i!==idx) });
    toast("File removed.");
  }

  async function toggleManual() {
    const now = !uploaded;
    await onUpdated(docDef.id, {received:now,receivedDate:now?new Date().toISOString().split("T")[0]:null,uploadedBy:now?"admin (manual)":null});
  }

  async function saveNote() {
    setSavingNote(true);
    await onUpdated(docDef.id, { adminNotes });
    setSavingNote(false);
    toast("Note saved.");
  }

  const hasAdminFiles = adminFiles.length > 0;

  return (
    <div style={{borderRadius:10,marginBottom:8,overflow:"hidden",
      border:`1.5px solid ${hasAdminFiles?"#93C5FD":uploaded?"#A7F3D0":C.border}`,
      background:uploaded?"#ECFDF5":"#fff"}}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"13px 16px",cursor:"pointer"}}
        onClick={()=>setExpanded(v=>!v)}>
        <div style={{width:10,height:10,borderRadius:"50%",flexShrink:0,background:uploaded?C.green:C.border}}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <p style={{fontSize:13,fontWeight:600,color:C.navy}}>{docDef.name}</p>
            <span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:20,
              background:`${meta.color}15`,color:meta.color}}>{meta.icon} {meta.label}</span>
            {hasAdminFiles&&(
              <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:20,
                background:"#EFF6FF",color:"#2563EB",border:"1px solid #BFDBFE"}}>
                📎 {adminFiles.length} result{adminFiles.length>1?"s":""}
              </span>
            )}
            {docState?.adminNotes&&(
              <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:20,
                background:`${C.amber}15`,color:C.amber2,border:`1px solid ${C.amber}44`}}>
                📝 Note
              </span>
            )}
          </div>
          {uploaded&&docState?.uploadedUrl&&(
            <a href={docState.uploadedUrl} target="_blank" rel="noopener noreferrer"
              onClick={e=>e.stopPropagation()}
              style={{fontSize:11,color:C.sky,display:"block",marginTop:2}}>
              View applicant file · {docState.receivedDate}
            </a>
          )}
          {uploaded&&!docState?.uploadedUrl&&(
            <p style={{fontSize:11,color:C.mid,marginTop:2}}>Manual · {docState.receivedDate}</p>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
          {docDef.docType==="bg_workflow"?(()=>{
            const bgSt = docState?.bgState||{};
            const s = bgSt.currentStep||"s1";
            const idx = BG_STEPS.findIndex(x=>x.id===s);
            const done = !!bgSt.clearedAt;
            return <span style={{fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:20,
              color:done?C.green:"#6D28D9",background:done?"#ECFDF5":"#F5F3FF",
              border:`1px solid ${done?"#A7F3D0":"#DDD6FE"}`}}>
              {done?"✓ Cleared":`Step ${idx+1}/4`}
            </span>;
          })():(
          <span style={{fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:20,
            color:uploaded?C.green:C.red,background:uploaded?"#ECFDF5":"#FEF2F2",
            border:`1px solid ${uploaded?"#A7F3D0":"#FECACA"}`}}>
            {uploaded?"✓ Received":"Pending"}
          </span>)}
          <span style={{fontSize:15,color:C.mid,transform:expanded?"rotate(90deg)":"",transition:"transform .2s"}}>›</span>
        </div>
      </div>

      {/* Expanded */}
      {expanded&&(
        <div style={{borderTop:`1px solid ${C.border}`}}>

          {/* BG Workflow gets its own dedicated panel */}
          {isBgWorkflow&&(
            <div style={{padding:"16px"}}>
              <BgWorkflowAdmin
                docDef={docDef} docState={docState} appId={appId}
                applicantName={docState?.applicantName||"Employee"}
                appEmail={docState?.applicantEmail||""}
                onUpdated={onUpdated} toast={toast}/>
            </div>
          )}

          {/* Standard docs */}
          {!isBgWorkflow&&(
            <div>
              {/* Applicant submission */}
              <div style={{padding:"14px 16px",borderBottom:`1px solid ${C.border}`}}>
                <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>
                  Applicant Submission
                </p>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:12}}>
                  <input type="checkbox" checked={!!uploaded} onChange={toggleManual}
                    style={{width:15,height:15,accentColor:C.green,cursor:"pointer"}}/>
                  <span style={{fontSize:12,color:C.mid}}>Mark received manually (paper / in-person)</span>
                </label>
                {prog!==null&&<div style={{marginBottom:10}}><ProgressBar pct={prog}/></div>}
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  style={{display:"none"}} onChange={handleApplicantUpload}/>
                <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <Btn onClick={()=>fileRef.current?.click()} size="sm"
                    bg={uploaded?C.navy2:C.amber} color={uploaded?"#fff":C.navy}>
                    {uploaded?"Replace Applicant File":"Upload on Behalf of Applicant"}
                  </Btn>
                  {uploaded&&docState?.uploadedUrl&&(
                    <a href={docState.uploadedUrl} target="_blank" rel="noopener noreferrer"
                      style={{fontSize:12,color:C.sky}}>View file ↗</a>
                  )}
                </div>
              </div>

              {/* Admin results */}
              <div style={{padding:"14px 16px",background:"#F8FAFF",borderBottom:`1px solid ${C.border}`}}>
                <p style={{fontSize:10,fontWeight:700,color:"#2563EB",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6}}>
                  Admin Results &amp; Documents
                </p>
                <p style={{fontSize:11,color:C.mid,marginBottom:12,lineHeight:1.5}}>
                  Upload results or internal documents. Admin-only — not visible to the applicant.
                </p>
                {adminFiles.map((f,idx)=>(
                  <div key={idx} style={{display:"flex",alignItems:"center",gap:10,
                    padding:"9px 12px",background:"#fff",borderRadius:8,marginBottom:6,
                    border:"1px solid #BFDBFE"}}>
                    <span style={{fontSize:18,flexShrink:0}}>
                      {/\.pdf$/i.test(f.name)?"📄":/\.(jpg|jpeg|png)$/i.test(f.name)?"🖼️":"📎"}
                    </span>
                    <div style={{flex:1,minWidth:0}}>
                      <a href={f.url} target="_blank" rel="noopener noreferrer"
                        style={{fontSize:12,fontWeight:600,color:"#2563EB",textDecoration:"none",
                          display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {f.name}
                      </a>
                      {f.uploadedAt&&(
                        <p style={{fontSize:10,color:C.mid,marginTop:1}}>
                          {new Date(f.uploadedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                        </p>
                      )}
                    </div>
                    <button onClick={()=>removeAdminFile(idx)}
                      style={{background:"none",border:"none",color:C.red,fontSize:18,cursor:"pointer",padding:"2px 6px"}}>
                      ×
                    </button>
                  </div>
                ))}
                {adminProg!==null&&<div style={{marginBottom:10}}><ProgressBar pct={adminProg}/></div>}
                <input ref={adminFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                  style={{display:"none"}} onChange={handleAdminResultUpload}/>
                <Btn onClick={()=>adminFileRef.current?.click()} size="sm" bg="#2563EB" color="#fff">
                  + Upload Result / Document
                </Btn>
              </div>

              {/* Admin notes */}
              <div style={{padding:"14px 16px"}}>
                <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>
                  Admin Notes (internal only)
                </p>
                <textarea rows={3} value={adminNotes} onChange={e=>setAdminNotes(e.target.value)}
                  placeholder="e.g. Background check cleared 6/1, expires 6/1/2026, follow-up needed…"
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,
                    fontFamily:B,fontSize:12,color:C.navy,resize:"vertical",outline:"none",
                    marginBottom:8,lineHeight:1.5}}
                  onFocus={e=>e.target.style.borderColor=C.amber}
                  onBlur={e=>e.target.style.borderColor=C.border}/>
                <Btn size="sm" onClick={saveNote} disabled={savingNote}>
                  {savingNote?"Saving…":"Save Note"}
                </Btn>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// PUBLIC EMPLOYMENT VERIFICATION FORM (no login required)
// ═══════════════════════════════════════════════════════════════════════════════
function VerifyView({ token, toast }) {
  const [data,    setData]    = useState(null);  // verification request doc
  const [loading, setLoading] = useState(true);
  const [done,    setDone]    = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [form,    setForm]    = useState({
    verifierName: "", verifierTitle: "", startDate: "", endDate: "",
    rehirable: null, comments: "",
  });
  const [sigUrl,   setSigUrl]   = useState(null);
  const [showSig,  setShowSig]  = useState(false);
  const set = k => e => setForm(p => ({...p, [k]: e.target.value}));

  useEffect(() => {
    (async () => {
      try {
        // Look up the token in verificationRequests collection
        const q = query(collection(db, "verificationRequests"), where("token", "==", token));
        const snap = await getDocs(q);
        if (snap.empty) { setData(null); setLoading(false); return; }
        const d = { id: snap.docs[0].id, ...snap.docs[0].data() };
        setData(d);
        if (d.status === "completed") setDone(true);
      } catch(e) { console.error(e); }
      setLoading(false);
    })();
  }, [token]);

  async function handleSubmit() {
    if (!form.verifierName.trim())  { toast("Please enter your name.", "error"); return; }
    if (!form.verifierTitle.trim()) { toast("Please enter your title.", "error"); return; }
    if (form.rehirable === null)    { toast("Please select a rehire status.", "error"); return; }
    if (!sigUrl)                    { toast("Please add your signature.", "error"); return; }
    setBusy(true);
    try {
      const response = {
        ...form,
        sigUrl,
        submittedAt: new Date().toISOString(),
        status: "completed",
      };
      await updateDoc(doc(db, "verificationRequests", data.id), response);
      // Try to update the application record too — may fail if unauthenticated, that's ok
      // Admin can always see the full response in verificationRequests
      try {
        await updateDoc(doc(db, "applications", data.appId), {
          [`verifications.${data.employerIndex}`]: { ...response, employer: data.employer },
        });
      } catch(ignore) { /* public user can't write to applications — admin will see it in verificationRequests */ }
      setDone(true);
      toast("✅ Thank you! Verification submitted.");
    } catch(e) { toast("Error submitting: " + e.message, "error"); }
    setBusy(false);
  }

  if (loading) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"100vh",gap:16}}>
      <Sun size={40} style={{color:C.amber,animation:"spin 2s linear infinite"}}/>
      <p style={{color:C.sand,fontFamily:H,fontSize:18}}>Loading verification request…</p>
    </div>
  );

  if (!data) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"100vh",gap:16,padding:24}}>
      <div style={{fontSize:48}}>🔍</div>
      <h2 style={{fontFamily:H,color:C.sand,fontSize:24,textAlign:"center"}}>Verification Link Not Found</h2>
      <p style={{color:`${C.sand}88`,textAlign:"center",maxWidth:400,lineHeight:1.7}}>
        This link may be invalid or expired. Please contact Summer Day Camp directly if you received this in error.
      </p>
    </div>
  );

  if (done) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"100vh",gap:16,padding:24}}>
      <div style={{fontSize:56}}>✅</div>
      <h2 style={{fontFamily:H,color:C.sand,fontSize:26,textAlign:"center"}}>Verification Submitted</h2>
      <p style={{color:`${C.sand}88`,textAlign:"center",maxWidth:420,lineHeight:1.7}}>
        Thank you for completing the employment verification for <strong style={{color:C.sand}}>{data.applicantName}</strong>.
        Your response has been received by Summer Day Camp.
      </p>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"36px 20px",minHeight:"100vh"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
        <Sun size={34} style={{color:C.amber}}/><h1 style={{fontFamily:H,color:C.sand,fontSize:20}}>Summer Day Camp</h1>
      </div>
      <p style={{color:`${C.sand}66`,fontSize:13,marginBottom:28}}>Employment Verification Request</p>

      <div style={{width:"100%",maxWidth:640,display:"flex",flexDirection:"column",gap:20}}>

        {/* Request context card */}
        <div style={{background:`${C.amber}18`,border:`1.5px solid ${C.amber}55`,borderRadius:12,padding:"18px 22px"}}>
          <p style={{fontSize:11,fontWeight:600,color:C.amber,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10}}>Verification Requested For</p>
          <p style={{fontFamily:H,color:C.sand,fontSize:22,marginBottom:4}}>{data.applicantName}</p>
          <p style={{color:`${C.sand}88`,fontSize:13}}>Listed employer: <strong style={{color:C.sand}}>{data.employer}</strong> — {data.jobTitle}</p>
          <p style={{color:`${C.sand}66`,fontSize:12,marginTop:8,lineHeight:1.6}}>
            Summer Day Camp is verifying employment history as part of a staff hiring process.
            Your response is confidential and used solely for this purpose.
          </p>
        </div>

        {/* Verification form */}
        <Card style={{padding:"24px 28px"}}>
          <h3 style={{fontFamily:H,color:C.navy,fontSize:18,marginBottom:20}}>Please Complete the Following</h3>

          {/* Verifier identity */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>
            <div>
              <p style={{fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>Your Full Name *</p>
              <input value={form.verifierName} onChange={set("verifierName")}
                placeholder="Jane Smith"
                style={{width:"100%",padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
            </div>
            <div>
              <p style={{fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>Your Title / Role *</p>
              <input value={form.verifierTitle} onChange={set("verifierTitle")}
                placeholder="HR Manager"
                style={{width:"100%",padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
            </div>
          </div>

          {/* Dates */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>
            <div>
              <p style={{fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>Start Date of Employment</p>
              <input type="date" value={form.startDate} onChange={set("startDate")}
                style={{width:"100%",padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
            </div>
            <div>
              <p style={{fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>End Date of Employment</p>
              <input type="date" value={form.endDate} onChange={set("endDate")}
                style={{width:"100%",padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
              <p style={{fontSize:11,color:C.mid,marginTop:4}}>Leave blank if still employed</p>
            </div>
          </div>

          {/* Rehirable */}
          <div style={{marginBottom:20}}>
            <p style={{fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>Would You Rehire This Person? *</p>
            <div style={{display:"flex",gap:10}}>
              {["Yes","No","Conditionally"].map(opt=>(
                <button key={opt} onClick={()=>setForm(p=>({...p,rehirable:opt}))} style={{
                  flex:1,padding:"12px 0",borderRadius:9,border:`2px solid ${form.rehirable===opt?(opt==="Yes"?C.green:opt==="No"?C.red:C.amber):C.border}`,
                  background:form.rehirable===opt?(opt==="Yes"?`${C.green}15`:opt==="No"?`${C.red}15`:`${C.amber}15`):"#fff",
                  color:form.rehirable===opt?(opt==="Yes"?C.green:opt==="No"?C.red:C.amber2):C.mid,
                  fontFamily:B,fontWeight:700,fontSize:13,cursor:"pointer",transition:"all .15s"}}>
                  {opt==="Yes"?"✅ Yes":opt==="No"?"❌ No":"⚠️ Conditionally"}
                </button>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div style={{marginBottom:24}}>
            <p style={{fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>Comments / Additional Information</p>
            <textarea rows={4} value={form.comments} onChange={set("comments")}
              placeholder="Dates of employment, job performance, reason for leaving, any relevant details…"
              style={{width:"100%",padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,
                fontFamily:B,fontSize:13,color:C.navy,resize:"vertical",outline:"none",lineHeight:1.6}}
              onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
          </div>

          {/* Signature */}
          <div style={{marginBottom:24,padding:"16px 18px",background:C.light,borderRadius:10,border:`1px solid ${C.border}`}}>
            <p style={{fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>Your Signature *</p>
            {sigUrl ? (
              <div style={{display:"flex",alignItems:"center",gap:14}}>
                <img src={sigUrl} alt="Signature" style={{height:48,background:"#fff",border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 10px"}}/>
                <button onClick={()=>setSigUrl(null)} style={{fontSize:12,color:C.red,background:"none",border:"none",cursor:"pointer"}}>Remove</button>
              </div>
            ) : showSig ? (
              <SignaturePad onSave={url=>{setSigUrl(url);setShowSig(false);}} onCancel={()=>setShowSig(false)}/>
            ) : (
              <Btn onClick={()=>setShowSig(true)} bg={C.navy} color="#fff">✍️ Add Signature</Btn>
            )}
          </div>

          {/* Legal */}
          <div style={{marginBottom:20,padding:"12px 14px",background:`${C.amber}10`,border:`1px solid ${C.amber}33`,borderRadius:8}}>
            <p style={{fontSize:12,color:C.amber2,lineHeight:1.7}}>
              By signing and submitting this form, I certify that the information provided is accurate and true to the best of my knowledge,
              and that I am authorized to provide employment verification on behalf of {data.employer}.
            </p>
          </div>

          <Btn onClick={handleSubmit} bg={C.green} color="#fff" disabled={busy} style={{width:"100%",padding:"14px",fontSize:15}}>
            {busy ? "Submitting…" : "Submit Verification ✓"}
          </Btn>
        </Card>

        <p style={{textAlign:"center",fontSize:11,color:`${C.sand}44`,marginBottom:24}}>
          Summer Day Camp · Powered by Rogers Recreation · Questions? Email {ADMIN_EMAIL}
        </p>
      </div>
    </div>
  );
}

export default function App() {
  injectFonts();
  const [authUser,  setAuthUser]  = useState(null);
  const [userDoc,   setUserDoc]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [toasts,    setToasts]    = useState([]);
  const [view,      setView]      = useState("login");
  const justRegistered = useRef(false);

  // Check for public verification token in URL — no login needed
  const verifyToken = new URLSearchParams(window.location.search).get("verify");

  const toast = useCallback((msg, type="success") => {
    setToasts(p => [...p, {id:Date.now()+Math.random(), msg, type}]);
  }, []);
  const rmToast = useCallback(id => setToasts(p => p.filter(t=>t.id!==id)), []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async u => {
      if (u) {
        setAuthUser(u);
        if (justRegistered.current) { justRegistered.current=false; setLoading(false); return; }
        const snap = await getDoc(doc(db,"users",u.uid));
        if (snap.exists()) {
          const d = snap.data();
          setUserDoc({uid:u.uid,...d});
          if (d.role==="admin") { setView("admin"); }
          else {
            const qs = await getDocs(query(collection(db,"applications"),where("uid","==",u.uid)));
            setView(qs.empty ? "apply" : "portal");
          }
        } else { setView("apply"); }
      } else { setAuthUser(null); setUserDoc(null); setView("login"); }
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleLogout = async () => { await signOut(auth); toast("Signed out.","info"); };

  if (loading && !verifyToken) return <LoadingScreen/>;

  // Public employment verification — no auth required
  if (verifyToken) return (
    <div style={{minHeight:"100vh",background:`linear-gradient(180deg,${C.navy} 0%,${C.navy2} 100%)`,fontFamily:B}}>
      {toasts.map(t=><Toast key={t.id} msg={t.msg} type={t.type} onClose={()=>rmToast(t.id)}/>)}
      <VerifyView token={verifyToken} toast={toast}/>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:C.navy,fontFamily:B}}>
      {toasts.map(t=><Toast key={t.id} msg={t.msg} type={t.type} onClose={()=>rmToast(t.id)}/>)}
      {view==="login"    && <AuthView mode="login"    onSwitch={()=>setView("register")} toast={toast}/>}
      {view==="register" && <AuthView mode="register" onSwitch={()=>setView("login")} toast={toast}
          onRegistered={()=>{ justRegistered.current=true; setView("apply"); }}/>}
      {view==="apply"  && authUser && <ApplyView  authUser={authUser} toast={toast} onDone={()=>setView("portal")}/>}
      {view==="portal" && authUser && <ApplicantPortal authUser={authUser} onLogout={handleLogout} toast={toast}/>}
      {view==="admin"  && userDoc?.role==="admin" && <AdminDashboard authUser={authUser} onLogout={handleLogout} toast={toast}/>}
    </div>
  );
}

function LoadingScreen(){
  return(
    <div style={{minHeight:"100vh",background:C.navy,display:"flex",flexDirection:"column",
      alignItems:"center",justifyContent:"center",gap:20}}>
      <Sun size={64}/><p style={{fontFamily:H,color:C.sand,fontSize:24}}>Summer Day Camp</p>
      <div style={{width:48,height:4,borderRadius:2,background:C.amber,animation:"pulse 1.2s infinite"}}/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH
// ═══════════════════════════════════════════════════════════════════════════════
function AuthView({mode,onSwitch,toast,onRegistered}){
  const [form,setForm]=useState({name:"",email:"",password:"",confirm:""});
  const [err,setErr]=useState(""); const [busy,setBusy]=useState(false);
  const isLogin=mode==="login";
  const set=k=>e=>setForm(p=>({...p,[k]:e.target.value}));

  async function handle(){
    setErr(""); setBusy(true);
    try{
      if(isLogin){
        await signInWithEmailAndPassword(auth,form.email,form.password);
      } else {
        if(!form.name.trim()){setErr("Full name required.");setBusy(false);return;}
        if(form.password!==form.confirm){setErr("Passwords do not match.");setBusy(false);return;}
        if(form.password.length<6){setErr("Password must be 6+ characters.");setBusy(false);return;}
        const cred=await createUserWithEmailAndPassword(auth,form.email,form.password);
        await setDoc(doc(db,"users",cred.user.uid),{
          uid:cred.user.uid,name:form.name,email:form.email,role:"applicant",createdAt:serverTimestamp()
        });
        toast("Account created! Let's start your application.");
        if(onRegistered) onRegistered();
      }
    }catch(e){ setErr(e.message.replace("Firebase: ","").replace(/\s*\(auth\/[^)]+\)/,"").trim()); }
    setBusy(false);
  }

  return(
    <div style={{minHeight:"100vh",
      background:`linear-gradient(135deg,${C.navy} 0%,${C.navy2} 55%,#0A1A26 100%)`,
      display:"flex",flexDirection:"column",alignItems:"center",
      justifyContent:"center",padding:"24px 16px"}}>

      {/* Logo + title — always centered on mobile */}
      <div className="fu" style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:28,textAlign:"center"}}>
        <div style={{display:"flex",alignItems:"center",gap:11,marginBottom:8}}>
          <Sun size={38} style={{color:C.amber,flexShrink:0}}/>
          <div style={{textAlign:"left"}}>
            <h1 style={{fontFamily:H,color:C.sand,fontSize:20,lineHeight:1.2}}>Summer Day Camp</h1>
            <p style={{color:C.amber,fontSize:11,fontWeight:500,letterSpacing:"0.05em"}}>Staff Portal</p>
          </div>
        </div>
        <h2 style={{fontFamily:H,color:"#fff",fontSize:28,lineHeight:1.2,marginTop:6}}>
          {isLogin?"Welcome back.":"Start your application."}
        </h2>
        <p style={{color:`${C.sand}88`,fontSize:13,lineHeight:1.6,marginTop:6,maxWidth:320}}>
          {isLogin?"Sign in to check your application status."
                  :"Create an account to apply for a staff position."}
        </p>
      </div>

      {/* Form card — full width on mobile, capped on desktop */}
      <div className="fu" style={{width:"100%",maxWidth:420}}>
        <Card style={{padding:"28px 24px"}}>
          <h3 style={{fontFamily:H,fontSize:22,color:C.navy,marginBottom:22,textAlign:"center"}}>
            {isLogin?"Sign In":"Create Account"}
          </h3>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {!isLogin&&(
              <Inp label="Full Legal Name" value={form.name} onChange={set("name")}
                placeholder="Jane Smith" required
                inputStyle={{fontSize:16}}/>
            )}
            <Inp label="Email Address" value={form.email} onChange={set("email")}
              type="email" placeholder="you@email.com" required
              inputStyle={{fontSize:16}}/>
            <Inp label="Password" value={form.password} onChange={set("password")}
              type="password" placeholder="••••••••" required
              inputStyle={{fontSize:16}}/>
            {!isLogin&&(
              <Inp label="Confirm Password" value={form.confirm} onChange={set("confirm")}
                type="password" placeholder="••••••••" required
                inputStyle={{fontSize:16}}/>
            )}
          </div>

          {err&&(
            <div style={{marginTop:14,padding:"10px 14px",background:"#FEF2F2",
              border:`1px solid ${C.red}44`,borderRadius:8}}>
              <p style={{color:C.red,fontSize:13,lineHeight:1.5}}>{err}</p>
            </div>
          )}

          <button onClick={handle} disabled={busy} style={{
            marginTop:20,width:"100%",padding:"15px",borderRadius:10,border:"none",
            background:busy?C.mid:`linear-gradient(135deg,${C.amber},${C.amber2})`,
            color:C.navy,fontFamily:B,fontWeight:700,fontSize:16,
            cursor:busy?"not-allowed":"pointer",
            boxShadow:busy?"none":"0 4px 14px rgba(245,158,11,0.35)",
            transition:"all .2s",letterSpacing:"0.01em"}}>
            {busy?"Please wait…":isLogin?"Sign In →":"Create Account →"}
          </button>

          <div style={{marginTop:18,textAlign:"center",paddingTop:16,borderTop:`1px solid ${C.border}`}}>
            <p style={{color:C.mid,fontSize:13,marginBottom:8}}>
              {isLogin?"New here?":"Already have an account?"}
            </p>
            <button onClick={onSwitch} style={{
              background:"none",border:`1.5px solid ${C.sky}`,borderRadius:8,
              color:C.sky,fontSize:14,fontWeight:600,fontFamily:B,
              padding:"10px 24px",cursor:"pointer",width:"100%"}}>
              {isLogin?"Create an Account — Apply Now":"Sign In Instead"}
            </button>
          </div>
        </Card>

        {/* Feature pills — compact row below the card */}
        <div style={{display:"flex",flexWrap:"wrap",gap:8,justifyContent:"center",marginTop:20}}>
          {[["📋","Application"],["📅","Scheduling"],["💼","Offers"],["📄","Documents"]].map(([icon,label])=>(
            <div key={label} style={{display:"flex",alignItems:"center",gap:5,
              padding:"5px 12px",borderRadius:20,background:`${C.amber}15`,border:`1px solid ${C.amber}30`}}>
              <span style={{fontSize:13}}>{icon}</span>
              <span style={{color:`${C.sand}cc`,fontSize:11,fontWeight:500}}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// APPLICATION FORM
// ═══════════════════════════════════════════════════════════════════════════════
function ApplyView({authUser,toast,onDone}){
  const [step,setStep]=useState(0);
  const [busy,setBusy]=useState(false);
  const [appSettings,setAppSettings]=useState(null);
  const [form,setForm]=useState({
    name:"",phone:"",address:"",education:"",training:"",experience:"",
    employment:[
      {employer:"",email:"",years:"",title:""},
      {employer:"",email:"",years:"",title:""},
    ],
    appAnswers:{},
  });
  const set=k=>e=>setForm(p=>({...p,[k]:e.target.value}));
  const setE=(i,k)=>e=>{const a=[...form.employment];a[i]={...a[i],[k]:e.target.value};setForm(p=>({...p,employment:a}));};
  const appQuestions = appSettings?.appQuestions||FACTORY_QUESTIONS;
  const STEPS=["Personal Info","Education & Training","Employment History","Application Questions","Review & Submit"];

  useEffect(()=>{
    (async()=>{
      try{
        const snap=await getDoc(doc(db,"settings","master"));
        if(snap.exists()) setAppSettings(snap.data());
      }catch(e){ console.warn("Could not load settings:",e.message); }
    })();
  },[]);

  async function submit(){
    setBusy(true);
    try{
      const us=await getDoc(doc(db,"users",authUser.uid));
      const ud=us.data();
      await addDoc(collection(db,"applications"),{
        uid:authUser.uid,email:ud.email,status:"applied",appliedAt:serverTimestamp(),
        interviewNotes:"",interviewAnswers:{},interviewDate:null,interviewTime:null,
        offerSent:false,onboardingDocs:[],docReminderCount:0,lastReminderAt:null,
        offer:null,offerResponse:null,offerRespondedAt:null,...form,
      });
      await sendEmail(ADMIN_EMAIL,`🌞 New Application: ${form.name}`,
        `New application submitted.\n\nApplicant: ${form.name}\nEmail: ${ud.email}\nPhone: ${form.phone}\n\nLog in to review.`);
      toast("🎉 Application submitted! We'll be in touch.");
      onDone();
    }catch(e){ toast("Error: "+e.message,"error"); }
    setBusy(false);
  }

  return(
    <div style={{minHeight:"100vh",background:`linear-gradient(180deg,${C.navy} 0%,${C.navy2} 100%)`,
      display:"flex",flexDirection:"column",alignItems:"center",padding:"36px 20px"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:28}}>
        <Sun size={36}/><h1 style={{fontFamily:H,color:C.sand,fontSize:19}}>Summer Day Camp — Staff Application</h1>
      </div>
      <div style={{display:"flex",alignItems:"center",marginBottom:24,flexWrap:"wrap",justifyContent:"center"}}>
        {STEPS.map((s,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center"}}>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{width:28,height:28,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",
                background:i<step?C.green:i===step?C.amber:`${C.amber}22`,
                border:`2px solid ${i<step?C.green:i===step?C.amber:`${C.amber}33`}`,
                color:i<step?"#fff":i===step?C.navy:`${C.sand}44`,fontSize:11,fontWeight:700}}>
                {i<step?"✓":i+1}
              </div>
              <span style={{fontSize:10,color:i===step?C.amber:`${C.sand}55`,fontWeight:i===step?600:400,whiteSpace:"nowrap"}}>{s}</span>
            </div>
            {i<STEPS.length-1&&<div style={{width:40,height:2,background:i<step?C.green:`${C.amber}22`,margin:"0 4px",marginBottom:16}}/>}
          </div>
        ))}
      </div>
      <Card style={{width:"100%",maxWidth:720}}>
        <div style={{padding:"28px 32px"}}>
          {step===0&&(<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            <div style={{gridColumn:"1/-1"}}><Inp label="Full Legal Name" value={form.name} onChange={set("name")} placeholder="Jane Smith" required/></div>
            <Inp label="Phone" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" required/>
            <div style={{gridColumn:"1/-1"}}><Inp label="Home Address" value={form.address} onChange={set("address")} placeholder="123 Main St, City, State ZIP" required/></div>
            <div style={{gridColumn:"1/-1",padding:"11px 14px",background:`${C.amber}11`,border:`1px solid ${C.amber}44`,borderRadius:8}}>
              <p style={{fontSize:12,color:C.amber2,lineHeight:1.6}}>📝 Date of birth collected after conditional offer, per regulatory requirements.</p>
            </div>
          </div>)}
          {step===1&&(<div style={{display:"flex",flexDirection:"column",gap:16}}>
            <Inp label="Education" value={form.education} onChange={set("education")} rows={4} required
              placeholder={"Degrees, diplomas, certifications.\nEx: High School Diploma – Lincoln HS, 2019\nNote: GED/diploma copy required."}/>
            <Inp label="Training" value={form.training} onChange={set("training")} rows={4} required
              placeholder={"Training with hours and dates.\nEx: CPR/AED Certified – Red Cross, 2023"}/>
            <Inp label="Experience with Youth" value={form.experience} onChange={set("experience")} rows={4} required
              placeholder="Describe your experience with children, youth programs, camps…"/>
          </div>)}
          {step===2&&(<div>
            <p style={{fontSize:13,color:C.mid,marginBottom:16,lineHeight:1.7}}>List <strong>all employment from the past 6 years</strong>. Employer email required for reference checks.</p>
            {form.employment.map((emp,i)=>(
              <div key={i} style={{background:C.light,borderRadius:10,padding:"14px 16px",marginBottom:12,border:`1px solid ${C.border}`}}>
                <p style={{fontWeight:600,color:C.navy,fontSize:11,marginBottom:10,textTransform:"uppercase",letterSpacing:"0.06em"}}>Employer #{i+1}</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <Inp label="Employer" value={emp.employer} onChange={setE(i,"employer")} placeholder="YMCA of Springfield"/>
                  <Inp label="Job Title" value={emp.title} onChange={setE(i,"title")} placeholder="Youth Coordinator"/>
                  <Inp label="Employer Email" value={emp.email} onChange={setE(i,"email")} type="email" placeholder="hr@employer.org"/>
                  <Inp label="Years" value={emp.years} onChange={setE(i,"years")} placeholder="2021–2024"/>
                </div>
              </div>
            ))}
            <Btn onClick={()=>setForm(p=>({...p,employment:[...p.employment,{employer:"",email:"",years:"",title:""}]}))}
              bg={C.navy2} color="#fff">+ Add Employer</Btn>
          </div>)}
          {step===3&&(<div>
            <p style={{fontSize:13,color:C.mid,marginBottom:20,lineHeight:1.6}}>Please answer the following questions to complete your application.</p>
            {appQuestions.map((q,qi)=>{
              const qId = q.id||`q${qi}`;
              const val = form.appAnswers?.[qId]||"";
              const setAns = v => setForm(p=>({...p,appAnswers:{...(p.appAnswers||{}),[qId]:v}}));
              if(q.type==="upload") return (
                <div key={qId} style={{marginBottom:20}}>
                  <p style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:8}}>{qi+1}. {q.label}{q.required&&<span style={{color:C.red}}> *</span>}</p>
                  <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={async e=>{
                      const file=e.target.files?.[0]; if(!file) return;
                      if(file.size>20*1024*1024){toast("File must be under 20 MB.","error");return;}
                      try{
                        const path=`applications/${authUser.uid}_${Date.now()}_${file.name}`;
                        const url=await uploadFile(path,file,()=>{});
                        setAns({name:file.name,url});
                        toast("✅ "+file.name+" uploaded!");
                      }catch(ex){toast("Upload failed: "+ex.message,"error");}
                    }}
                    style={{display:"block",padding:"8px",border:`1.5px dashed ${C.border}`,borderRadius:8,
                      fontFamily:B,fontSize:12,color:C.mid,cursor:"pointer",width:"100%"}}/>
                  {val?.name&&<p style={{fontSize:11,color:C.green,marginTop:6}}>✅ {val.name}</p>}
                </div>
              );
              if(q.type==="yesno") return (
                <div key={qId} style={{marginBottom:20}}>
                  <p style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:10}}>{qi+1}. {q.label}{q.required&&<span style={{color:C.red}}> *</span>}</p>
                  <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                    {(q.options||["Yes","No"]).map(opt=>(
                      <button key={opt} onClick={()=>setAns(opt)} style={{
                        padding:"9px 20px",borderRadius:8,border:`2px solid ${val===opt?C.amber:C.border}`,
                        background:val===opt?`${C.amber}20`:"#fff",color:val===opt?C.amber2:C.navy,
                        fontFamily:B,fontWeight:val===opt?700:400,fontSize:13,cursor:"pointer"}}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              );
              if(q.type==="checkbox") return (
                <div key={qId} style={{marginBottom:20}}>
                  <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
                    <input type="checkbox" checked={!!val} onChange={e=>setAns(e.target.checked)}
                      style={{width:16,height:16,accentColor:C.amber,cursor:"pointer",marginTop:2,flexShrink:0}}/>
                    <span style={{fontSize:13,color:C.navy,lineHeight:1.6}}>{q.checkLabel||q.label}{q.required&&<span style={{color:C.red}}> *</span>}</span>
                  </label>
                </div>
              );
              if(q.type==="text") return (
                <div key={qId} style={{marginBottom:20}}>
                  <p style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:8}}>{qi+1}. {q.label}{q.required&&<span style={{color:C.red}}> *</span>}</p>
                  <input value={val} onChange={e=>setAns(e.target.value)}
                    style={{width:"100%",padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,
                      fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                    onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
              );
              // Default: textarea
              return (
                <div key={qId} style={{marginBottom:20}}>
                  <p style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:8}}>{qi+1}. {q.label}{q.required&&<span style={{color:C.red}}> *</span>}</p>
                  <textarea rows={4} value={val} onChange={e=>setAns(e.target.value)}
                    style={{width:"100%",padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,
                      fontFamily:B,fontSize:13,color:C.navy,resize:"vertical",outline:"none",lineHeight:1.6}}
                    onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
              );
            })}
          </div>)}
          {step===4&&(<div>
            <p style={{fontSize:13,color:C.mid,marginBottom:18}}>Review before submitting.</p>
            {[["Name",form.name],["Phone",form.phone],["Address",form.address],
              ["Education",form.education],["Training",form.training],["Experience",form.experience]].map(([l,v])=>(
              <div key={l} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
                <span style={{minWidth:90,fontSize:11,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",paddingTop:2}}>{l}</span>
                <span style={{fontSize:13,color:C.navy,flex:1,whiteSpace:"pre-wrap",lineHeight:1.6}}>{v||"—"}</span>
              </div>
            ))}
            <div style={{marginTop:12,padding:"10px 14px",background:`${C.sky}11`,border:`1px solid ${C.sky}33`,borderRadius:8}}>
              <p style={{fontSize:12,color:C.navy2,fontWeight:600,marginBottom:6}}>{appQuestions.length} Application Questions answered</p>
            </div>
            <div style={{marginTop:12,padding:"13px 16px",background:`${C.amber}15`,border:`1px solid ${C.amber}55`,borderRadius:8}}>
              <p style={{fontSize:12,color:C.amber2,lineHeight:1.6}}>By submitting you certify all information is accurate and authorize Summer Day Camp to verify employment and contact references.</p>
            </div>
          </div>)}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",padding:"16px 32px",borderTop:`1px solid ${C.border}`,background:C.light}}>
          <Btn onClick={()=>setStep(s=>s-1)} bg="#E5E7EB" color={C.navy} disabled={step===0}>← Back</Btn>
          {step<4?<Btn onClick={()=>setStep(s=>s+1)}>Continue →</Btn>
                 :<Btn onClick={submit} bg={C.green} color="#fff" disabled={busy}>{busy?"Submitting…":"Submit Application ✓"}</Btn>}
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// APPLICANT PORTAL
// ═══════════════════════════════════════════════════════════════════════════════
function ApplicantPortal({authUser,onLogout,toast}){
  const [app,setApp]=useState(null);
  const [masterDocs,setMasterDocs]=useState(null);
  const [loading,setLoading]=useState(true);
  const [offerBusy,setOfferBusy]=useState(false);

  useEffect(()=>{
    const q=query(collection(db,"applications"),where("uid","==",authUser.uid));
    const unsub=onSnapshot(q,snap=>{
      if(!snap.empty) setApp({id:snap.docs[0].id,...snap.docs[0].data()});
      setLoading(false);
    }, err=>{
      console.error("Applicant portal snapshot error:", err.message);
      setLoading(false);
    });
    return unsub;
  },[authUser.uid]);

  useEffect(()=>{
    (async()=>{
      const snap=await getDoc(doc(db,"settings","master"));
      if(snap.exists()) setMasterDocs(snap.data().docs||FACTORY_DOCS);
      else setMasterDocs(FACTORY_DOCS);
    })();
  },[]);

  async function updateDocField(docId, updates) {
    if (!app?.id) { console.error("updateDocField: no app.id"); return; }
    const existing = app.onboardingDocs||[];
    const found = existing.find(d => d.id === docId);
    let docs;
    if (found) {
      // Update existing entry
      docs = existing.map(d => d.id === docId ? {...d, ...updates} : d);
    } else {
      // Doc was added to Settings after offer was sent — find its def from masterDocs and append
      const masterDef = (masterDocs||[]).find(d => d.id === docId) || { id: docId };
      docs = [...existing, { ...masterDef, ...updates }];
    }
    try {
      await updateDoc(doc(db,"applications",app.id),{onboardingDocs:docs});
    } catch(e) {
      console.error("updateDocField FAILED:", e.code, e.message);
    }
  }

  async function respondOffer(accepted){
    setOfferBusy(true);
    await updateDoc(doc(db,"applications",app.id),{
      status:accepted?"offer_accepted":"offer_declined",
      offerResponse:accepted?"accepted":"declined", offerRespondedAt:serverTimestamp(),
    });
    await sendEmail(ADMIN_EMAIL,
      `${accepted?"✅ Offer Accepted":"❌ Offer Declined"} — ${app.name}`,
      `${app.name} has ${accepted?"ACCEPTED":"DECLINED"} the offer.\n\nPay Rate: ${app.offer?.payRate}\nHours/Week: ${app.offer?.hoursPerWeek}\n\nLog in to take next steps.`);
    toast(accepted?"🎉 Offer accepted! We'll be in touch.":"Offer declined. The hiring team has been notified.");
    setOfferBusy(false);
  }

  if(loading||!masterDocs) return <LoadingScreen/>;
  if(!app) return(
    <div style={{minHeight:"100vh",background:C.navy,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <p style={{color:C.sand,fontFamily:H,fontSize:20}}>No application found. Contact your administrator.</p>
    </div>
  );

  const st=STATUSES[app.status]||STATUSES.applied;
  const onboardingDocs=app.onboardingDocs||[];

  // Doc types/names that have dedicated tabs — excluded from onboarding counts & display
  const LEGACY_TYPES = ["bg_workflow"];
  const LEGACY_NAMES = ["background check","fingerprint","maltreatment","cpr","first aid"];
  const isLegacyDoc = d =>
    LEGACY_TYPES.includes(d.docType) ||
    LEGACY_NAMES.some(n => (d.name||"").toLowerCase().includes(n));

  const received=onboardingDocs.filter(d=>d.received && !isLegacyDoc(d)).length;

  // Merge strategy: onboardingDocs has both def+state fields (copied at offer-send).
  // masterDocs may have newer templateUrls if admin re-uploaded a template after sending offer.
  // Priority: masterDocs definition fields > onboardingDocs definition fields > fallback.
  const liveMasterById = {};
  (masterDocs||[]).forEach(d => { liveMasterById[d.id] = d; });

  // Build final doc list: masterDocs defines order/canonical list.
  // For each, pull saved state from onboardingDocs if it exists there.
  // This ensures docs added to Settings after offer-send also appear.
  const onboardingById = {};
  onboardingDocs.forEach(d => { onboardingById[d.id] = d; });

  const masterList = (masterDocs||[]).map(def => {
    const saved = onboardingById[def.id];
    return {
      def: {
        id: def.id,
        name: def.name,
        docType: def.docType,
        templateUrl: def.templateUrl || (saved?.templateUrl) || null,
        requireSignature: def.requireSignature ?? saved?.requireSignature,
        fields: def.fields || saved?.fields || [],
        signaturePage: def.signaturePage ?? saved?.signaturePage,
        signatureX: def.signatureX ?? saved?.signatureX,
        signatureY: def.signatureY ?? saved?.signatureY,
        sigWidth: def.sigWidth ?? saved?.sigWidth,
        sigHeight: def.sigHeight ?? saved?.sigHeight,
        url: def.url || saved?.url,
      },
      state: saved ? {
        received: saved.received, receivedDate: saved.receivedDate,
        uploadedUrl: saved.uploadedUrl, uploadedName: saved.uploadedName,
        formData: saved.formData,
      } : {}
    };
  });

  // Also keep any completed docs that have been removed from Settings (don't lose completed work)
  // But skip legacy doc types that now have dedicated tabs
  onboardingDocs.forEach(d => {
    if (d.received && !isLegacyDoc(d) && !(masterDocs||[]).find(m => m.id === d.id)) {
      masterList.push({
        def: { id:d.id, name:d.name, docType:d.docType, templateUrl:d.templateUrl,
               requireSignature:d.requireSignature, fields:d.fields||[] },
        state: { received:d.received, receivedDate:d.receivedDate,
                 uploadedUrl:d.uploadedUrl, uploadedName:d.uploadedName }
      });
    }
  });

  const docsList = masterList.filter(({def}) => !isLegacyDoc(def));
  const total = docsList.length;

  return(
    <div style={{minHeight:"100vh",background:`linear-gradient(180deg,${C.navy} 0%,${C.navy2} 100%)`}}>
      <header style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"16px 32px",borderBottom:`1px solid ${C.amber}22`,
        background:`${C.navy}dd`,backdropFilter:"blur(8px)",position:"sticky",top:0,zIndex:100}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <Sun size={32}/><div>
            <h1 style={{fontFamily:H,color:C.sand,fontSize:16}}>Summer Day Camp</h1>
            <p style={{color:C.amber,fontSize:10,fontWeight:500}}>Applicant Portal</p>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          <span style={{color:`${C.sand}99`,fontSize:12}}>{app.name}</span>
          <Btn onClick={onLogout} bg={C.navy2} color={C.sand} size="sm">Sign Out</Btn>
        </div>
      </header>

      <div style={{maxWidth:780,margin:"0 auto",padding:"32px 16px"}}>
        {/* Status */}
        <Card className="fu" style={{padding:26,marginBottom:18,borderTop:`4px solid ${st.color}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
            <div>
              <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Application Status</p>
              <div style={{display:"flex",alignItems:"center",gap:9}}>
                <span style={{fontSize:28}}>{st.icon}</span>
                <h2 style={{fontFamily:H,color:st.color,fontSize:24}}>{st.label}</h2>
              </div>
              <p style={{color:C.mid,fontSize:11,marginTop:5}}>Applied: {app.appliedAt?.toDate?.()?.toLocaleDateString?.()||"Recently"}</p>
            </div>
            <Badge status={app.status}/>
          </div>
        </Card>

        {/* Interview scheduled */}
        {app.status==="interview_scheduled"&&app.interviewDate&&(
          <Card className="fu" style={{padding:20,marginBottom:18,borderLeft:`4px solid ${C.amber}`}}>
            <h3 style={{fontFamily:H,color:C.amber2,marginBottom:5}}>📅 Interview Confirmed</h3>
            <p style={{color:C.navy,fontSize:13}}>Your interview is on <strong>{app.interviewDate}</strong> at <strong>{app.interviewTime}</strong>.</p>
            <p style={{color:C.mid,fontSize:11,marginTop:3}}>Please arrive 10 minutes early and bring a valid photo ID.</p>
          </Card>
        )}

        {/* Offer card */}
        {["offer_sent","offer_accepted","offer_declined"].includes(app.status)&&app.offer&&(
          <Card className="fu" style={{padding:26,marginBottom:18,borderTop:`4px solid ${
            app.status==="offer_accepted"?C.green:app.status==="offer_declined"?C.red:C.sky}`}}>
            <h3 style={{fontFamily:H,color:C.navy,fontSize:19,marginBottom:14}}>
              {app.status==="offer_sent"?"💼 Employment Offer":app.status==="offer_accepted"?"✅ Offer Accepted":"↩️ Offer Declined"}
            </h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:18}}>
              {[["Position","Summer Camp Staff"],["Pay Rate",app.offer.payRate],
                ["Hours / Week",app.offer.hoursPerWeek],["Season",app.offer.season||"Summer 2025"],
                ...(app.offer.startDate?[["Start Date",app.offer.startDate]]:[]),
              ].map(([l,v])=>(
                <div key={l} style={{background:C.light,borderRadius:8,padding:"10px 13px"}}>
                  <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3}}>{l}</p>
                  <p style={{fontSize:14,fontWeight:600,color:C.navy}}>{v}</p>
                </div>
              ))}
            </div>
            {app.offer.message&&(
              <div style={{padding:"11px 14px",background:`${C.sky}11`,border:`1px solid ${C.sky}33`,borderRadius:8,marginBottom:16}}>
                <p style={{fontSize:13,color:C.navy2,fontStyle:"italic"}}>"{app.offer.message}"</p>
              </div>
            )}
            {app.status==="offer_sent"&&(
              <div>
                <p style={{fontSize:12,color:C.mid,marginBottom:12}}>Review this offer carefully. Your response goes to the hiring team immediately.</p>
                <div style={{display:"flex",gap:10}}>
                  <Btn onClick={()=>respondOffer(true)} bg={C.green} color="#fff" disabled={offerBusy} size="lg">✓ Accept Offer</Btn>
                  <Btn onClick={()=>respondOffer(false)} bg={C.red} color="#fff" disabled={offerBusy}>✗ Decline</Btn>
                </div>
              </div>
            )}
            {app.status==="offer_accepted"&&(
              <div style={{padding:"10px 13px",background:"#ECFDF5",borderRadius:8,border:`1px solid #A7F3D0`}}>
                <p style={{fontSize:12,color:"#065F46",fontWeight:500}}>✓ Accepted on {app.offerRespondedAt?.toDate?.()?.toLocaleDateString?.()||"record"}. Welcome!</p>
              </div>
            )}
            {app.status==="offer_declined"&&(
              <div style={{padding:"10px 13px",background:"#FEF2F2",borderRadius:8,border:`1px solid #FECACA`}}>
                <p style={{fontSize:12,color:"#991B1B"}}>You declined this offer. The hiring team has been notified.</p>
              </div>
            )}
          </Card>
        )}

        {/* Onboarding docs */}
        {app.status==="hired"&&(
          <Card className="fu" style={{padding:26,marginBottom:18,borderTop:`4px solid ${C.green}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
              <h3 style={{fontFamily:H,color:C.green,fontSize:19}}>🎉 Welcome to the Team!</h3>
              <span style={{fontSize:12,color:C.mid}}>{received} of {total} complete</span>
            </div>
            {total>0&&<><ProgressBar pct={total?Math.round(received/total*100):0}/><div style={{height:14}}/></>}
            <p style={{fontSize:12,color:C.mid,marginBottom:16,lineHeight:1.6}}>
              Complete each required document below. Click on a document to expand it and see your options.
            </p>
            {docsList.map(({def,state})=>(
              <ApplicantDocCard key={def.id} docDef={def} docState={state}
                appId={app.id} applicantName={app.name}
                onUpdated={(id,u)=>updateDocField(id,u)} toast={toast}/>
            ))}
          </Card>
        )}

        {/* Application summary */}
        <Card className="fu" style={{padding:24}}>
          <h3 style={{fontFamily:H,color:C.navy,fontSize:19,marginBottom:16}}>Your Application</h3>
          {[["Phone",app.phone],["Address",app.address],["Education",app.education],
            ["Training",app.training],["Experience",app.experience]].map(([l,v])=>(
            <div key={l} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
              <span style={{minWidth:90,fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",paddingTop:2}}>{l}</span>
              <span style={{fontSize:13,color:C.navy,flex:1,whiteSpace:"pre-wrap",lineHeight:1.5}}>{v||"—"}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
function AdminDashboard({authUser,onLogout,toast}){
  const [tab,setTab]=useState("dashboard");
  const [applicants,setApplicants]=useState([]);
  const [settings,setSettings]=useState(null);
  const [selectedApp,setSelectedApp]=useState(null);
  const [loadingApps,setLoadingApps]=useState(true);

  useEffect(()=>{
    const q=query(collection(db,"applications"),orderBy("appliedAt","desc"));
    const unsub=onSnapshot(q,snap=>{setApplicants(snap.docs.map(d=>({id:d.id,...d.data()})));setLoadingApps(false);});
    return unsub;
  },[]);

  useEffect(()=>{
    (async()=>{
      const snap=await getDoc(doc(db,"settings","master"));
      if(snap.exists()){
        const data = snap.data();
        // Auto-strip legacy doc types that now have dedicated tabs
        const EXCLUDED_TYPES = ["bg_workflow"];
        const EXCLUDED_NAMES = ["background check","fingerprint","maltreatment","cpr","first aid"];
        const cleanedDocs = (data.docs||[]).filter(d=>
          !EXCLUDED_TYPES.includes(d.docType) &&
          !EXCLUDED_NAMES.some(n=>d.name?.toLowerCase().includes(n))
        );
        const needsClean = cleanedDocs.length !== (data.docs||[]).length;
        const cleaned = {...data, docs:cleanedDocs};
        setSettings(cleaned);
        // Persist the cleanup automatically
        if(needsClean) await setDoc(doc(db,"settings","master"),cleaned);
      }
      else { const def={docs:FACTORY_DOCS,questions:FACTORY_QUESTIONS};await setDoc(doc(db,"settings","master"),def);setSettings(def); }
    })();
  },[]);

  const saveSettings=async s=>{setSettings(s);await setDoc(doc(db,"settings","master"),s);};
  const updateApp=async(id,u)=>{await updateDoc(doc(db,"applications",id),u);if(selectedApp?.id===id)setSelectedApp(p=>({...p,...u}));};
  const counts={};Object.keys(STATUSES).forEach(k=>{counts[k]=applicants.filter(a=>a.status===k).length;});

  const activeStaff = applicants.filter(a=>a.status==="hired");
  const allStaff    = applicants.filter(a=>a.status==="hired"||a.status==="terminated");
  const NAV=[
    {id:"dashboard",   icon:"📊",label:"Dashboard"},
    {id:"applicants",  icon:"👥",label:"Applicants",badge:counts.applied||0},
    {id:"onboarding",  icon:"📋",label:"Onboarding",
      badge:activeStaff.filter(a=>(a.onboardingDocs||[]).some(d=>{
        const LTYPES=["bg_workflow"],LNAMES=["background check","fingerprint","maltreatment","cpr","first aid"];
        const isLeg=LTYPES.includes(d.docType)||LNAMES.some(n=>(d.name||"").toLowerCase().includes(n));
        return !d.received&&!isLeg;
      })).length},
    {id:"bgchecks",    icon:"🔍",label:"Background Checks",
      badge:activeStaff.filter(a=>!["approved"].includes(a.bgCheck?.approvalStatus||(a.bgCheck?.fullyCleared?"approved":"pending"))).length||0},
    {id:"maltreatment",icon:"🛡️",label:"Maltreatment",
      badge:activeStaff.filter(a=>!a.mtCheck?.cleared).length||0},
    {id:"cpr",         icon:"❤️",label:"CPR Cards",
      badge:activeStaff.filter(a=>!a.cprCheck?.cardOnFile).length||0},
    {id:"artraining",  icon:"📚",label:"AR Staff Training",
      badge:activeStaff.filter(a=>!a.arTraining?.certified).length||0},
    {id:"terminated",  icon:"📁",label:"Terminated",
      badge:applicants.filter(a=>a.status==="terminated").length||0},
    {id:"reports",     icon:"📊",label:"Reports"},
    {id:"settings",    icon:"⚙️",label:"Settings"},
    {id:"setup",       icon:"🔧",label:"Setup"},
  ];

  return(
    <div style={{display:"flex",minHeight:"100vh",background:C.light}}>
      <aside style={{width:220,background:C.navy,display:"flex",flexDirection:"column",flexShrink:0,borderRight:`1px solid ${C.amber}22`}}>
        <div style={{padding:"22px 16px 12px"}}>
          <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:4}}>
            <Sun size={30}/><div>
              <h2 style={{fontFamily:H,color:C.sand,fontSize:14}}>Summer Camp</h2>
              <p style={{color:C.amber,fontSize:10,fontWeight:500}}>Admin Portal</p>
            </div>
          </div>
        </div>
        <nav style={{flex:1,padding:"0 8px"}}>
          {NAV.map(n=>(
            <button key={n.id} onClick={()=>{setTab(n.id);setSelectedApp(null);}} style={{
              display:"flex",alignItems:"center",gap:9,width:"100%",
              padding:"9px 11px",borderRadius:7,border:"none",marginBottom:2,
              background:tab===n.id?C.amber:"transparent",
              color:tab===n.id?C.navy:`${C.sand}99`,
              fontWeight:tab===n.id?600:400,fontSize:12,textAlign:"left",transition:"all .2s"}}>
              <span style={{fontSize:14}}>{n.icon}</span>
              <span style={{flex:1}}>{n.label}</span>
              {n.badge>0&&<span style={{background:C.red,color:"#fff",borderRadius:20,padding:"1px 6px",fontSize:10,fontWeight:700}}>{n.badge}</span>}
            </button>
          ))}
        </nav>
        <div style={{padding:14,borderTop:`1px solid ${C.amber}22`}}>
          <p style={{color:`${C.sand}44`,fontSize:10,marginBottom:7,wordBreak:"break-all"}}>{authUser.email}</p>
          <Btn onClick={onLogout} bg={C.navy2} color={`${C.sand}99`} size="sm" full>Sign Out</Btn>
        </div>
      </aside>
      <main style={{flex:1,overflow:"auto"}}>
        {tab==="dashboard" &&<DashTab applicants={applicants} counts={counts} onNav={t=>setTab(t)}/>}
        {tab==="applicants"&&!selectedApp&&<ApplicantsTab applicants={applicants} loading={loadingApps} onSelect={a=>setSelectedApp(a)}/>}
        {tab==="applicants"&&selectedApp &&<AppDetail app={selectedApp} settings={settings}
          onBack={()=>setSelectedApp(null)} onUpdate={u=>updateApp(selectedApp.id,u)} toast={toast}/>}
        {tab==="onboarding"&&settings&&<OnboardingTab applicants={activeStaff}
          masterDocs={settings.docs||FACTORY_DOCS} onUpdate={(id,u)=>updateApp(id,u)} toast={toast}/>}
        {tab==="bgchecks"    &&<BgChecksTab applicants={activeStaff}
          onUpdate={(id,u)=>updateApp(id,u)} toast={toast}/>}
        {tab==="maltreatment"&&<MaltreatmentTab applicants={activeStaff}
          onUpdate={(id,u)=>updateApp(id,u)} toast={toast}/>}
        {tab==="cpr"         &&<CprTab applicants={activeStaff}
          onUpdate={(id,u)=>updateApp(id,u)} toast={toast}/>}
        {tab==="artraining"  &&<ArTrainingTab applicants={activeStaff}
          onUpdate={(id,u)=>updateApp(id,u)} toast={toast}/>}
        {tab==="terminated"  &&<TerminatedTab applicants={applicants}
          onUpdate={(id,u)=>updateApp(id,u)} toast={toast}/>}
        {tab==="reports"   &&<ReportsTab applicants={applicants} toast={toast}/>}
        {tab==="settings"  &&settings&&<SettingsTab settings={settings} onSave={saveSettings} toast={toast}/>}
        {tab==="setup"     &&<SetupTab authUser={authUser} toast={toast}/>}
      </main>
    </div>
  );
}

// ─── DASH TAB ─────────────────────────────────────────────────────────────────
function DashTab({applicants,counts,onNav}){
  const needsAction=applicants.filter(a=>["applied","offer_accepted","offer_declined"].includes(a.status));
  return(
    <div style={{padding:32}}>
      <h2 style={{fontFamily:H,color:C.navy,fontSize:30,marginBottom:24}}>Dashboard</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(138px,1fr))",gap:12,marginBottom:26}}>
        {Object.entries(STATUSES).map(([k,v])=>(
          <Card key={k} style={{padding:16,cursor:"pointer",borderTop:`3px solid ${v.color}`}} onClick={()=>onNav("applicants")}>
            <p style={{fontFamily:H,fontSize:30,color:v.color,lineHeight:1}}>{counts[k]||0}</p>
            <p style={{fontSize:10,color:C.mid,marginTop:4}}>{v.label}</p>
          </Card>
        ))}
      </div>
      {needsAction.length>0&&(
        <Card style={{marginBottom:22,borderLeft:`4px solid ${C.red}`}}>
          <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`}}>
            <h3 style={{fontFamily:H,color:C.navy,fontSize:17}}>⚡ Needs Attention</h3>
          </div>
          {needsAction.map(a=>(
            <div key={a.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"13px 20px",borderBottom:`1px solid ${C.border}`}}>
              <div><p style={{fontWeight:600,color:C.navy,fontSize:13}}>{a.name}</p><p style={{fontSize:11,color:C.mid}}>{a.email}</p></div>
              <Badge status={a.status}/>
            </div>
          ))}
        </Card>
      )}
      <Card>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`}}><h3 style={{fontFamily:H,color:C.navy,fontSize:18}}>Recent Applications</h3></div>
        {applicants.slice(0,8).map(a=>(
          <div key={a.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"13px 20px",borderBottom:`1px solid ${C.border}`}}>
            <div><p style={{fontWeight:600,color:C.navy,fontSize:13}}>{a.name}</p><p style={{fontSize:11,color:C.mid}}>{a.email}</p></div>
            <Badge status={a.status}/>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── APPLICANTS TAB ───────────────────────────────────────────────────────────
function ApplicantsTab({applicants,loading,onSelect}){
  const [filter,setFilter]=useState("all");
  const [search,setSearch]=useState("");
  const [showAdd,setShowAdd]=useState(false);
  const [addBusy,setAddBusy]=useState(false);
  const [form,setForm]=useState({
    name:"",email:"",phone:"",address:"",
    position:"Counselor",startDate:"",status:"hired",
    education:"",experience:"",notes:"",
  });
  const setF=k=>e=>setForm(p=>({...p,[k]:e.target.value}));

  const shown=applicants
    .filter(a=>filter==="all"||a.status===filter)
    .filter(a=>!search||a.name?.toLowerCase().includes(search.toLowerCase())||a.email?.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b)=>a.name?.localeCompare(b.name));

  async function saveManual(){
    if(!form.name.trim()){alert("Name is required.");return;}
    if(!form.email.trim()){alert("Email is required.");return;}
    setAddBusy(true);
    try{
      // Load current master docs from settings so the checklist is always up to date
      let masterDocs = FACTORY_DOCS;
      try {
        const snap = await getDoc(doc(db,"settings","master"));
        if(snap.exists() && snap.data().docs) masterDocs = snap.data().docs;
      } catch(e) { /* fall back to factory */ }
      const onboardingDocs = masterDocs.map(d=>({
        ...d, received:false, receivedDate:null,
        uploadedUrl:null, uploadedName:null, formData:null, bgWorkflow:null,
      }));
      await addDoc(collection(db,"applications"),{
        uid:"manual_"+Date.now(),
        email:form.email.trim(),
        name:form.name.trim(),
        phone:form.phone.trim(),
        address:form.address.trim(),
        status:form.status,
        position:form.position,
        startDate:form.startDate||null,
        education:form.education,
        experience:form.experience,
        interviewNotes:form.notes,
        interviewAnswers:{},
        interviewDate:null,interviewTime:null,
        offerSent:form.status==="hired",
        onboardingDocs:form.status==="hired"?onboardingDocs:[],
        offer:form.status==="hired"?{sentAt:new Date().toISOString(),payRate:"",hoursPerWeek:"",season:"Summer 2025",startDate:form.startDate||"",message:""}:null,
        offerResponse:form.status==="hired"?"accepted":null,
        offerRespondedAt:form.status==="hired"?new Date().toISOString():null,
        appliedAt:serverTimestamp(),
        addedManually:true,
        employment:[{employer:"",email:"",years:"",title:""},{employer:"",email:"",years:"",title:""}],
      });
      setShowAdd(false);
      setForm({name:"",email:"",phone:"",address:"",position:"Counselor",startDate:"",status:"hired",education:"",experience:"",notes:""});
    }catch(e){alert("Error: "+e.message);}
    setAddBusy(false);
  }

  return(
    <div style={{padding:32}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <h2 style={{fontFamily:H,color:C.navy,fontSize:30}}>Applicants</h2>
        <Btn bg={C.green} color="#fff" onClick={()=>setShowAdd(true)}>➕ Add Staff Manually</Btn>
      </div>

      {/* Manual add modal */}
      {showAdd&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,
          display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:560,
            maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
            <div style={{padding:"20px 24px",borderBottom:`1px solid ${C.border}`,
              display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <h3 style={{fontFamily:H,color:C.navy,fontSize:20}}>Add Staff Member</h3>
              <button onClick={()=>setShowAdd(false)}
                style={{background:"none",border:"none",fontSize:22,color:C.mid,cursor:"pointer"}}>×</button>
            </div>
            <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:14}}>

              {/* Status selector at top */}
              <div style={{padding:"12px 14px",background:`${C.green}0d`,border:`1px solid ${C.green}44`,borderRadius:9}}>
                <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",
                  letterSpacing:"0.06em",marginBottom:8}}>Add As</p>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {[
                    {val:"hired",    label:"✅ Hired / Active Staff"},
                    {val:"applied",  label:"📋 Applicant"},
                    {val:"interviewed", label:"🎤 Interviewed"},
                  ].map(o=>(
                    <button key={o.val} onClick={()=>setForm(p=>({...p,status:o.val}))}
                      style={{padding:"7px 14px",borderRadius:8,cursor:"pointer",fontFamily:B,
                        fontSize:12,fontWeight:600,border:`2px solid ${form.status===o.val?C.green:C.border}`,
                        background:form.status===o.val?`${C.green}15`:"#fff",
                        color:form.status===o.val?C.green:C.mid}}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Name + email */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div>
                  <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Full Name *</p>
                  <input value={form.name} onChange={setF("name")} placeholder="Jane Smith"
                    style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                    onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
                <div>
                  <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Email *</p>
                  <input value={form.email} onChange={setF("email")} placeholder="jane@email.com" type="email"
                    style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                    onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
              </div>

              {/* Phone + position */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div>
                  <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Phone</p>
                  <input value={form.phone} onChange={setF("phone")} placeholder="(555) 555-5555"
                    style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                    onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
                <div>
                  <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Position / Role</p>
                  <input value={form.position} onChange={setF("position")} placeholder="Counselor"
                    style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                    onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
              </div>

              {/* Address + start date */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div>
                  <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Address</p>
                  <input value={form.address} onChange={setF("address")} placeholder="123 Main St, City, AR"
                    style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                    onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
                <div>
                  <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Start Date</p>
                  <input type="date" value={form.startDate} onChange={setF("startDate")}
                    style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                    onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
              </div>

              {/* Notes */}
              <div>
                <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Internal Notes (optional)</p>
                <textarea rows={3} value={form.notes} onChange={setF("notes")}
                  placeholder="Any relevant notes about this staff member…"
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,
                    fontFamily:B,fontSize:13,color:C.navy,resize:"vertical",outline:"none",lineHeight:1.5}}
                  onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
              </div>

              {form.status==="hired"&&(
                <div style={{padding:"10px 14px",background:`${C.sky}0d`,border:`1px solid ${C.sky}33`,borderRadius:8}}>
                  <p style={{fontSize:12,color:C.navy,lineHeight:1.6}}>
                    ✅ This person will be added as <strong>Active Staff</strong> and will appear immediately in the
                    Background Checks, Maltreatment, CPR, and AR Training tabs.
                  </p>
                </div>
              )}
            </div>

            <div style={{padding:"16px 24px",borderTop:`1px solid ${C.border}`,
              display:"flex",gap:10,justifyContent:"flex-end"}}>
              <Btn bg="#E5E7EB" color={C.mid} onClick={()=>setShowAdd(false)}>Cancel</Btn>
              <Btn bg={C.green} color="#fff" disabled={addBusy} onClick={saveManual}>
                {addBusy?"Saving…":"➕ Add Staff Member"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        <input placeholder="🔍  Search…" value={search} onChange={e=>setSearch(e.target.value)}
          style={{flex:1,minWidth:180,padding:"9px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,background:"#fff",outline:"none"}}/>
        <select value={filter} onChange={e=>setFilter(e.target.value)}
          style={{padding:"9px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,background:"#fff",outline:"none"}}>
          <option value="all">All Statuses</option>
          {Object.entries(STATUSES).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>
      <Card>
        {loading&&<p style={{padding:24,textAlign:"center",color:C.mid}}>Loading…</p>}
        {!loading&&shown.length===0&&<p style={{padding:24,textAlign:"center",color:C.mid}}>No applicants found.</p>}
        {shown.map(a=>(
          <div key={a.id} onClick={()=>onSelect(a)}
            style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 20px",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}
            onMouseEnter={e=>e.currentTarget.style.background=`${C.amber}0a`}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <div>
              <p style={{fontWeight:600,color:C.navy,fontSize:13}}>{a.name}</p>
              <p style={{fontSize:11,color:C.mid}}>{a.email} · {a.phone}</p>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:9}}>
              {a.addedManually&&<span style={{fontSize:10,color:C.mid,fontStyle:"italic"}}>manual</span>}
              <Badge status={a.status}/><span style={{color:C.border,fontSize:18}}>›</span>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── APP DETAIL ───────────────────────────────────────────────────────────────
function AppDetail({app,settings,onBack,onUpdate,toast}){
  const [subTab,setSubTab]=useState("info");
  const [notes,setNotes]=useState(app.interviewNotes||"");
  const [answers,setAnswers]=useState(app.interviewAnswers||{});
  const [schedDate,setSchedDate]=useState(app.interviewDate||"");
  const [schedTime,setSchedTime]=useState(app.interviewTime||"");
  const [showSched,setShowSched]=useState(false);
  const [busy,setBusy]=useState(false);
  const [verifResults,setVerifResults]=useState({});
  const [editingOffer,setEditingOffer]=useState(false);
  const [offerDraft,setOfferDraft]=useState(null);
  const questions=settings?.questions||FACTORY_QUESTIONS;
  const masterDocs=settings?.docs||FACTORY_DOCS;

  // Load latest verification responses directly from verificationRequests collection
  useEffect(()=>{
    if(!app?.id) return;
    (async()=>{
      try{
        const q=query(collection(db,"verificationRequests"),where("appId","==",app.id));
        const snap=await getDocs(q);
        const map={};
        snap.docs.forEach(d=>{const r=d.data();map[r.employerIndex]=r;});
        setVerifResults(map);
      }catch(e){ console.warn("verif fetch:",e.message); }
    })();
  },[app?.id]);

  async function action(type){
    setBusy(true);
    try{
      if(type==="request_interview"){
        await onUpdate({status:"interview_requested"});
        await sendEmail(app.email,"Interview Invitation — Summer Day Camp",
          `Hi ${app.name},\n\nThank you for applying! We'd love to schedule an interview.\n\nSelect a time here:\n${CALENDAR_URL}\n\nSummer Day Camp Hiring Team`);
        toast("📨 Interview invitation sent!");
      }
      if(type==="confirm_interview"){
        if(!schedDate||!schedTime){toast("Enter date and time.","error");setBusy(false);return;}
        await onUpdate({status:"interview_scheduled",interviewDate:schedDate,interviewTime:schedTime});
        await sendEmail(app.email,"Interview Confirmed — Summer Day Camp",
          `Hi ${app.name},\n\nYour interview is confirmed!\n📅 ${schedDate} at ${schedTime}\n📍 Summer Day Camp Office\n\nPlease arrive 10 minutes early with a valid photo ID.`);
        toast("✅ Confirmed & applicant notified!"); setShowSched(false);
      }
      if(type==="mark_interviewed"){
        await onUpdate({status:"interviewed",interviewNotes:notes,interviewAnswers:answers});
        toast("Interview documented.");
      }

      if(type==="hire"){
        await onUpdate({status:"hired",offerSent:true});
        await sendEmail(app.email,"🎉 Welcome! Please complete your onboarding documents.",
          `Hi ${app.name},\n\nWelcome to the team! Please log in to complete your required documents:\nhttps://sdc-employment.web.app\n\nSummer Day Camp Team`);
        toast("🎉 Moved to onboarding!");
      }
      if(type==="reject"){
        await onUpdate({status:"rejected"});
        await sendEmail(app.email,"Application Update — Summer Day Camp",
          `Hi ${app.name},\n\nThank you for your interest. After careful consideration we've decided to move forward with other candidates at this time. We appreciate your time and wish you the best.\n\nSummer Day Camp Team`);
        toast("Applicant notified.");
      }
      if(type==="reopen"){ await onUpdate({status:"interviewed",offer:null,offerResponse:null}); toast("Reopened."); }
    }catch(e){ toast("Error: "+e.message,"error"); }
    setBusy(false);
  }

  async function updateDocField(docId,updates){
    const docs=(app.onboardingDocs||[]).map(d=>d.id===docId?{...d,...updates}:d);
    await onUpdate({onboardingDocs:docs});
  }

  const SUBTABS=[["info","Application"],["interview","Interview"],["docs","Documents"],["notes","Notes"],["offer","💼 Job Offer"]];

  return(
    <div style={{padding:32}}>
      <button onClick={onBack} style={{background:"none",border:"none",color:C.sky,fontSize:12,cursor:"pointer",marginBottom:16,display:"flex",alignItems:"center",gap:5}}>← Back</button>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:H,color:C.navy,fontSize:28}}>{app.name}</h2>
          <p style={{color:C.mid,fontSize:12}}>{app.email} · {app.phone}</p>
          <div style={{marginTop:7}}><Badge status={app.status}/></div>
        </div>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          {app.status==="applied"&&<Btn onClick={()=>action("request_interview")} bg="#8B5CF6" color="#fff" disabled={busy}>📨 Request Interview</Btn>}
          {(app.status==="interview_requested"||app.status==="interview_scheduled")&&
            <Btn onClick={()=>setShowSched(v=>!v)} bg={C.amber} disabled={busy}>📅 {showSched?"Hide":"Confirm Slot"}</Btn>}
          {app.status==="interview_scheduled"&&<Btn onClick={()=>action("mark_interviewed")} bg={C.navy2} color="#fff" disabled={busy}>✓ Mark Interviewed</Btn>}
          {app.status==="interviewed"&&<Btn onClick={()=>setSubTab("offer")} bg={C.sky} color="#fff" disabled={busy}>💼 Send Offer</Btn>}
          {app.status==="interviewed"&&<Btn onClick={()=>action("reject")} bg={C.red} color="#fff" disabled={busy}>✗ Not Selected</Btn>}
          {app.status==="offer_accepted"&&<Btn onClick={()=>action("hire")} bg={C.green} color="#fff" disabled={busy}>🎉 Move to Onboarding</Btn>}
          {app.status==="offer_declined"&&<Btn onClick={()=>action("reopen")} bg={C.navy2} color="#fff" disabled={busy}>↩ Reopen</Btn>}
          {app.status==="offer_declined"&&<Btn onClick={()=>action("reject")} bg={C.red} color="#fff" disabled={busy}>✗ Not Selected</Btn>}
        </div>
      </div>

      {app.status==="offer_accepted"&&(
        <div style={{marginBottom:18,padding:"12px 16px",background:"#ECFDF5",border:`1px solid #A7F3D0`,borderRadius:9}}>
          <p style={{color:"#065F46",fontWeight:600,fontSize:13}}>✅ {app.name} ACCEPTED the offer — click "Move to Onboarding" to proceed.</p>
        </div>
      )}
      {app.status==="offer_declined"&&(
        <div style={{marginBottom:18,padding:"12px 16px",background:"#FEF2F2",border:`1px solid #FECACA`,borderRadius:9}}>
          <p style={{color:"#991B1B",fontWeight:600,fontSize:13}}>❌ {app.name} DECLINED the offer — reopen to negotiate or mark as Not Selected.</p>
        </div>
      )}

      {showSched&&(
        <Card style={{padding:24,marginBottom:20,borderLeft:`4px solid ${C.amber}`}}>
          <h3 style={{fontFamily:H,color:C.navy,marginBottom:7}}>Confirm Interview Time</h3>
          <p style={{fontSize:12,color:C.mid,marginBottom:14}}>Share your booking link or confirm manually: <a href={CALENDAR_URL} target="_blank" rel="noopener noreferrer">{CALENDAR_URL}</a></p>
          <div style={{border:`1.5px solid ${C.border}`,borderRadius:9,overflow:"hidden",marginBottom:16,height:360}}>
            <iframe src={CALENDAR_URL} style={{width:"100%",height:"100%"}} title="Calendar"/>
          </div>
          <div style={{display:"flex",gap:12,marginBottom:14,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:150}}><Inp label="Date" type="date" value={schedDate} onChange={e=>setSchedDate(e.target.value)}/></div>
            <div style={{flex:1,minWidth:150}}><Inp label="Time" type="time" value={schedTime} onChange={e=>setSchedTime(e.target.value)}/></div>
          </div>
          <Btn onClick={()=>action("confirm_interview")} bg={C.amber} disabled={busy}>✓ Confirm & Notify</Btn>
        </Card>
      )}



      <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap"}}>
        {SUBTABS.map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)} style={{
            padding:"8px 16px",borderRadius:8,border:"none",
            background:subTab===id?C.amber:C.border,
            color:subTab===id?C.navy:C.mid,
            fontWeight:subTab===id?600:400,fontSize:12}}>
            {label}
          </button>
        ))}
      </div>

      {subTab==="info"&&(
        <Card style={{padding:24}}>
          {[["Address",app.address],["Education",app.education],["Training",app.training],["Experience",app.experience]].map(([l,v])=>(
            <div key={l} style={{display:"flex",gap:12,padding:"11px 0",borderBottom:`1px solid ${C.border}`}}>
              <span style={{minWidth:90,fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",paddingTop:2}}>{l}</span>
              <span style={{fontSize:13,color:C.navy,flex:1,whiteSpace:"pre-wrap",lineHeight:1.5}}>{v||"—"}</span>
            </div>
          ))}
          {app.employment?.filter(e=>e.employer).length>0&&(
            <div style={{marginTop:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:9}}>
                <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em"}}>Employment History</p>
                <p style={{fontSize:11,color:C.mid}}>Click Request to send verification email</p>
              </div>
              {app.employment.filter(e=>e.employer).map((e,i)=>{
                const vr = verifResults[i] || app.verifications?.[i];
                return (
                <div key={i} style={{background:C.light,borderRadius:10,padding:"13px 16px",marginBottom:10,border:`1px solid ${vr?.status==="completed"?C.green:C.border}`}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                    <div>
                      <p style={{fontWeight:600,color:C.navy,fontSize:13}}>{e.employer} <span style={{color:C.mid,fontWeight:400}}>— {e.title}</span></p>
                      <p style={{fontSize:11,color:C.mid,marginTop:2}}>{e.years}{e.email&&` · ${e.email}`}</p>
                    </div>
                    {e.email&&(
                      vr?.status==="completed" ? (
                        <span style={{fontSize:11,fontWeight:600,color:C.green,padding:"4px 10px",background:`${C.green}15`,borderRadius:20,flexShrink:0}}>✅ Received</span>
                      ) : vr?.requestedAt ? (
                        <span style={{fontSize:11,color:C.amber2,padding:"4px 10px",background:`${C.amber}15`,borderRadius:20,flexShrink:0,cursor:"pointer"}}
                          onClick={async()=>{
                            const url=`https://employment.rogersrecreation.org?verify=${vr.token}`;
                            await sendEmail(e.email,`Employment Verification Request — ${app.name}`,
                              `Hello,\n\nSummer Day Camp is requesting employment verification for ${app.name}, who listed you as a previous employer.\n\nPlease complete the brief form at the link below — no login required:\n\n${url}\n\nThank you,\nSummer Day Camp Hiring Team`);
                            toast("📨 Reminder sent to "+e.email);
                          }}>📨 Pending — Resend</span>
                      ) : (
                        <Btn size="sm" bg={C.sky} color="#fff" onClick={async()=>{
                          const tkn=[...Array(32)].map(()=>Math.random().toString(36)[2]).join("");
                          const url=`https://employment.rogersrecreation.org?verify=${tkn}`;
                          await addDoc(collection(db,"verificationRequests"),{
                            token:tkn, appId:app.id, employerIndex:i,
                            applicantName:app.name, employer:e.employer, jobTitle:e.title||"",
                            employerEmail:e.email, requestedAt:new Date().toISOString(), status:"pending",
                          });
                          const vMap={...(app.verifications||{}),[i]:{token:tkn,requestedAt:new Date().toISOString(),status:"pending",employer:e.employer}};
                          await onUpdate({verifications:vMap});
                          await sendEmail(e.email,`Employment Verification Request — ${app.name}`,
                            `Hello,\n\nSummer Day Camp is requesting employment verification for ${app.name}, who listed ${e.employer} as a previous employer.\n\nPlease complete the brief verification form at the link below — no account or login required:\n\n${url}\n\nThis only takes a few minutes. We ask for dates of employment, rehire status, and any comments.\n\nThank you for your time,\nSummer Day Camp Hiring Team\n${ADMIN_EMAIL}`);
                          toast("📨 Verification request sent to "+e.email);
                        }}>📨 Request Verification</Btn>
                      )
                    )}
                    {!e.email&&<span style={{fontSize:11,color:`${C.mid}88`,fontStyle:"italic"}}>No email listed</span>}
                  </div>

                  {/* Show completed verification response */}
                  {vr?.status==="completed"&&(
                    <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${C.green}33`}}>
                      <p style={{fontSize:10,fontWeight:600,color:C.green,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Verification Response</p>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                        <div style={{background:"#fff",borderRadius:7,padding:"8px 11px"}}>
                          <p style={{fontSize:10,color:C.mid,marginBottom:3}}>Verified By</p>
                          <p style={{fontSize:12,fontWeight:600,color:C.navy}}>{vr.verifierName}</p>
                          <p style={{fontSize:11,color:C.mid}}>{vr.verifierTitle}</p>
                        </div>
                        <div style={{background:"#fff",borderRadius:7,padding:"8px 11px"}}>
                          <p style={{fontSize:10,color:C.mid,marginBottom:3}}>Dates Employed</p>
                          <p style={{fontSize:12,fontWeight:600,color:C.navy}}>{vr.startDate||"—"} → {vr.endDate||"Present"}</p>
                        </div>
                        <div style={{background:"#fff",borderRadius:7,padding:"8px 11px"}}>
                          <p style={{fontSize:10,color:C.mid,marginBottom:3}}>Would Rehire?</p>
                          <p style={{fontSize:13,fontWeight:700,color:vr.rehirable==="Yes"?C.green:vr.rehirable==="No"?C.red:C.amber2}}>
                            {vr.rehirable==="Yes"?"✅ Yes":vr.rehirable==="No"?"❌ No":"⚠️ Conditionally"}
                          </p>
                        </div>
                        <div style={{background:"#fff",borderRadius:7,padding:"8px 11px"}}>
                          <p style={{fontSize:10,color:C.mid,marginBottom:3}}>Submitted</p>
                          <p style={{fontSize:11,color:C.navy}}>{vr.submittedAt?new Date(vr.submittedAt).toLocaleDateString():"—"}</p>
                        </div>
                      </div>
                      {vr.comments&&<div style={{background:"#fff",borderRadius:7,padding:"9px 11px",marginBottom:8}}>
                        <p style={{fontSize:10,color:C.mid,marginBottom:3}}>Comments</p>
                        <p style={{fontSize:12,color:C.navy,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{vr.comments}</p>
                      </div>}
                      {vr.sigUrl&&<div style={{display:"flex",alignItems:"center",gap:10}}>
                        <p style={{fontSize:10,color:C.mid}}>Signature:</p>
                        <img src={vr.sigUrl} alt="sig" style={{height:36,background:"#fff",border:`1px solid ${C.border}`,borderRadius:5,padding:"2px 8px"}}/>
                      </div>}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
          {app.appAnswers&&Object.keys(app.appAnswers).length>0&&(
            <div style={{marginTop:20}}>
              <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:12}}>Application Questions</p>
              {(settings?.appQuestions||FACTORY_QUESTIONS).map((q,qi)=>{
                const qId=q.id||`q${qi}`;
                const ans=app.appAnswers?.[qId];
                if(!ans&&ans!==false) return null;
                return (
                  <div key={qId} style={{marginBottom:14,padding:"11px 14px",background:C.light,borderRadius:8,border:`1px solid ${C.border}`}}>
                    <p style={{fontSize:11,fontWeight:600,color:C.mid,marginBottom:5}}>{qi+1}. {q.label}</p>
                    {q.type==="upload"&&ans?.url?(
                      <a href={ans.url} target="_blank" rel="noopener noreferrer"
                        style={{fontSize:13,color:C.sky,textDecoration:"none"}}>📎 {ans.name}</a>
                    ):(
                      <p style={{fontSize:13,color:C.navy,lineHeight:1.5,whiteSpace:"pre-wrap"}}>
                        {typeof ans==="boolean"?(ans?"✅ Yes":"☐ No"):String(ans)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {subTab==="interview"&&(
        <Card style={{padding:24}}>
          <p style={{color:C.mid,fontSize:12,marginBottom:20}}>Private — not visible to applicant.</p>
          {questions.map((q,i)=>{
            const label = typeof q==="string" ? q : q.label;
            const qId   = typeof q==="string" ? String(i) : (q.id||String(i));
            return (
            <div key={i} style={{marginBottom:20}}>
              <p style={{fontWeight:600,color:C.navy,marginBottom:6,fontSize:13}}>{i+1}. {label}</p>
              <textarea rows={3} placeholder="Record answer…" value={answers[qId]||answers[i]||""}
                onChange={e=>setAnswers(p=>({...p,[qId]:e.target.value}))}
                style={{width:"100%",padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,
                  fontFamily:B,fontSize:13,color:C.navy,resize:"vertical",outline:"none"}}
                onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
            </div>
            );
          })}
          <Btn onClick={()=>{onUpdate({interviewAnswers:answers,interviewNotes:notes});toast("Saved.");}}>Save Record</Btn>
        </Card>
      )}

      {subTab==="docs"&&(
        <Card style={{padding:24}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:10}}>
            <p style={{fontSize:12,color:C.mid,lineHeight:1.6}}>
              Upload documents on behalf of walk-in applicants or mark them received manually.
            </p>
            <Btn size="sm" bg={C.navy2} color="#fff" onClick={async()=>{
              // Merge all masterDocs into onboardingDocs without overwriting existing state
              const existing = app.onboardingDocs||[];
              const existingIds = new Set(existing.map(d=>d.id));
              const toAdd = masterDocs.filter(d=>!existingIds.has(d.id)).map(d=>({
                ...d, received:false, receivedDate:null, uploadedUrl:null,
                uploadedName:null, formData:null, bgWorkflow:null,
              }));
              if(toAdd.length===0){ toast("All documents already in checklist."); return; }
              await onUpdate({onboardingDocs:[...existing,...toAdd]});
              toast(`✅ Added ${toAdd.length} missing doc${toAdd.length>1?"s":""} to checklist.`);
            }}>+ Sync Missing Docs</Btn>
          </div>
          {(app.onboardingDocs||[]).length===0&&(
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <p style={{color:C.mid,fontSize:13,marginBottom:12}}>No documents loaded yet.</p>
              <Btn bg={C.amber} onClick={async()=>{
                const docs=masterDocs.map(d=>({...d,received:false,receivedDate:null,
                  uploadedUrl:null,uploadedName:null,formData:null,bgWorkflow:null}));
                await onUpdate({onboardingDocs:docs});
                toast("✅ Document checklist loaded!");
              }}>Load Document Checklist Now</Btn>
            </div>
          )}
          {masterDocs.map(def=>{
            const state=(app.onboardingDocs||[]).find(d=>d.id===def.id)||{};
            return <AdminDocRow key={def.id} docDef={def} docState={{...state,applicantName:app.name,applicantEmail:app.email}} appId={app.id}
              onUpdated={(id,u)=>updateDocField(id,u)} toast={toast}/>;
          })}
        </Card>
      )}

      {subTab==="notes"&&(
        <Card style={{padding:24}}>
          <p style={{color:C.mid,fontSize:12,marginBottom:12}}>Private admin notes — not visible to applicant.</p>
          <textarea rows={9} placeholder="Notes, impressions, reference check outcomes, background check status…"
            value={notes} onChange={e=>setNotes(e.target.value)}
            style={{width:"100%",padding:13,borderRadius:8,border:`1.5px solid ${C.border}`,
              fontFamily:B,fontSize:13,color:C.navy,resize:"vertical",outline:"none",marginBottom:13}}
            onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
          <Btn onClick={()=>{onUpdate({interviewNotes:notes});toast("Notes saved.");}}>Save Notes</Btn>
        </Card>
      )}

      {subTab==="offer"&&(()=>{
        const o = app.offer;
        const hasOffer = !!o;
        const st = app.status;
        const draft = offerDraft || (o ? {...o} : {payRate:"$15.00/hr",hoursPerWeek:"30–40 hrs/week",season:"Summer 2025",startDate:"",message:""});
        const setD = k => e => setOfferDraft(p=>({...(p||draft),[k]:e.target.value}));

        // Status badge styles
        const offerStatus =
          st==="offer_sent"     ? {label:"⏳ Pending Response", bg:`${C.amber}15`,   border:C.amber,  color:C.amber2} :
          st==="offer_accepted" ? {label:"✅ Accepted",          bg:`${C.green}12`,   border:C.green,  color:C.green}  :
          st==="offer_declined" ? {label:"❌ Declined",          bg:`${C.red}12`,     border:C.red,    color:C.red}    :
          st==="hired"          ? {label:"🎉 Hired & Onboarding",bg:`${C.green}12`,   border:C.green,  color:C.green}  :
          st==="rejected"       ? {label:"✗ Not Selected",       bg:`${C.red}12`,     border:C.red,    color:C.red}    :
                                  {label:"No offer sent yet",    bg:`${C.border}55`,  border:C.border, color:C.mid};

        return (
          <div>
            {/* Status banner */}
            <div style={{padding:"14px 20px",borderRadius:10,marginBottom:20,
              background:offerStatus.bg, border:`1.5px solid ${offerStatus.border}`}}>
              <p style={{fontWeight:700,fontSize:15,color:offerStatus.color}}>{offerStatus.label}</p>
              {st==="offer_sent"&&o?.sentAt&&(
                <p style={{fontSize:12,color:C.mid,marginTop:4}}>Sent {new Date(o.sentAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</p>
              )}
              {(st==="offer_accepted"||st==="offer_declined")&&app.offerRespondedAt&&(
                <p style={{fontSize:12,color:C.mid,marginTop:4}}>Responded {new Date(app.offerRespondedAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</p>
              )}
            </div>

            {/* Offer details card — view or edit mode */}
            {hasOffer&&!editingOffer&&(
              <Card style={{padding:24,marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
                  <h3 style={{fontFamily:H,color:C.navy,fontSize:20}}>Offer Details</h3>
                  {(st==="offer_sent"||st==="offer_accepted"||st==="offer_declined")&&(
                    <Btn size="sm" bg={C.navy2} color="#fff" onClick={()=>{setOfferDraft({...o});setEditingOffer(true);}}>✏️ Edit Offer</Btn>
                  )}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:o.message?16:0}}>
                  {[
                    ["💰 Pay Rate",       o.payRate||"—"],
                    ["🕐 Hours / Week",   o.hoursPerWeek||"—"],
                    ["📅 Season",         o.season||"—"],
                    ["🗓️ Start Date",     o.startDate ? new Date(o.startDate+"T00:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}) : "Not specified"],
                  ].map(([label,val])=>(
                    <div key={label} style={{background:C.light,borderRadius:9,padding:"13px 16px"}}>
                      <p style={{fontSize:11,fontWeight:600,color:C.mid,marginBottom:5}}>{label}</p>
                      <p style={{fontSize:16,fontWeight:700,color:C.navy}}>{val}</p>
                    </div>
                  ))}
                </div>
                {o.message&&(
                  <div style={{marginTop:4,padding:"13px 16px",background:`${C.sky}0d`,border:`1px solid ${C.sky}33`,borderRadius:9}}>
                    <p style={{fontSize:11,fontWeight:600,color:C.mid,marginBottom:6}}>Personal Message</p>
                    <p style={{fontSize:13,color:C.navy,fontStyle:"italic",lineHeight:1.7}}>"{o.message}"</p>
                  </div>
                )}
              </Card>
            )}

            {/* Edit offer form */}
            {hasOffer&&editingOffer&&(
              <Card style={{padding:24,marginBottom:16,borderLeft:`4px solid ${C.amber}`}}>
                <h3 style={{fontFamily:H,color:C.navy,fontSize:18,marginBottom:16}}>✏️ Edit Offer</h3>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                  <Inp label="Pay Rate" value={draft.payRate||""} onChange={setD("payRate")} placeholder="$15.00/hr"/>
                  <Inp label="Hours / Week" value={draft.hoursPerWeek||""} onChange={setD("hoursPerWeek")} placeholder="30–40 hrs/week"/>
                  <Inp label="Season" value={draft.season||""} onChange={setD("season")} placeholder="Summer 2025"/>
                  <Inp label="Start Date" value={draft.startDate||""} onChange={setD("startDate")} type="date"/>
                </div>
                <Inp label="Personal Message (optional)" value={draft.message||""} onChange={setD("message")} rows={3} placeholder="Add a warm personal note…"/>
                <div style={{display:"flex",gap:10,marginTop:16}}>
                  <Btn bg={C.amber} onClick={async()=>{
                    const updated={...draft,updatedAt:new Date().toISOString()};
                    await onUpdate({offer:updated});
                    setEditingOffer(false); setOfferDraft(null);
                    toast("✅ Offer updated.");
                  }}>Save Changes</Btn>
                  <Btn bg={C.sky} color="#fff" onClick={async()=>{
                    const updated={...draft,sentAt:new Date().toISOString(),updatedAt:new Date().toISOString()};
                    await onUpdate({offer:updated,status:"offer_sent",offerResponse:null,offerRespondedAt:null});
                    await sendEmail(app.email,"💼 Updated Employment Offer — Summer Day Camp",
                      `Hi ${app.name},\n\nYour offer has been updated:\n\n💰 ${draft.payRate}  🕐 ${draft.hoursPerWeek}  📅 ${draft.season}${draft.startDate?`\nStart: ${draft.startDate}`:""}${draft.message?`\n\n"${draft.message}"`:""}\n\nPlease log in to review and respond:\nhttps://employment.rogersrecreation.org\n\nSummer Day Camp Hiring Team`);
                    setEditingOffer(false); setOfferDraft(null);
                    toast("📤 Updated offer resent to "+app.email);
                  }}>Save & Resend to Applicant</Btn>
                  <Btn bg="#E5E7EB" color={C.mid} onClick={()=>{setEditingOffer(false);setOfferDraft(null);}}>Cancel</Btn>
                </div>
              </Card>
            )}

            {/* No offer yet — build one */}
            {!hasOffer&&(
              <Card style={{padding:24,marginBottom:16}}>
                <h3 style={{fontFamily:H,color:C.navy,fontSize:18,marginBottom:6}}>Build an Offer</h3>
                <p style={{color:C.mid,fontSize:13,marginBottom:18,lineHeight:1.6}}>
                  {["applied","interview_requested","interview_scheduled","interviewed"].includes(st)
                    ? "Complete the interview process before sending an offer, or send one now if you're ready."
                    : "No offer on file for this applicant."}
                </p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                  <Inp label="Pay Rate" value={draft.payRate} onChange={setD("payRate")} placeholder="$15.00/hr"/>
                  <Inp label="Hours / Week" value={draft.hoursPerWeek} onChange={setD("hoursPerWeek")} placeholder="30–40 hrs/week"/>
                  <Inp label="Season" value={draft.season} onChange={setD("season")} placeholder="Summer 2025"/>
                  <Inp label="Start Date (optional)" value={draft.startDate} onChange={setD("startDate")} type="date"/>
                </div>
                <Inp label="Personal Message (optional)" value={draft.message} onChange={setD("message")} rows={3} placeholder="Add a warm personal note to the offer email…"/>
                <div style={{marginTop:14,marginBottom:16,padding:"11px 14px",background:`${C.sky}11`,border:`1px solid ${C.sky}33`,borderRadius:8}}>
                  <p style={{fontSize:12,color:C.navy2,lineHeight:1.6}}>📋 The current required document checklist will be attached to this offer automatically.</p>
                </div>
                <Btn bg={C.sky} color="#fff" size="lg" onClick={async()=>{
                  const docs=masterDocs.map((d,i)=>({...d,id:d.id||`d${i}`,received:false,receivedDate:null,uploadedUrl:null,uploadedName:null,formData:null}));
                  const sentOffer={...draft,sentAt:new Date().toISOString()};
                  await onUpdate({status:"offer_sent",offer:sentOffer,offerResponse:null,offerRespondedAt:null,onboardingDocs:docs});
                  await sendEmail(app.email,"💼 Employment Offer — Summer Day Camp",
                    `Hi ${app.name},\n\nWe're excited to offer you a position!\n\n💰 ${draft.payRate} · 🕐 ${draft.hoursPerWeek} · 📅 ${draft.season}${draft.startDate?`\nStart: ${draft.startDate}`:""}${draft.message?`\n\n"${draft.message}"`:""}\n\nPlease log in to accept or decline:\nhttps://employment.rogersrecreation.org`);
                  setOfferDraft(null);
                  toast("💼 Offer sent to "+app.name+"!");
                }}>📤 Send Offer to {app.name}</Btn>
              </Card>
            )}

            {/* Timeline */}
            <Card style={{padding:24}}>
              <h3 style={{fontFamily:H,color:C.navy,fontSize:17,marginBottom:18}}>Offer Timeline</h3>
              {[
                {
                  done: hasOffer,
                  label: "Offer Created & Sent",
                  detail: o?.sentAt ? new Date(o.sentAt).toLocaleString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"}) : null,
                  color: C.sky,
                },
                {
                  done: o?.updatedAt && o.updatedAt !== o.sentAt,
                  label: "Offer Last Updated",
                  detail: o?.updatedAt && o.updatedAt !== o.sentAt ? new Date(o.updatedAt).toLocaleString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"}) : null,
                  color: C.amber,
                },
                {
                  done: ["offer_accepted","offer_declined","hired","rejected"].includes(st),
                  label: st==="offer_declined" ? "Applicant Declined" : "Applicant Accepted",
                  detail: app.offerRespondedAt ? new Date(app.offerRespondedAt).toLocaleString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"}) : null,
                  color: st==="offer_declined" ? C.red : C.green,
                  skip: !["offer_accepted","offer_declined","hired","rejected"].includes(st),
                },
                {
                  done: st==="hired",
                  label: "Moved to Onboarding",
                  detail: null,
                  color: C.green,
                },
              ].map((step,i)=>(
                <div key={i} style={{display:"flex",gap:14,marginBottom:i<3?0:0,paddingBottom:i<3?16:0,
                  borderLeft:i<3?`2px solid ${step.done?step.color:C.border}`:"none",
                  marginLeft:10,paddingLeft:16,position:"relative"}}>
                  <div style={{position:"absolute",left:-9,top:2,width:16,height:16,borderRadius:"50%",
                    background:step.done?step.color:C.border,border:`2px solid #fff`,
                    display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {step.done&&<span style={{color:"#fff",fontSize:9,fontWeight:700}}>✓</span>}
                  </div>
                  <div style={{paddingBottom:i<3?16:0}}>
                    <p style={{fontSize:13,fontWeight:600,color:step.done?C.navy:C.mid}}>{step.label}</p>
                    {step.detail&&<p style={{fontSize:11,color:C.mid,marginTop:2}}>{step.detail}</p>}
                    {!step.done&&!step.skip&&<p style={{fontSize:11,color:`${C.mid}88`,marginTop:2,fontStyle:"italic"}}>Pending…</p>}
                  </div>
                </div>
              ))}
            </Card>

            {/* Quick actions */}
            {(st==="offer_accepted")&&(
              <div style={{marginTop:16,padding:"16px 20px",background:`${C.green}10`,border:`1px solid ${C.green}44`,borderRadius:10}}>
                <p style={{fontWeight:600,color:C.green,fontSize:14,marginBottom:10}}>✅ Applicant accepted — ready to move forward</p>
                <Btn bg={C.green} color="#fff" onClick={()=>action("hire")}>🎉 Move to Onboarding</Btn>
              </div>
            )}
            {(st==="offer_declined")&&(
              <div style={{marginTop:16,padding:"16px 20px",background:`${C.red}10`,border:`1px solid ${C.red}44`,borderRadius:10}}>
                <p style={{fontWeight:600,color:C.red,fontSize:14,marginBottom:10}}>❌ Applicant declined — what would you like to do?</p>
                <div style={{display:"flex",gap:10}}>
                  <Btn bg={C.navy2} color="#fff" onClick={()=>{action("reopen");setSubTab("offer");}}>↩ Reopen & Edit Offer</Btn>
                  <Btn bg={C.red} color="#fff" onClick={()=>action("reject")}>✗ Mark Not Selected</Btn>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ─── ONBOARDING TAB ───────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
// BACKGROUND CHECKS TAB
// Auto-adds hired employees; tracks all 7 steps per person
// ═══════════════════════════════════════════════════════════════════════════════
function BgChecksTab({ applicants, onUpdate, toast }) {
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bgSearch, setBgSearch] = useState("");
  const [bgStatusFilter, setBgStatusFilter] = useState("all");

  // Auto-add any hired employees who don't have a bgCheck record yet
  useEffect(() => {
    applicants.forEach(async app => {
      if (!app.bgCheck) {
        try {
          await onUpdate(app.id, {
            bgCheck: {
              startedAt: new Date().toISOString(),
              currentStep: "s1",
              approvalStatus: "pending",
              approvalDate: null,
              expiryDate: null,
              transactionId: null,
              fingerprintDate: null,
              bgClearanceUrl: null,
              bgClearanceName: null,
              notes: "",
            }
          });
        } catch(e) { console.warn("bgCheck init failed:", e.message); }
      }
    });
  }, [applicants.map(a=>a.id).join(",")]);

  const sel = applicants.find(a => a.id === selected);

  // Status helpers
  function bgStatus(app) {
    const bg = app.bgCheck || {};
    const approval = bg.approvalStatus || (bg.fullyCleared ? "approved" : "pending");

    // Pending — yellow
    if (approval==="pending") {
      const step = bg.currentStep || "s1";
      if (step==="s1") return {label:"📋 Awaiting Auth Form", color:"#92400E", bg:"#FFF7ED",       border:"#FCD34D"};
      if (step==="s2") return {label:"📤 Submitted to AR",    color:C.sky,     bg:`${C.sky}11`,    border:`${C.sky}44`};
      if (step==="s3") return {label:"🖐️ Fingerprints Pending",color:C.amber2, bg:`${C.amber}11`,  border:`${C.amber}44`};
      if (step==="s4") return {label:"⏳ Pending Decision",    color:"#92400E", bg:"#FFF7ED",       border:"#FCD34D"};
      return {label:"⏳ Pending", color:"#92400E", bg:"#FFF7ED", border:"#FCD34D"};
    }

    // Provisional — blue
    if (approval==="provisional") {
      if (bg.expiryDate) {
        const days = Math.round((new Date(bg.expiryDate+"T00:00:00")-new Date())/(1000*60*60*24));
        if (days<0)  return {label:`❌ Expired ${Math.abs(days)}d ago`, color:C.red,     bg:"#FEF2F2",  border:"#FECACA"};
        if (days<90) return {label:`🔵 Prov. · ${days}d left`,          color:"#1D4ED8", bg:"#EFF6FF",  border:"#93C5FD"};
        return        {label:`🔵 Prov. · ${days}d left`,                 color:"#1D4ED8", bg:"#EFF6FF",  border:"#93C5FD"};
      }
      return {label:"🔵 Provisional", color:"#1D4ED8", bg:"#EFF6FF", border:"#93C5FD"};
    }

    // Approved — green
    if (approval==="approved") {
      if (bg.expiryDate) {
        const days = Math.round((new Date(bg.expiryDate+"T00:00:00")-new Date())/(1000*60*60*24));
        if (days<0)   return {label:`❌ Expired ${Math.abs(days)}d ago`, color:C.red,    bg:"#FEF2F2",  border:"#FECACA"};
        if (days<90)  return {label:`⚠️ ${days}d remaining`,             color:C.amber2, bg:`${C.amber}11`, border:`${C.amber}44`};
        return         {label:`✅ ${days}d remaining`,                    color:C.green,  bg:"#ECFDF5",  border:"#A7F3D0"};
      }
      return {label:"✅ Approved", color:C.green, bg:"#ECFDF5", border:"#A7F3D0"};
    }

    return {label:"Not Started", color:C.mid, bg:C.light, border:C.border};
  }

  return (
    <div style={{display:"flex",height:"100%",minHeight:"100vh"}}>

      {/* Left: employee list */}
      {(()=>{
        const sorted=[...applicants].sort((a,b)=>a.name.localeCompare(b.name));
        const statuses=["all",...Array.from(new Set(sorted.map(a=>bgStatus(a).label)))];
        const filtered=sorted.filter(a=>
          a.name.toLowerCase().includes(bgSearch.toLowerCase())&&
          (bgStatusFilter==="all"||bgStatus(a).label===bgStatusFilter));
        return (
          <div style={{width:290,borderRight:`1px solid ${C.border}`,flexShrink:0,
            display:"flex",flexDirection:"column",background:"#fff"}}>
            <div style={{padding:"16px 14px 12px",borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <h2 style={{fontFamily:H,color:C.navy,fontSize:18,marginBottom:2}}>Background Checks</h2>
              <p style={{fontSize:11,color:C.mid,marginBottom:10}}>{applicants.length} hired staff</p>
              <div style={{position:"relative",marginBottom:8}}>
                <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",fontSize:13,color:C.mid,pointerEvents:"none"}}>🔍</span>
                <input value={bgSearch} onChange={e=>setBgSearch(e.target.value)} placeholder="Search by name…"
                  style={{width:"100%",padding:"7px 10px 7px 28px",borderRadius:7,border:`1.5px solid ${C.border}`,
                    fontFamily:B,fontSize:12,color:C.navy,outline:"none"}}
                  onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
              </div>
              <select value={bgStatusFilter} onChange={e=>setBgStatusFilter(e.target.value)}
                style={{width:"100%",padding:"7px 10px",borderRadius:7,border:`1.5px solid ${C.border}`,
                  fontFamily:B,fontSize:12,color:C.navy,outline:"none",background:"#fff",cursor:"pointer"}}>
                {statuses.map(s=><option key={s} value={s}>{s==="all"?"All Statuses":s}</option>)}
              </select>
            </div>
            <div style={{padding:"5px 14px",background:C.light,borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <p style={{fontSize:10,color:C.mid}}>{filtered.length} of {applicants.length}{(bgSearch||bgStatusFilter!=="all")?" (filtered)":""}</p>
            </div>
            <div style={{overflowY:"auto",flex:1}}>
              {applicants.length===0&&<div style={{padding:24,textAlign:"center"}}><p style={{fontSize:13,color:C.mid,lineHeight:1.7}}>No hired employees yet.</p></div>}
              {filtered.length===0&&applicants.length>0&&(
                <div style={{padding:20,textAlign:"center"}}>
                  <p style={{fontSize:13,color:C.mid}}>No matches.</p>
                  <button onClick={()=>{setBgSearch("");setBgStatusFilter("all");}}
                    style={{marginTop:6,fontSize:11,color:C.sky,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>Clear</button>
                </div>
              )}
              {filtered.map(app=>{
                const st=bgStatus(app);
                const isSelected=selected===app.id;
                return (
                  <div key={app.id} onClick={()=>setSelected(app.id)}
                    style={{padding:"11px 14px",cursor:"pointer",borderBottom:`1px solid ${C.border}`,
                      background:isSelected?`${C.amber}12`:"#fff",
                      borderLeft:isSelected?`3px solid ${C.amber}`:"3px solid transparent"}}>
                    <p style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:4}}>{app.name}</p>
                    <span style={{display:"inline-block",fontSize:10,fontWeight:600,padding:"2px 8px",
                      borderRadius:20,background:st.bg,color:st.color,border:`1px solid ${st.border}`}}>
                      {st.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Right: detail panel */}
      <div style={{flex:1,overflowY:"auto",padding:28}}>
        {!sel&&(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
            minHeight:400,gap:12}}>
            <span style={{fontSize:48}}>🔍</span>
            <p style={{fontFamily:H,color:C.navy,fontSize:20}}>Select an employee</p>
            <p style={{color:C.mid,fontSize:13}}>to manage their background check process</p>
          </div>
        )}

        {sel&&<BgCheckDetail app={sel} onUpdate={u=>onUpdate(sel.id,u)} toast={toast}/>}
      </div>
    </div>
  );
}

// ─── BG CHECK DETAIL ──────────────────────────────────────────────────────────
function BgCheckDetail({ app, onUpdate, toast }) {
  const bg = app.bgCheck || {};
  const step = bg.currentStep || "s1";
  const [approval,     setApproval]     = useState(bg.approvalStatus||(bg.fullyCleared?"approved":"pending"));
  const [txnInput,     setTxnInput]     = useState(bg.transactionId||"");
  const [approvalDate, setApprovalDate] = useState(bg.approvalDate||"");
  const [expiryDate,   setExpiryDate]   = useState(bg.expiryDate||"");
  const [notes, setNotes] = useState(bg.notes||"");
  const [busy, setBusy] = useState(false);
  const [bgProg, setBgProg] = useState(null);
  const bgFileRef = useRef();

  // Sync state if parent passes updated app data (e.g. after Firestore write)
  useEffect(()=>{
    const b = app.bgCheck||{};
    setApproval(b.approvalStatus||(b.fullyCleared?"approved":"pending"));
    setApprovalDate(b.approvalDate||"");
    setExpiryDate(b.expiryDate||"");
  },[app.id]);

  async function advance(newStep, extra={}) {
    setBusy(true);
    await onUpdate({ bgCheck: { ...bg, currentStep:newStep, ...extra }});
    setBusy(false);
  }

  async function uploadClearance(file) {
    setBgProg(0);
    try {
      const path = `bg-clearances/${app.id}/bg_${Date.now()}_${file.name}`;
      const url = await uploadFile(path, file, p=>setBgProg(p));
      await onUpdate({ bgCheck: { ...bg,
        bgClearanceUrl:url, bgClearanceName:file.name,
        bgClearedAt:new Date().toISOString(),
        fullyCleared:true, currentStep:"s4" }});
      toast("✅ Background check clearance uploaded!");
    } catch(e) { toast("Upload failed: "+e.message,"error"); }
    setBgProg(null);
  }

  const STEPS_DISPLAY = [
    { id:"s1", icon:"📋", label:"Authorization Form",      who:"employee",
      desc:"Employee completes the background check authorization form." },
    { id:"s2", icon:"📤", label:"Submitted to Arkansas",  who:"admin",
      desc:"Admin submits to AR and pays the fee. Enter transaction ID below." },
    { id:"s3", icon:"🖐️", label:"Fingerprint Appointment", who:"employee",
      desc:"Employee schedules and completes fingerprinting using the transaction ID." },
    { id:"s4", icon:"✅", label:"Final Clearances",        who:"admin",
      desc:"Admin uploads the background check clearance letter from Arkansas." },
  ];
  const stepIdx = STEPS_DISPLAY.findIndex(s=>s.id===step);

  return (
    <div style={{maxWidth:700}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:H,color:C.navy,fontSize:26,marginBottom:4}}>{app.name}</h2>
          <p style={{color:C.mid,fontSize:12}}>{app.email} · {app.phone}</p>
        </div>
        {(approval==="approved"||approval==="provisional")&&(
          <div style={{padding:"10px 18px",borderRadius:10,
            background:approval==="approved"?"#ECFDF5":"#EFF6FF",
            border:`1.5px solid ${approval==="approved"?"#A7F3D0":"#93C5FD"}`}}>
            <p style={{fontWeight:700,fontSize:14,color:approval==="approved"?C.green:"#1D4ED8"}}>
              {approval==="approved"?"✅ Approved":"🔵 Provisional"}
            </p>
            {bg.expiryDate&&<p style={{fontSize:11,color:C.mid,marginTop:2}}>
              Exp: {new Date(bg.expiryDate+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
            </p>}
          </div>
        )}
      </div>

      {/* Step progress */}
      <div style={{display:"flex",marginBottom:28}}>
        {STEPS_DISPLAY.map((s,i)=>(
          <div key={s.id} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",position:"relative"}}>
            {i<STEPS_DISPLAY.length-1&&(
              <div style={{position:"absolute",top:16,left:"50%",width:"100%",height:2,zIndex:0,
                background:(approval==="approved"||approval==="provisional")||stepIdx>i?C.green:C.border}}/>
            )}
            <div style={{width:32,height:32,borderRadius:"50%",zIndex:1,position:"relative",
              border:`2px solid ${(approval==="approved"||approval==="provisional")||stepIdx>i?C.green:s.id===step?"#7C3AED":C.border}`,
              background:(approval==="approved"||approval==="provisional")||stepIdx>i?C.green:s.id===step?"#7C3AED":"#fff",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:13,color:(approval==="approved"||approval==="provisional")||stepIdx>i||s.id===step?"#fff":C.mid,fontWeight:700}}>
              {(approval==="approved"||approval==="provisional")||stepIdx>i?"✓":i+1}
            </div>
            <p style={{fontSize:10,textAlign:"center",marginTop:5,lineHeight:1.3,
              color:s.id===step?"#7C3AED":stepIdx>i?C.green:C.mid,
              fontWeight:s.id===step?700:400}}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Step 1 — waiting for employee auth form */}
      {step==="s1"&&(
        <Card style={{padding:22,marginBottom:16,borderLeft:"4px solid #7C3AED"}}>
          <p style={{fontWeight:700,color:"#6D28D9",fontSize:15,marginBottom:8}}>📋 Step 1: Employee Authorization Form</p>
          <p style={{fontSize:13,color:C.mid,lineHeight:1.7,marginBottom:16}}>
            The employee needs to complete and submit the Arkansas background check authorization form.
            Once you receive it (via email), advance to Step 2.
          </p>

          {/* Instructions preview */}
          <div style={{padding:"14px 16px",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:9,marginBottom:16}}>
            <p style={{fontSize:11,fontWeight:700,color:"#6D28D9",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Instructions sent to employee</p>
            {[
              ["🔗","Background Check Form","https://ardhs.formstack.com/forms/arkansas_dhs_criminal_background_check_request_form"],
              ["ℹ️","Reason for Background Check: ECE Early Childhood Education",null],
              ["ℹ️","Child Care Facility/Provider Name: Rogers Activity Center",null],
              ["ℹ️","Facility License Number: 6019",null],
              ["ℹ️","Facility Email: Cody@rogersrecreation.com",null],
              ["ℹ️","If you are a college student, frequently it is best to use your home address.",null],
            ].map(([icon,text,url],i)=>(
              <div key={i} style={{display:"flex",gap:8,marginBottom:6,alignItems:"flex-start"}}>
                <span style={{fontSize:13,flexShrink:0}}>{icon}</span>
                {url
                  ? <a href={url} target="_blank" rel="noopener noreferrer"
                      style={{fontSize:12,color:"#4F46E5",lineHeight:1.5,wordBreak:"break-all"}}>{text} ↗</a>
                  : <p style={{fontSize:12,color:C.navy,lineHeight:1.5}}>{text}</p>
                }
              </div>
            ))}
          </div>

          {bg.bgReminderSentAt&&(
            <p style={{fontSize:11,color:C.mid,marginBottom:12}}>
              Last reminder sent: {new Date(bg.bgReminderSentAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
            </p>
          )}
          {bg.authFormSubmittedAt&&(
            <div style={{padding:"10px 14px",background:"#ECFDF5",border:"1px solid #A7F3D0",borderRadius:8,marginBottom:16}}>
              <p style={{fontSize:12,fontWeight:600,color:C.green}}>
                ✅ Form submitted by employee on {new Date(bg.authFormSubmittedAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
              </p>
            </div>
          )}
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <Btn bg="#7C3AED" color="#fff" disabled={busy}
              onClick={()=>advance("s2",{submittedToARAt:null})}>
              {bg.authFormSubmittedAt?"✓ Advance to Step 2 →":"Mark Form Received & Advance →"}
            </Btn>
            <Btn bg={C.sky} color="#fff" disabled={busy}
              onClick={async()=>{
                setBusy(true);
                await sendEmail(app.email,
                  "Action Required: Background Check Form — Summer Day Camp",
                  `Hi ${app.name},

As part of your hiring process with Summer Day Camp, you are required to complete the Arkansas DHS Criminal Background Check Request Form.

Please use the following information when completing the form:

📋 Background Check Request Form:
https://ardhs.formstack.com/forms/arkansas_dhs_criminal_background_check_request_form

When filling out the form, please use the following:
• Reason for Background Check: ECE Early Childhood Education
• Child Care Facility/Provider Name: Rogers Activity Center
• Facility License Number: 6019
• Facility Email: Cody@rogersrecreation.com

Note: If you are a college student, it is frequently best to use your home address.

Once you have submitted the form, it will be emailed directly to us. You do not need to do anything else at that point.

If you have any questions, please contact us at ${ADMIN_EMAIL}.

Thank you,
Summer Day Camp Hiring Team`);
                await onUpdate({bgCheck:{...bg, bgReminderSentAt:new Date().toISOString()}});
                toast("📨 Background check instructions sent to "+app.email);
                setBusy(false);
              }}>
              📨 Send Instructions to Employee
            </Btn>
          </div>
        </Card>
      )}

      {/* Step 2 — admin submits to AR, enters transaction ID */}
      {step==="s2"&&(
        <Card style={{padding:22,marginBottom:16,borderLeft:`4px solid ${C.sky}`}}>
          <p style={{fontWeight:700,color:"#1D4ED8",fontSize:15,marginBottom:8}}>📤 Step 2: Submit to Arkansas & Send Transaction ID</p>
          <p style={{fontSize:13,color:C.mid,lineHeight:1.7,marginBottom:16}}>
            Submit the background check form to the State of Arkansas and pay the fee.
            Once you receive the confirmation/transaction ID, enter it below and click Send —
            the employee will be emailed the ID and fingerprinting instructions automatically.
          </p>
          <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap",marginBottom:12}}>
            <div style={{flex:1,minWidth:220}}>
              <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>
                Transaction ID from Arkansas *
              </p>
              <input value={txnInput} onChange={e=>setTxnInput(e.target.value)}
                placeholder="e.g. ECE004827974"
                style={{width:"100%",padding:"11px 14px",borderRadius:8,
                  border:"1.5px solid #93C5FD",fontFamily:"monospace",fontSize:15,
                  fontWeight:700,color:"#1E3A8A",outline:"none",letterSpacing:"0.06em"}}
                onFocus={e=>e.target.style.borderColor="#2563EB"}
                onBlur={e=>e.target.style.borderColor="#93C5FD"}/>
            </div>
            <Btn bg="#2563EB" color="#fff" disabled={busy||!txnInput.trim()}
              onClick={async()=>{
                if(!txnInput.trim()){toast("Enter the transaction ID first.","error");return;}
                setBusy(true);
                await onUpdate({ bgCheck:{...bg, currentStep:"s3",
                  transactionId:txnInput.trim(), txnSentAt:new Date().toISOString()}});
                await sendEmail(app.email,
                  "🔢 Your Fingerprinting Transaction ID — Summer Day Camp",
                  `Hi ${app.name},

Here is the number you will need for when you go for your fingerprinting.

Schedule your appointment at https://telegov.egov.com/dhsfingerprint

Here is your Transaction ID: ${txnInput.trim()}

Once you have completed your fingerprint appointment, please let us know.

If you have any questions, contact us at ${ADMIN_EMAIL}.

Summer Day Camp Hiring Team`);
                toast("📨 Transaction ID sent to "+app.email);
                setBusy(false);
              }}>
              📨 Send to {app.name.split(" ")[0]}
            </Btn>
          </div>
          <div style={{padding:"10px 14px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8}}>
            <p style={{fontSize:11,color:"#1D4ED8",lineHeight:1.6}}>
              Clicking Send will email {app.name} their transaction ID along with a link to
              schedule their fingerprint appointment at <strong>telegov.egov.com/dhsfingerprint</strong>.
            </p>
          </div>
        </Card>
      )}

      {/* Step 3 — waiting for fingerprints */}
      {step==="s3"&&(
        <Card style={{padding:22,marginBottom:16,borderLeft:`4px solid ${C.amber}`}}>
          <p style={{fontWeight:700,color:C.amber2,fontSize:15,marginBottom:8}}>🖐️ Step 3: Fingerprint Appointment</p>
          <div style={{padding:"12px 16px",background:`${C.amber}0d`,border:`1px solid ${C.amber}44`,borderRadius:9,marginBottom:16}}>
            <p style={{fontSize:11,fontWeight:600,color:C.mid,marginBottom:4}}>Transaction ID sent to employee</p>
            <p style={{fontFamily:"monospace",fontSize:18,fontWeight:700,color:C.navy,letterSpacing:"0.06em"}}>
              {bg.transactionId}
            </p>
            {bg.txnSentAt&&<p style={{fontSize:11,color:C.mid,marginTop:4}}>
              Sent {new Date(bg.txnSentAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
            </p>}
          </div>
          <p style={{fontSize:13,color:C.mid,lineHeight:1.7,marginBottom:16}}>
            Waiting for {app.name.split(" ")[0]} to schedule and complete their fingerprint appointment.
            Once confirmed, advance to Step 4 to upload the final clearance letters.
          </p>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <Btn bg={C.amber} onClick={()=>advance("s4",{fingerprintDoneAt:new Date().toISOString()})} disabled={busy}>
              ✓ Fingerprints Complete — Advance to Clearances →
            </Btn>
            <Btn size="sm" bg={C.navy2} color="#fff" disabled={busy}
              onClick={async()=>{
                setBusy(true);
                await sendEmail(app.email,
                  "Reminder: Schedule Your Fingerprint Appointment — Summer Day Camp",
                  `Hi ${app.name},

This is a reminder to schedule your fingerprint appointment.

Schedule at: https://telegov.egov.com/dhsfingerprint

Your Transaction ID: ${bg.transactionId}

Please complete this as soon as possible.

Summer Day Camp Hiring Team`);
                toast("📨 Reminder sent to "+app.email);
                setBusy(false);
              }}>
              📨 Send Reminder
            </Btn>
          </div>
        </Card>
      )}

      {/* Step 4 — approval status & clearance upload */}
      {(step==="s4"||approval==="approved"||approval==="provisional")&&(
        <Card style={{padding:22,marginBottom:16,
          borderLeft:`4px solid ${approval==="approved"?C.green:approval==="provisional"?"#2563EB":"#92400E"}`}}>
          <p style={{fontWeight:700,fontSize:15,marginBottom:8,
            color:approval==="approved"?C.green:approval==="provisional"?"#2563EB":"#92400E"}}>
            {approval==="approved"?"✅ Step 4: Approved"
             :approval==="provisional"?"🔵 Step 4: Provisional Approval"
             :"⏳ Step 4: Awaiting Decision from Arkansas"}
          </p>
          <p style={{fontSize:13,color:C.mid,lineHeight:1.7,marginBottom:20}}>
            Set the approval status once you receive the decision from Arkansas.
            Upload the clearance letter and enter the approval and expiration dates.
          </p>

          {/* Approval status buttons */}
          <div style={{marginBottom:20}}>
            <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",
              letterSpacing:"0.06em",marginBottom:10}}>Approval Status</p>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {[
                {val:"pending",     label:"⏳ Pending",      active:"#92400E", bg:"#FFF7ED", border:"#FCD34D"},
                {val:"provisional", label:"🔵 Provisional",  active:"#1D4ED8", bg:"#EFF6FF", border:"#93C5FD"},
                {val:"approved",    label:"✅ Approved",      active:C.green,   bg:"#ECFDF5", border:"#A7F3D0"},
              ].map(opt=>(
                <button key={opt.val} onClick={async()=>{
                  setApproval(opt.val);
                  // Auto-calc expiry if we have an approval date but no expiry yet
                  let autoExpiry = expiryDate;
                  if(opt.val!=="pending" && approvalDate && !expiryDate){
                    const d=new Date(approvalDate+"T00:00:00");
                    d.setFullYear(d.getFullYear()+5);
                    autoExpiry=d.toISOString().split("T")[0];
                    setExpiryDate(autoExpiry);
                  }
                  setBusy(true);
                  await onUpdate({bgCheck:{...bg, approvalStatus:opt.val,
                    fullyCleared:opt.val==="approved",
                    approvedAt:opt.val!=="pending"?new Date().toISOString():null,
                    expiryDate:autoExpiry||null}});
                  setBusy(false);
                  toast(`Status set to ${opt.label}`);
                }} style={{
                  padding:"10px 18px",borderRadius:9,cursor:"pointer",fontFamily:B,fontWeight:700,fontSize:13,
                  border:`2px solid ${approval===opt.val?opt.active:C.border}`,
                  background:approval===opt.val?opt.bg:"#fff",
                  color:approval===opt.val?opt.active:C.mid}}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dates — approval date auto-calculates +5 year expiry */}
          {approval!=="pending"&&(
            <div style={{marginBottom:20}}>
              <div style={{display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap",marginBottom:12}}>
                <div>
                  <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",
                    letterSpacing:"0.06em",marginBottom:6}}>Approval Date</p>
                  <input type="date" value={approvalDate} onChange={e=>{
                    setApprovalDate(e.target.value);
                    if(e.target.value){
                      const d=new Date(e.target.value+"T00:00:00");
                      d.setFullYear(d.getFullYear()+5);
                      setExpiryDate(d.toISOString().split("T")[0]);
                    } else { setExpiryDate(""); }
                  }}
                    style={{padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,
                      fontFamily:"inherit",fontSize:13,color:C.navy,outline:"none"}}
                    onFocus={e=>e.target.style.borderColor=C.amber}
                    onBlur={e=>e.target.style.borderColor=C.border}/>
                </div>
                {expiryDate&&(
                  <div style={{padding:"9px 14px",background:`${C.sky}11`,border:`1px solid ${C.sky}44`,borderRadius:8}}>
                    <p style={{fontSize:10,fontWeight:700,color:C.mid,marginBottom:2}}>AUTO-CALCULATED EXPIRY (5 YRS)</p>
                    <p style={{fontSize:14,fontWeight:700,color:"#1D4ED8"}}>
                      {new Date(expiryDate+"T00:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
                    </p>
                  </div>
                )}
                {approvalDate&&(
                  <Btn size="sm" onClick={async()=>{
                    setBusy(true);
                    await onUpdate({bgCheck:{...bg,approvalDate,expiryDate}});
                    toast("✅ Dates saved.");
                    setBusy(false);
                  }} disabled={busy}>Save</Btn>
                )}
              </div>
            </div>
          )}

          {/* Expiry alert */}
          {expiryDate&&(()=>{
            const days=Math.round((new Date(expiryDate+"T00:00:00")-new Date())/(1000*60*60*24));
            return (
              <div style={{marginBottom:16,padding:"10px 14px",borderRadius:8,
                background:days<0?"#FEF2F2":days<90?`${C.amber}11`:"#ECFDF5",
                border:`1px solid ${days<0?"#FECACA":days<90?C.amber:"#A7F3D0"}`}}>
                <p style={{fontSize:12,fontWeight:600,
                  color:days<0?C.red:days<90?C.amber2:C.green}}>
                  {days<0?`❌ Expired ${Math.abs(days)} days ago`
                   :days===0?"Expires today!"
                   :`Expires in ${days} days — ${new Date(expiryDate+"T00:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}`}
                </p>
              </div>
            );
          })()}

          {/* Clearance letter upload */}
          <div style={{padding:"14px 16px",background:bg.bgClearanceUrl?"#ECFDF5":C.light,
            border:`1px solid ${bg.bgClearanceUrl?"#A7F3D0":C.border}`,borderRadius:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              marginBottom:10,flexWrap:"wrap",gap:8}}>
              <p style={{fontWeight:600,color:C.navy,fontSize:13}}>
                {bg.bgClearanceUrl?"✅":"⬜"} Clearance / Decision Letter
              </p>
              {bg.bgClearanceUrl&&(
                <a href={bg.bgClearanceUrl} target="_blank" rel="noopener noreferrer"
                  style={{fontSize:12,color:C.sky,fontWeight:600}}>View ↗</a>
              )}
            </div>
            {bgProg!==null&&<div style={{marginBottom:10}}><ProgressBar pct={bgProg}/></div>}
            <input ref={bgFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
              style={{display:"none"}}
              onChange={e=>{const f=e.target.files?.[0];if(f)uploadClearance(f);}}/>
            <Btn size="sm" bg={bg.bgClearanceUrl?C.navy2:C.green} color="#fff"
              onClick={()=>bgFileRef.current?.click()}>
              {bg.bgClearanceUrl?"Replace File":"📎 Upload Letter"}
            </Btn>
          </div>
        </Card>
      )}

      {/* Admin notes */}
      <Card style={{padding:22}}>
        <p style={{fontSize:11,fontWeight:700,color:C.mid,textTransform:"uppercase",
          letterSpacing:"0.07em",marginBottom:8}}>Internal Notes</p>
        <textarea rows={3} value={notes} onChange={e=>setNotes(e.target.value)}
          placeholder="Notes on this background check process…"
          style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,
            fontFamily:B,fontSize:13,color:C.navy,resize:"vertical",outline:"none",
            marginBottom:10,lineHeight:1.5}}
          onFocus={e=>e.target.style.borderColor=C.amber}
          onBlur={e=>e.target.style.borderColor=C.border}/>
        <Btn size="sm" onClick={async()=>{
          await onUpdate({bgCheck:{...bg,notes}});
          toast("Notes saved.");
        }}>Save Notes</Btn>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MALTREATMENT TAB
// Auto-adds hired employees; 3-step: form submitted → admin submits to state → approved/declined
// ═══════════════════════════════════════════════════════════════════════════════
function MaltreatmentTab({ applicants, onUpdate, toast }) {
  const [selected, setSelected] = useState(null);
  const [mtSearch, setMtSearch] = useState("");
  const [mtStatusFilter, setMtStatusFilter] = useState("all");

  // Auto-initialize mtCheck for any hired employee who doesn't have one yet
  useEffect(() => {
    applicants.forEach(async app => {
      if (!app.mtCheck) {
        try {
          await onUpdate(app.id, {
            mtCheck: {
              startedAt: new Date().toISOString(),
              currentStep: "s1",
              cleared: false,
              declined: false,
              submittedToStateAt: null,
              clearedAt: null,
              declinedAt: null,
              clearanceUrl: null,
              clearanceName: null,
              notes: "",
            }
          });
        } catch(e) { console.warn("mtCheck init:", e.message); }
      }
    });
  }, [applicants.map(a=>a.id).join(",")]);

  const sel = applicants.find(a => a.id === selected);

  function mtStatus(app) {
    const mt = app.mtCheck || {};
    if (mt.cleared) {
      if (mt.expiryDate) {
        const days=Math.round((new Date(mt.expiryDate+"T00:00:00")-new Date())/(1000*60*60*24));
        if (days<0)  return {label:`❌ Expired ${Math.abs(days)}d ago`, color:C.red,    bg:"#FEF2F2",      border:"#FECACA"};
        if (days<60) return {label:`⚠️ ${days}d remaining`,             color:C.amber2, bg:`${C.amber}11`, border:`${C.amber}44`};
        return        {label:`✅ ${days}d remaining`,                    color:C.green,  bg:"#ECFDF5",      border:"#A7F3D0"};
      }
      return {label:"✅ Approved — no exp date", color:C.green, bg:"#ECFDF5", border:"#A7F3D0"};
    }
    if (mt.declined) return {label:"❌ Declined",           color:C.red,    bg:"#FEF2F2",      border:"#FECACA"};
    const step = mt.currentStep || "s1";
    if (step==="s1") return {label:"📋 Awaiting Form",      color:"#7C3AED", bg:"#F5F3FF",      border:"#DDD6FE"};
    if (step==="s2") return {label:"📤 Submitted to State", color:C.sky,     bg:`${C.sky}11`,   border:`${C.sky}44`};
    if (step==="s3") return {label:"⏳ Awaiting Decision",  color:C.amber2,  bg:`${C.amber}11`, border:`${C.amber}44`};
    return {label:"Not Started", color:C.mid, bg:C.light, border:C.border};
  }

  return (
    <div style={{display:"flex",height:"100%",minHeight:"100vh"}}>

      {/* Left: employee list */}
      {(()=>{
        const sorted=[...applicants].sort((a,b)=>a.name.localeCompare(b.name));
        const statuses=["all",...Array.from(new Set(sorted.map(a=>mtStatus(a).label)))];
        const filtered=sorted.filter(a=>
          a.name.toLowerCase().includes(mtSearch.toLowerCase())&&
          (mtStatusFilter==="all"||mtStatus(a).label===mtStatusFilter));
        return (
          <div style={{width:290,borderRight:`1px solid ${C.border}`,flexShrink:0,
            display:"flex",flexDirection:"column",background:"#fff"}}>
            <div style={{padding:"16px 14px 12px",borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <h2 style={{fontFamily:H,color:C.navy,fontSize:18,marginBottom:2}}>Maltreatment Registry</h2>
              <p style={{fontSize:11,color:C.mid,marginBottom:10}}>{applicants.length} hired staff</p>
              <div style={{position:"relative",marginBottom:8}}>
                <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",fontSize:13,color:C.mid,pointerEvents:"none"}}>🔍</span>
                <input value={mtSearch} onChange={e=>setMtSearch(e.target.value)} placeholder="Search by name…"
                  style={{width:"100%",padding:"7px 10px 7px 28px",borderRadius:7,border:`1.5px solid ${C.border}`,
                    fontFamily:B,fontSize:12,color:C.navy,outline:"none"}}
                  onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
              </div>
              <select value={mtStatusFilter} onChange={e=>setMtStatusFilter(e.target.value)}
                style={{width:"100%",padding:"7px 10px",borderRadius:7,border:`1.5px solid ${C.border}`,
                  fontFamily:B,fontSize:12,color:C.navy,outline:"none",background:"#fff",cursor:"pointer"}}>
                {statuses.map(s=><option key={s} value={s}>{s==="all"?"All Statuses":s}</option>)}
              </select>
            </div>
            <div style={{padding:"5px 14px",background:C.light,borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
              <p style={{fontSize:10,color:C.mid}}>{filtered.length} of {applicants.length}{(mtSearch||mtStatusFilter!=="all")?" (filtered)":""}</p>
            </div>
            <div style={{overflowY:"auto",flex:1}}>
              {applicants.length===0&&<div style={{padding:24,textAlign:"center"}}><p style={{fontSize:13,color:C.mid,lineHeight:1.7}}>No hired employees yet.</p></div>}
              {filtered.length===0&&applicants.length>0&&(
                <div style={{padding:20,textAlign:"center"}}>
                  <p style={{fontSize:13,color:C.mid}}>No matches.</p>
                  <button onClick={()=>{setMtSearch("");setMtStatusFilter("all");}}
                    style={{marginTop:6,fontSize:11,color:C.sky,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>Clear</button>
                </div>
              )}
              {filtered.map(app=>{
                const st=mtStatus(app);
                const isSelected=selected===app.id;
                return (
                  <div key={app.id} onClick={()=>setSelected(app.id)}
                    style={{padding:"11px 14px",cursor:"pointer",borderBottom:`1px solid ${C.border}`,
                      background:isSelected?`${C.amber}12`:"#fff",
                      borderLeft:isSelected?`3px solid ${C.amber}`:"3px solid transparent"}}>
                    <p style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:4}}>{app.name}</p>
                    <span style={{display:"inline-block",fontSize:10,fontWeight:600,padding:"2px 8px",
                      borderRadius:20,background:st.bg,color:st.color,border:`1px solid ${st.border}`}}>
                      {st.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Right: detail panel */}
      <div style={{flex:1,overflowY:"auto",padding:28}}>
        {!sel&&(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",
            justifyContent:"center",minHeight:400,gap:12}}>
            <span style={{fontSize:48}}>🛡️</span>
            <p style={{fontFamily:H,color:C.navy,fontSize:20}}>Select an employee</p>
            <p style={{color:C.mid,fontSize:13}}>to manage their maltreatment registry check</p>
          </div>
        )}
        {sel&&<MaltreatmentDetail app={sel} onUpdate={u=>onUpdate(sel.id,u)} toast={toast}/>}
      </div>
    </div>
  );
}

// ─── MALTREATMENT DETAIL ──────────────────────────────────────────────────────
function MaltreatmentDetail({ app, onUpdate, toast }) {
  const mt = app.mtCheck || {};
  const step = mt.currentStep || "s1";
  const [notes,      setNotes]      = useState(mt.notes||"");
  const [expiryDate, setExpiryDate] = useState(mt.expiryDate||"");
  const [busy,  setBusy]  = useState(false);
  const [prog,  setProg]  = useState(null);
  const fileRef = useRef();

  async function advance(newStep, extra={}) {
    setBusy(true);
    await onUpdate({ mtCheck:{ ...mt, currentStep:newStep, ...extra }});
    setBusy(false);
  }

  const STEPS = [
    { id:"s1", n:1, icon:"📋", label:"Form Submitted",       desc:"Employee submits their maltreatment registry check authorization form." },
    { id:"s2", n:2, icon:"📤", label:"Submitted to State",   desc:"Admin submits the form to the state registry." },
    { id:"s3", n:3, icon:"⏳", label:"Awaiting Decision",    desc:"Waiting for the state to return approval or declination." },
  ];
  const stepIdx = STEPS.findIndex(s=>s.id===step);

  const isDone = mt.cleared || mt.declined;

  return (
    <div style={{maxWidth:680}}>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",
        marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:H,color:C.navy,fontSize:26,marginBottom:4}}>{app.name}</h2>
          <p style={{color:C.mid,fontSize:12}}>{app.email} · {app.phone}</p>
        </div>
        {mt.cleared&&(
          <div style={{padding:"10px 18px",background:"#ECFDF5",border:"1.5px solid #A7F3D0",borderRadius:10}}>
            <p style={{fontWeight:700,color:C.green,fontSize:14}}>✅ Approved by State</p>
            {mt.clearedAt&&<p style={{fontSize:11,color:C.mid,marginTop:2}}>
              {new Date(mt.clearedAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
            </p>}
          </div>
        )}
        {mt.declined&&(
          <div style={{padding:"10px 18px",background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:10}}>
            <p style={{fontWeight:700,color:C.red,fontSize:14}}>❌ Declined by State</p>
            {mt.declinedAt&&<p style={{fontSize:11,color:C.mid,marginTop:2}}>
              {new Date(mt.declinedAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
            </p>}
          </div>
        )}
      </div>

      {/* Step progress */}
      <div style={{display:"flex",marginBottom:28}}>
        {STEPS.map((s,i)=>{
          const done = isDone ? true : stepIdx > i;
          const active = !isDone && s.id===step;
          const color = mt.declined&&isDone ? C.red : C.green;
          return (
            <div key={s.id} style={{flex:1,display:"flex",flexDirection:"column",
              alignItems:"center",position:"relative"}}>
              {i<STEPS.length-1&&(
                <div style={{position:"absolute",top:16,left:"50%",width:"100%",height:2,zIndex:0,
                  background:done?color:C.border}}/>
              )}
              <div style={{width:32,height:32,borderRadius:"50%",zIndex:1,position:"relative",
                border:`2px solid ${done?color:active?"#7C3AED":C.border}`,
                background:done?color:active?"#7C3AED":"#fff",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:13,color:done||active?"#fff":C.mid,fontWeight:700}}>
                {done?(mt.declined&&i===2?"✗":"✓"):i+1}
              </div>
              <p style={{fontSize:10,textAlign:"center",marginTop:5,lineHeight:1.3,
                color:active?"#7C3AED":done?color:C.mid,
                fontWeight:active?700:400}}>{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* Step 1 — waiting for employee to submit form */}
      {step==="s1"&&(
        <Card style={{padding:22,marginBottom:16,borderLeft:"4px solid #7C3AED"}}>
          <p style={{fontWeight:700,color:"#6D28D9",fontSize:15,marginBottom:8}}>
            📋 Step 1: Employee Authorization Form
          </p>
          <p style={{fontSize:13,color:C.mid,lineHeight:1.7,marginBottom:16}}>
            The employee needs to complete and submit the Arkansas Maltreatment Registry check form.
            Once you receive it (via email), advance to Step 2.
          </p>

          {/* Instructions preview */}
          <div style={{padding:"14px 16px",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:9,marginBottom:16}}>
            <p style={{fontSize:11,fontWeight:700,color:"#6D28D9",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Instructions sent to employee</p>
            {[
              ["🔗","Maltreatment Registry Form","https://ardhs.formstack.com/forms/dcfs_central_registry_request_v2"],
              ["ℹ️","You are needing a registry check because: You are a potential or current employee of a childcare facility or residential facility for children/youth licensed by Arkansas DHS.",null],
              ["ℹ️","2nd Email Address: cody@rogersrecreation.com",null],
              ["ℹ️","If you are a college student, frequently it is best to use your home address.",null],
            ].map(([icon,text,url],i)=>(
              <div key={i} style={{display:"flex",gap:8,marginBottom:6,alignItems:"flex-start"}}>
                <span style={{fontSize:13,flexShrink:0}}>{icon}</span>
                {url
                  ? <a href={url} target="_blank" rel="noopener noreferrer"
                      style={{fontSize:12,color:"#4F46E5",lineHeight:1.5,wordBreak:"break-all"}}>{text} ↗</a>
                  : <p style={{fontSize:12,color:C.navy,lineHeight:1.5}}>{text}</p>
                }
              </div>
            ))}
          </div>

          {mt.mtReminderSentAt&&(
            <p style={{fontSize:11,color:C.mid,marginBottom:12}}>
              Last reminder sent: {new Date(mt.mtReminderSentAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
            </p>
          )}
          {mt.formReceivedAt&&(
            <div style={{padding:"10px 14px",background:"#ECFDF5",border:"1px solid #A7F3D0",
              borderRadius:8,marginBottom:16}}>
              <p style={{fontSize:12,fontWeight:600,color:C.green}}>
                ✅ Form received on {new Date(mt.formReceivedAt).toLocaleDateString("en-US",
                  {month:"long",day:"numeric",year:"numeric"})}
              </p>
            </div>
          )}
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <Btn bg="#7C3AED" color="#fff" disabled={busy}
              onClick={()=>advance("s2",{formReceivedAt:new Date().toISOString()})}>
              ✓ Form Received — Advance to Submit →
            </Btn>
            <Btn bg={C.sky} color="#fff" disabled={busy}
              onClick={async()=>{
                setBusy(true);
                await sendEmail(app.email,
                  "Action Required: Maltreatment Registry Form — Summer Day Camp",
                  `Hi ${app.name},

As part of your hiring process with Summer Day Camp, you are required to complete the Arkansas DCFS Central Maltreatment Registry Request Form.

Please use the following link and information:

🔗 Maltreatment Registry Form:
https://ardhs.formstack.com/forms/dcfs_central_registry_request_v2

When filling out the form, please note:
• You are needing a registry check because: You are a potential or current employee of a childcare facility or residential facility for children/youth licensed by Arkansas DHS.
• 2nd Email Address: cody@rogersrecreation.com

Note: If you are a college student, it is frequently best to use your home address.

Once you submit the form, it will be sent directly to us. You do not need to do anything else at that point.

If you have any questions, please contact us at ${ADMIN_EMAIL}.

Thank you,
Summer Day Camp Hiring Team`);
                await onUpdate({mtCheck:{...mt, mtReminderSentAt:new Date().toISOString()}});
                toast("📨 Maltreatment instructions sent to "+app.email);
                setBusy(false);
              }}>
              📨 Send Instructions to Employee
            </Btn>
          </div>
        </Card>
      )}

      {/* Step 2 — admin submits to state */}
      {step==="s2"&&(
        <Card style={{padding:22,marginBottom:16,borderLeft:`4px solid ${C.sky}`}}>
          <p style={{fontWeight:700,color:"#1D4ED8",fontSize:15,marginBottom:8}}>
            📤 Step 2: Submit to State Registry
          </p>
          <p style={{fontSize:13,color:C.mid,lineHeight:1.7,marginBottom:20}}>
            Submit {app.name.split(" ")[0]}'s maltreatment registry check to the state.
            Once submitted, click below to mark it submitted and move to waiting for the state's decision.
          </p>
          <div style={{padding:"12px 16px",background:"#EFF6FF",border:"1px solid #BFDBFE",
            borderRadius:9,marginBottom:20}}>
            <p style={{fontSize:12,color:"#1D4ED8",lineHeight:1.6}}>
              After submission the state will contact you directly with their approval or declination letter.
              Once you receive it, advance to Step 3 and upload the result.
            </p>
          </div>
          <Btn bg={C.sky} color="#fff" disabled={busy}
            onClick={()=>advance("s3",{submittedToStateAt:new Date().toISOString()})}>
            ✓ Submitted to State — Awaiting Decision →
          </Btn>
        </Card>
      )}

      {/* Step 3 — waiting for state decision */}
      {step==="s3"&&!isDone&&(
        <Card style={{padding:22,marginBottom:16,borderLeft:`4px solid ${C.amber}`}}>
          <p style={{fontWeight:700,color:C.amber2,fontSize:15,marginBottom:8}}>
            ⏳ Step 3: Awaiting State Decision
          </p>
          {mt.submittedToStateAt&&(
            <div style={{padding:"10px 14px",background:`${C.amber}0d`,border:`1px solid ${C.amber}44`,
              borderRadius:8,marginBottom:16}}>
              <p style={{fontSize:12,color:C.amber2}}>
                Submitted to state on {new Date(mt.submittedToStateAt).toLocaleDateString("en-US",
                  {month:"long",day:"numeric",year:"numeric"})}
              </p>
            </div>
          )}
          <p style={{fontSize:13,color:C.mid,lineHeight:1.7,marginBottom:20}}>
            Waiting for the state to return their decision. Once you receive the letter,
            upload it below and mark the result.
          </p>

          {/* Upload clearance letter */}
          <div style={{marginBottom:20}}>
            <p style={{fontSize:11,fontWeight:700,color:C.mid,textTransform:"uppercase",
              letterSpacing:"0.06em",marginBottom:8}}>Upload Decision Letter (optional)</p>
            {prog!==null&&<div style={{marginBottom:10}}><ProgressBar pct={prog}/></div>}
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
              style={{display:"none"}}
              onChange={async e=>{
                const file=e.target.files?.[0]; if(!file) return;
                setProg(0);
                try {
                  const path=`maltreatment/${app.id}/${Date.now()}_${file.name}`;
                  const url=await uploadFile(path,file,p=>setProg(p));
                  await onUpdate({mtCheck:{...mt,clearanceUrl:url,clearanceName:file.name}});
                  toast("✅ Letter uploaded!");
                } catch(ex){toast("Upload failed: "+ex.message,"error");}
                setProg(null);
              }}/>
            {mt.clearanceUrl?(
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <a href={mt.clearanceUrl} target="_blank" rel="noopener noreferrer"
                  style={{fontSize:13,fontWeight:600,color:C.sky}}>📄 View Uploaded Letter ↗</a>
                <button onClick={()=>fileRef.current?.click()}
                  style={{fontSize:11,color:C.mid,background:"none",border:"none",
                    cursor:"pointer",textDecoration:"underline"}}>Replace</button>
              </div>
            ):(
              <Btn size="sm" bg={C.navy2} color="#fff" onClick={()=>fileRef.current?.click()}>
                📎 Upload Decision Letter
              </Btn>
            )}
          </div>

          {/* Approve or Decline buttons */}
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <Btn bg={C.green} color="#fff" disabled={busy}
              onClick={async()=>{
                setBusy(true);
                await onUpdate({mtCheck:{...mt,cleared:true,declined:false,
                  currentStep:"s3",clearedAt:new Date().toISOString()}});
                toast("✅ Marked Approved!");
                setBusy(false);
              }}>
              ✅ Mark Approved
            </Btn>
            <Btn bg={C.red} color="#fff" disabled={busy}
              onClick={async()=>{
                setBusy(true);
                await onUpdate({mtCheck:{...mt,declined:true,cleared:false,
                  currentStep:"s3",declinedAt:new Date().toISOString()}});
                toast("Marked as Declined.");
                setBusy(false);
              }}>
              ❌ Mark Declined
            </Btn>
          </div>
        </Card>
      )}

      {/* Final result — cleared */}
      {mt.cleared&&(
        <Card style={{padding:22,marginBottom:16,borderLeft:`4px solid ${C.green}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",
            marginBottom:14,flexWrap:"wrap",gap:10}}>
            <p style={{fontWeight:700,color:C.green,fontSize:15}}>✅ Approved by State</p>
            {mt.clearedAt&&<p style={{fontSize:11,color:C.mid}}>
              {new Date(mt.clearedAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
            </p>}
          </div>
          {mt.clearanceUrl&&(
            <a href={mt.clearanceUrl} target="_blank" rel="noopener noreferrer"
              style={{display:"inline-block",fontSize:13,fontWeight:600,color:C.sky,marginBottom:14}}>
              📄 View Clearance Letter ↗
            </a>
          )}

          {/* Approval + expiry date — auto-calculates +2 years */}
          <div style={{marginBottom:16,padding:"14px 16px",background:C.light,
            border:`1px solid ${C.border}`,borderRadius:9}}>
            <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",
              letterSpacing:"0.06em",marginBottom:10}}>Approval Date <span style={{fontWeight:400,color:C.mid}}> — expiry auto-calculated (+2 yrs)</span></p>
            <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
              <div>
                <p style={{fontSize:10,color:C.mid,marginBottom:5}}>Approval Date</p>
                <input type="date" value={expiryDate?new Date(new Date(expiryDate+"T00:00:00").setFullYear(new Date(expiryDate+"T00:00:00").getFullYear()-2)).toISOString().split("T")[0]:mt.approvalDate||""}
                  onChange={e=>{
                    const val=e.target.value;
                    if(val){
                      const d=new Date(val+"T00:00:00");
                      d.setFullYear(d.getFullYear()+2);
                      setExpiryDate(d.toISOString().split("T")[0]);
                      onUpdate({mtCheck:{...mt,approvalDate:val,expiryDate:d.toISOString().split("T")[0]}});
                    }
                  }}
                  style={{padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,
                    fontFamily:"inherit",fontSize:13,color:C.navy,outline:"none"}}
                  onFocus={e=>e.target.style.borderColor=C.amber}
                  onBlur={e=>e.target.style.borderColor=C.border}/>
              </div>
              {expiryDate&&(
                <div style={{padding:"9px 14px",background:`${C.sky}11`,border:`1px solid ${C.sky}44`,borderRadius:8}}>
                  <p style={{fontSize:10,fontWeight:700,color:C.mid,marginBottom:2}}>EXPIRES (AUTO)</p>
                  <p style={{fontSize:14,fontWeight:700,color:"#1D4ED8"}}>
                    {new Date(expiryDate+"T00:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
                  </p>
                </div>
              )}
            </div>
            {mt.expiryDate&&(()=>{
              const days=Math.round((new Date(mt.expiryDate+"T00:00:00")-new Date())/(1000*60*60*24));
              return (
                <div style={{marginTop:10,padding:"9px 12px",borderRadius:7,
                  background:days<0?"#FEF2F2":days<60?`${C.amber}11`:"#ECFDF5",
                  border:`1px solid ${days<0?"#FECACA":days<60?C.amber:"#A7F3D0"}`}}>
                  <p style={{fontSize:12,fontWeight:600,
                    color:days<0?C.red:days<60?C.amber2:C.green}}>
                    {days<0?`❌ Expired ${Math.abs(days)} days ago`
                     :days===0?"Expires today!"
                     :`Expires in ${days} days — ${new Date(mt.expiryDate+"T00:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}`}
                  </p>
                </div>
              );
            })()}
          </div>

          <p style={{fontSize:13,color:C.mid,marginBottom:16,lineHeight:1.7}}>
            {app.name.split(" ")[0]} has been cleared by the state maltreatment registry.
          </p>
          <Btn size="sm" bg="#E5E7EB" color={C.mid} disabled={busy}
            onClick={()=>advance("s3",{cleared:false,clearedAt:null,expiryDate:null})}>
            Undo — Reopen
          </Btn>
        </Card>
      )}

      {/* Final result — declined */}
      {mt.declined&&(
        <Card style={{padding:22,marginBottom:16,borderLeft:`4px solid ${C.red}`}}>
          <p style={{fontWeight:700,color:C.red,fontSize:15,marginBottom:8}}>❌ Declined by State</p>
          {mt.clearanceUrl&&(
            <a href={mt.clearanceUrl} target="_blank" rel="noopener noreferrer"
              style={{display:"inline-block",fontSize:13,fontWeight:600,color:C.sky,marginBottom:12}}>
              📄 View Decision Letter ↗
            </a>
          )}
          <p style={{fontSize:13,color:C.mid,marginBottom:16,lineHeight:1.7}}>
            {app.name.split(" ")[0]} has been declined by the state maltreatment registry.
            Please consult your HR policy for next steps.
          </p>
          <Btn size="sm" bg="#E5E7EB" color={C.mid} disabled={busy}
            onClick={()=>advance("s3",{declined:false,declinedAt:null})}>
            Undo — Reopen
          </Btn>
        </Card>
      )}

      {/* Admin notes */}
      <Card style={{padding:22}}>
        <p style={{fontSize:11,fontWeight:700,color:C.mid,textTransform:"uppercase",
          letterSpacing:"0.07em",marginBottom:8}}>Internal Notes</p>
        <textarea rows={3} value={notes} onChange={e=>setNotes(e.target.value)}
          placeholder="Notes on this maltreatment check…"
          style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,
            fontFamily:B,fontSize:13,color:C.navy,resize:"vertical",outline:"none",
            marginBottom:10,lineHeight:1.5}}
          onFocus={e=>e.target.style.borderColor=C.amber}
          onBlur={e=>e.target.style.borderColor=C.border}/>
        <Btn size="sm" onClick={async()=>{
          await onUpdate({mtCheck:{...mt,notes}});
          toast("Notes saved.");
        }}>Save Notes</Btn>
      </Card>

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED HELPER — simple two-panel staff tab layout
// ═══════════════════════════════════════════════════════════════════════════════
function StaffListPanel({ title, subtitle, people, selected, onSelect, getStatus }) {
  const [search,     setSearch]     = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Sort alphabetically by first name
  const sorted = [...people].sort((a,b)=>a.name.localeCompare(b.name));

  // Collect unique status labels for the filter dropdown
  const statuses = ["all", ...Array.from(new Set(sorted.map(p=>getStatus(p).label)))];

  // Apply search + status filter
  const filtered = sorted.filter(app=>{
    const matchSearch = app.name.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter==="all" || getStatus(app).label===statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div style={{width:290,borderRight:`1px solid ${C.border}`,flexShrink:0,
      display:"flex",flexDirection:"column",background:"#fff"}}>

      {/* Header */}
      <div style={{padding:"16px 14px 12px",borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
        <h2 style={{fontFamily:H,color:C.navy,fontSize:18,marginBottom:2}}>{title}</h2>
        <p style={{fontSize:11,color:C.mid,marginBottom:10}}>{subtitle}</p>

        {/* Search */}
        <div style={{position:"relative",marginBottom:8}}>
          <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",
            fontSize:13,color:C.mid,pointerEvents:"none"}}>🔍</span>
          <input
            value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search by name…"
            style={{width:"100%",padding:"7px 10px 7px 28px",borderRadius:7,
              border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:12,
              color:C.navy,outline:"none",background:"#fff"}}
            onFocus={e=>e.target.style.borderColor=C.amber}
            onBlur={e=>e.target.style.borderColor=C.border}/>
        </div>

        {/* Status filter */}
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
          style={{width:"100%",padding:"7px 10px",borderRadius:7,
            border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:12,
            color:C.navy,outline:"none",background:"#fff",cursor:"pointer"}}>
          {statuses.map(s=>(
            <option key={s} value={s}>{s==="all"?"All Statuses":s}</option>
          ))}
        </select>
      </div>

      {/* Results count */}
      <div style={{padding:"6px 14px",background:C.light,borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
        <p style={{fontSize:10,color:C.mid}}>
          {filtered.length} of {people.length} employee{people.length!==1?"s":""}
          {search||statusFilter!=="all"?" (filtered)":""}
        </p>
      </div>

      {/* List */}
      <div style={{overflowY:"auto",flex:1}}>
        {people.length===0&&(
          <div style={{padding:24,textAlign:"center"}}>
            <p style={{fontSize:13,color:C.mid,lineHeight:1.7}}>No employees yet.</p>
          </div>
        )}
        {filtered.length===0&&people.length>0&&(
          <div style={{padding:20,textAlign:"center"}}>
            <p style={{fontSize:13,color:C.mid}}>No matches found.</p>
            <button onClick={()=>{setSearch("");setStatusFilter("all");}}
              style={{marginTop:8,fontSize:11,color:C.sky,background:"none",
                border:"none",cursor:"pointer",textDecoration:"underline"}}>
              Clear filters
            </button>
          </div>
        )}
        {filtered.map(app=>{
          const st=getStatus(app);
          const isSel=selected===app.id;
          return (
            <div key={app.id} onClick={()=>onSelect(app.id)}
              style={{padding:"11px 14px",cursor:"pointer",borderBottom:`1px solid ${C.border}`,
                background:isSel?`${C.amber}12`:"#fff",
                borderLeft:isSel?`3px solid ${C.amber}`:"3px solid transparent"}}>
              <p style={{fontWeight:600,color:C.navy,fontSize:13,marginBottom:4}}>{app.name}</p>
              <span style={{display:"inline-block",fontSize:10,fontWeight:600,padding:"2px 8px",
                borderRadius:20,background:st.bg,color:st.color,border:`1px solid ${st.border}`}}>
                {st.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CPR TAB
// ═══════════════════════════════════════════════════════════════════════════════
function CprTab({ applicants, onUpdate, toast }) {
  const [selected, setSelected] = useState(null);

  function cprStatus(app) {
    const c = app.cprCheck||{};
    if (c.cardOnFile && c.expDate) {
      const days = Math.round((new Date(c.expDate+"T00:00:00")-new Date())/(1000*60*60*24));
      if (days<0)   return {label:`❌ Expired ${Math.abs(days)}d ago`, color:C.red,    bg:"#FEF2F2",      border:"#FECACA"};
      if (days<60)  return {label:`⚠️ ${days}d remaining`,             color:C.amber2, bg:`${C.amber}11`, border:`${C.amber}44`};
      return         {label:`✅ ${days}d remaining`,                    color:C.green,  bg:"#ECFDF5",      border:"#A7F3D0"};
    }
    if (c.cardOnFile) return {label:"📎 On File — No Exp Date", color:C.sky, bg:`${C.sky}11`, border:`${C.sky}44`};
    return             {label:"⬜ Not on File",                  color:C.mid, bg:C.light,      border:C.border};
  }

  const sel = applicants.find(a=>a.id===selected);

  return (
    <div style={{display:"flex",height:"100%",minHeight:"100vh"}}>
      <StaffListPanel title="CPR Cards" subtitle={`${applicants.length} active staff`}
        people={applicants} selected={selected} onSelect={setSelected} getStatus={cprStatus}/>
      <div style={{flex:1,overflowY:"auto",padding:28}}>
        {!sel&&(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:400,gap:12}}>
            <span style={{fontSize:48}}>❤️</span>
            <p style={{fontFamily:H,color:C.navy,fontSize:20}}>Select an employee</p>
            <p style={{color:C.mid,fontSize:13}}>to manage their CPR card</p>
          </div>
        )}
        {sel&&<CprDetail app={sel} onUpdate={u=>onUpdate(sel.id,u)} toast={toast}/>}
      </div>
    </div>
  );
}

function CprDetail({ app, onUpdate, toast }) {
  const c = app.cprCheck||{};
  const [expDate, setExpDate] = useState(c.expDate||"");
  const [notes,   setNotes]   = useState(c.notes||"");
  const [prog,    setProg]    = useState(null);
  const [busy,    setBusy]    = useState(false);
  const fileRef = useRef();

  // Check if CPR card already in onboarding docs
  const onboardingCpr = (app.onboardingDocs||[]).find(d=>
    d.name?.toLowerCase().includes("cpr") && d.uploadedUrl);

  async function saveExpDate() {
    setBusy(true);
    await onUpdate({cprCheck:{...c, expDate, updatedAt:new Date().toISOString()}});
    toast("✅ Expiration date saved.");
    setBusy(false);
  }

  async function uploadCard(file) {
    if (file.size>20*1024*1024){toast("File must be under 20 MB.","error");return;}
    setProg(0);
    try {
      const path=`cpr-cards/${app.id}/${Date.now()}_${file.name}`;
      const url=await uploadFile(path,file,p=>setProg(p));
      await onUpdate({cprCheck:{...c,cardOnFile:true,cardUrl:url,cardName:file.name,
        uploadedAt:new Date().toISOString(),expDate:expDate||c.expDate||""}});
      toast("✅ CPR card uploaded!");
    } catch(ex){toast("Upload failed: "+ex.message,"error");}
    setProg(null);
  }

  const cardUrl  = c.cardUrl || onboardingCpr?.uploadedUrl;
  const cardName = c.cardName|| onboardingCpr?.uploadedName;

  return (
    <div style={{maxWidth:620}}>
      <div style={{marginBottom:24}}>
        <h2 style={{fontFamily:H,color:C.navy,fontSize:26,marginBottom:4}}>{app.name}</h2>
        <p style={{color:C.mid,fontSize:12}}>{app.email}</p>
      </div>

      {/* Card on file */}
      <Card style={{padding:22,marginBottom:16}}>
        <p style={{fontWeight:700,color:C.navy,fontSize:15,marginBottom:14}}>❤️ CPR Card</p>

        {onboardingCpr&&!c.cardUrl&&(
          <div style={{padding:"10px 14px",background:"#EFF6FF",border:"1px solid #BFDBFE",
            borderRadius:8,marginBottom:14}}>
            <p style={{fontSize:12,color:"#1D4ED8",marginBottom:4,fontWeight:600}}>
              📎 Found in onboarding documents
            </p>
            <a href={onboardingCpr.uploadedUrl} target="_blank" rel="noopener noreferrer"
              style={{fontSize:12,color:C.sky}}>View uploaded CPR card ↗</a>
            <div style={{marginTop:8}}>
              <Btn size="sm" bg={C.sky} color="#fff" onClick={async()=>{
                await onUpdate({cprCheck:{...c,cardOnFile:true,cardUrl:onboardingCpr.uploadedUrl,
                  cardName:onboardingCpr.uploadedName,pulledFromOnboarding:true}});
                toast("✅ CPR card linked from onboarding docs!");
              }}>Use This as CPR Card on File</Btn>
            </div>
          </div>
        )}

        {cardUrl&&(
          <div style={{padding:"12px 14px",background:"#ECFDF5",border:"1px solid #A7F3D0",
            borderRadius:8,marginBottom:14}}>
            <p style={{fontSize:11,fontWeight:700,color:C.green,marginBottom:4}}>✅ Card on File</p>
            <a href={cardUrl} target="_blank" rel="noopener noreferrer"
              style={{fontSize:13,fontWeight:600,color:C.sky}}>📄 {cardName||"View CPR Card"} ↗</a>
            {c.uploadedAt&&<p style={{fontSize:10,color:C.mid,marginTop:3}}>
              Uploaded {new Date(c.uploadedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
            </p>}
          </div>
        )}

        {prog!==null&&<div style={{marginBottom:10}}><ProgressBar pct={prog}/></div>}
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
          style={{display:"none"}} onChange={e=>{const f=e.target.files?.[0];if(f)uploadCard(f);}}/>
        <Btn size="sm" bg={cardUrl?C.navy2:C.amber} color={cardUrl?"#fff":C.navy}
          onClick={()=>fileRef.current?.click()}>
          {cardUrl?"🔄 Replace CPR Card":"📎 Upload CPR Card"}
        </Btn>
      </Card>

      {/* Expiration date */}
      <Card style={{padding:22,marginBottom:16}}>
        <p style={{fontWeight:700,color:C.navy,fontSize:15,marginBottom:6}}>📅 Expiration Date</p>
        <p style={{fontSize:12,color:C.mid,marginBottom:14,lineHeight:1.6}}>
          Enter the expiration date from the CPR card. You'll see a warning badge 60 days before expiry.
        </p>
        <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
          <div>
            <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",
              letterSpacing:"0.05em",marginBottom:6}}>Expiration Date</p>
            <input type="date" value={expDate} onChange={e=>setExpDate(e.target.value)}
              style={{padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,
                fontFamily:"inherit",fontSize:14,color:C.navy,outline:"none"}}
              onFocus={e=>e.target.style.borderColor=C.amber}
              onBlur={e=>e.target.style.borderColor=C.border}/>
          </div>
          <Btn onClick={saveExpDate} disabled={busy}>Save Date</Btn>
        </div>
        {c.expDate&&(()=>{
          const exp=new Date(c.expDate+"T00:00:00");
          const days=Math.round((exp-new Date())/(1000*60*60*24));
          return (
            <div style={{marginTop:12,padding:"12px 14px",borderRadius:8,
              background:days<0?"#FEF2F2":days<60?`${C.amber}11`:"#ECFDF5",
              border:`1px solid ${days<0?"#FECACA":days<60?C.amber:"#A7F3D0"}`}}>
              <p style={{fontSize:22,fontWeight:800,color:days<0?C.red:days<60?C.amber2:C.green,marginBottom:2}}>
                {Math.abs(days)}<span style={{fontSize:13,fontWeight:600}}> days {days<0?"overdue":"remaining"}</span>
              </p>
              <p style={{fontSize:11,color:C.mid}}>
                {days<0?"Expired":"Expires"} {exp.toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
              </p>
            </div>
          );
        })()}
      </Card>

      {/* Notes */}
      <Card style={{padding:22}}>
        <p style={{fontSize:11,fontWeight:700,color:C.mid,textTransform:"uppercase",
          letterSpacing:"0.07em",marginBottom:8}}>Internal Notes</p>
        <textarea rows={3} value={notes} onChange={e=>setNotes(e.target.value)}
          placeholder="e.g. BLS certified, renewal due, training provider…"
          style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,
            fontFamily:B,fontSize:13,color:C.navy,resize:"vertical",outline:"none",
            marginBottom:10,lineHeight:1.5}}
          onFocus={e=>e.target.style.borderColor=C.amber}
          onBlur={e=>e.target.style.borderColor=C.border}/>
        <Btn size="sm" onClick={async()=>{
          await onUpdate({cprCheck:{...c,notes}});toast("Notes saved.");
        }}>Save Notes</Btn>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ARKANSAS NEW STAFF TRAINING TAB (due every 3 years)
// ═══════════════════════════════════════════════════════════════════════════════
function ArTrainingTab({ applicants, onUpdate, toast }) {
  const [selected, setSelected] = useState(null);

  function trainingStatus(app) {
    const t = app.arTraining||{};
    if (t.certified && t.certDate) {
      const exp = new Date(t.certDate+"T00:00:00");
      exp.setFullYear(exp.getFullYear()+3);
      const days=Math.round((exp-new Date())/(1000*60*60*24));
      if (days<0)  return {label:`❌ Expired ${Math.abs(days)}d ago`, color:C.red,    bg:"#FEF2F2",      border:"#FECACA"};
      if (days<90) return {label:`⚠️ ${days}d remaining`,             color:C.amber2, bg:`${C.amber}11`, border:`${C.amber}44`};
      return        {label:`✅ ${days}d remaining`,                    color:C.green,  bg:"#ECFDF5",      border:"#A7F3D0"};
    }
    if (t.certified) return {label:"✅ Certified — No Date", color:C.green, bg:"#ECFDF5", border:"#A7F3D0"};
    return            {label:"⬜ Not Certified",              color:C.mid,   bg:C.light,   border:C.border};
  }

  const sel = applicants.find(a=>a.id===selected);
  return (
    <div style={{display:"flex",height:"100%",minHeight:"100vh"}}>
      <StaffListPanel title="AR Staff Training" subtitle={`${applicants.length} active staff — renewal every 3 years`}
        people={applicants} selected={selected} onSelect={setSelected} getStatus={trainingStatus}/>
      <div style={{flex:1,overflowY:"auto",padding:28}}>
        {!sel&&(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:400,gap:12}}>
            <span style={{fontSize:48}}>📚</span>
            <p style={{fontFamily:H,color:C.navy,fontSize:20}}>Select an employee</p>
            <p style={{color:C.mid,fontSize:13}}>to manage their AR New Staff Training</p>
          </div>
        )}
        {sel&&<ArTrainingDetail app={sel} onUpdate={u=>onUpdate(sel.id,u)} toast={toast}/>}
      </div>
    </div>
  );
}

function ArTrainingDetail({ app, onUpdate, toast }) {
  const t = app.arTraining||{};
  const [certDate, setCertDate] = useState(t.certDate||"");
  const [notes,    setNotes]    = useState(t.notes||"");
  const [busy,     setBusy]     = useState(false);
  const [prog,     setProg]     = useState(null);
  const fileRef = useRef();

  async function uploadCert(file) {
    if (file.size>20*1024*1024){toast("File must be under 20 MB.","error");return;}
    setProg(0);
    try {
      const path=`ar-training/${app.id}/${Date.now()}_${file.name}`;
      const url=await uploadFile(path,file,p=>setProg(p));
      await onUpdate({arTraining:{...t,certUrl:url,certFileName:file.name,
        certified:true,certUploadedAt:new Date().toISOString()}});
      toast("✅ Certificate uploaded!");
    } catch(ex){toast("Upload failed: "+ex.message,"error");}
    setProg(null);
  }

  const expDate = certDate ? (()=>{
    const d=new Date(certDate+"T00:00:00"); d.setFullYear(d.getFullYear()+3); return d;
  })() : null;
  const daysLeft = expDate ? Math.round((expDate-new Date())/(1000*60*60*24)) : null;

  return (
    <div style={{maxWidth:620}}>
      <div style={{marginBottom:24}}>
        <h2 style={{fontFamily:H,color:C.navy,fontSize:26,marginBottom:4}}>{app.name}</h2>
        <p style={{color:C.mid,fontSize:12}}>{app.email}</p>
      </div>

      <div style={{padding:"12px 16px",background:"#EFF6FF",border:"1px solid #BFDBFE",
        borderRadius:9,marginBottom:20}}>
        <p style={{fontSize:12,color:"#1D4ED8",lineHeight:1.6}}>
          📚 Arkansas New Staff Training is required for all childcare staff and must be renewed every <strong>3 years</strong>.
          Enter the date they completed training and the system will automatically calculate the renewal due date.
        </p>
      </div>

      {/* Certificate upload */}
      <Card style={{padding:22,marginBottom:16}}>
        <p style={{fontWeight:700,color:C.navy,fontSize:15,marginBottom:6}}>📜 Training Certificate</p>
        <p style={{fontSize:12,color:C.mid,marginBottom:14,lineHeight:1.5}}>
          Upload the certificate issued upon completion of Arkansas New Staff Training.
        </p>
        {t.certUrl&&(
          <div style={{padding:"11px 14px",background:"#ECFDF5",border:"1px solid #A7F3D0",
            borderRadius:8,marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
            <div>
              <p style={{fontSize:11,fontWeight:700,color:C.green,marginBottom:3}}>✅ Certificate on File</p>
              <a href={t.certUrl} target="_blank" rel="noopener noreferrer"
                style={{fontSize:13,fontWeight:600,color:C.sky}}>
                📄 {t.certFileName||"View Certificate"} ↗
              </a>
              {t.certUploadedAt&&<p style={{fontSize:10,color:C.mid,marginTop:2}}>
                Uploaded {new Date(t.certUploadedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
              </p>}
            </div>
          </div>
        )}
        {prog!==null&&<div style={{marginBottom:10}}><ProgressBar pct={prog}/></div>}
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
          style={{display:"none"}} onChange={e=>{const f=e.target.files?.[0];if(f)uploadCert(f);}}/>
        <Btn onClick={()=>fileRef.current?.click()}
          bg={t.certUrl?C.navy2:C.green} color="#fff" size="sm">
          {t.certUrl?"🔄 Replace Certificate":"📎 Upload Certificate"}
        </Btn>
      </Card>

      {/* Certification date */}
      <Card style={{padding:22,marginBottom:16}}>
        <p style={{fontWeight:700,color:C.navy,fontSize:15,marginBottom:14}}>Training Completion Date</p>
        <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap",marginBottom:16}}>
          <div>
            <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",
              letterSpacing:"0.05em",marginBottom:6}}>Date Completed</p>
            <input type="date" value={certDate} onChange={e=>setCertDate(e.target.value)}
              style={{padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,
                fontFamily:"inherit",fontSize:14,color:C.navy,outline:"none"}}
              onFocus={e=>e.target.style.borderColor=C.amber}
              onBlur={e=>e.target.style.borderColor=C.border}/>
          </div>
          <Btn onClick={async()=>{
            if(!certDate){toast("Select a date first.","error");return;}
            setBusy(true);
            await onUpdate({arTraining:{...t,certified:true,certDate,updatedAt:new Date().toISOString()}});
            toast("✅ Training date saved.");
            setBusy(false);
          }} disabled={busy}>Save</Btn>
        </div>

        {/* Renewal calculation */}
        {expDate&&(
          <div style={{padding:"13px 16px",borderRadius:9,
            background:daysLeft<0?"#FEF2F2":daysLeft<90?`${C.amber}11`:"#ECFDF5",
            border:`1px solid ${daysLeft<0?"#FECACA":daysLeft<90?C.amber:"#A7F3D0"}`}}>
            <p style={{fontSize:11,fontWeight:700,color:C.mid,marginBottom:6}}>3-YEAR RENEWAL DEADLINE</p>
            <p style={{fontSize:18,fontWeight:700,
              color:daysLeft<0?C.red:daysLeft<90?C.amber2:C.green}}>
              {expDate.toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
            </p>
            <p style={{fontSize:12,color:C.mid,marginTop:4}}>
              {daysLeft<0?`⚠️ Overdue by ${Math.abs(daysLeft)} days — renewal required`
               :daysLeft===0?"Due today!"
               :`${daysLeft} days remaining`}
            </p>
          </div>
        )}
      </Card>

      {/* Certified toggle */}
      <Card style={{padding:22,marginBottom:16}}>
        <p style={{fontWeight:700,color:C.navy,fontSize:15,marginBottom:6}}>Certification Status</p>
        <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
          <input type="checkbox" checked={!!t.certified}
            onChange={async e=>{
              await onUpdate({arTraining:{...t,certified:e.target.checked,
                certifiedAt:e.target.checked?new Date().toISOString():null}});
              toast(e.target.checked?"✅ Marked certified.":"Marked not certified.");
            }}
            style={{width:16,height:16,accentColor:C.green,cursor:"pointer"}}/>
          <span style={{fontSize:13,color:C.navy}}>Arkansas New Staff Training completed & on file</span>
        </label>
      </Card>

      {/* Notes */}
      <Card style={{padding:22}}>
        <p style={{fontSize:11,fontWeight:700,color:C.mid,textTransform:"uppercase",
          letterSpacing:"0.07em",marginBottom:8}}>Internal Notes</p>
        <textarea rows={3} value={notes} onChange={e=>setNotes(e.target.value)}
          placeholder="e.g. Training provider, certificate number, follow-up needed…"
          style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,
            fontFamily:B,fontSize:13,color:C.navy,resize:"vertical",outline:"none",
            marginBottom:10,lineHeight:1.5}}
          onFocus={e=>e.target.style.borderColor=C.amber}
          onBlur={e=>e.target.style.borderColor=C.border}/>
        <Btn size="sm" onClick={async()=>{
          await onUpdate({arTraining:{...t,notes}});toast("Notes saved.");
        }}>Save Notes</Btn>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TERMINATED EMPLOYEES TAB
// ═══════════════════════════════════════════════════════════════════════════════
function TerminatedTab({ applicants, onUpdate, toast }) {
  const [selected, setSelected] = useState(null);
  const terminated = applicants.filter(a=>a.status==="terminated");
  const hired      = applicants.filter(a=>a.status==="hired");
  const [terminating, setTerminating] = useState(null); // appId being terminated
  const [termDate, setTermDate] = useState("");
  const [termReason, setTermReason] = useState("");
  const [busy, setBusy] = useState(false);

  const sel = terminated.find(a=>a.id===selected) ||
              (terminating ? applicants.find(a=>a.id===terminating) : null);

  return (
    <div style={{display:"flex",height:"100%",minHeight:"100vh"}}>

      {/* Left panel */}
      <div style={{width:300,borderRight:`1px solid ${C.border}`,flexShrink:0,overflowY:"auto",background:"#fff"}}>
        {/* Active staff — can be terminated */}
        {hired.length>0&&(
          <div>
            <div style={{padding:"14px 16px 8px",background:C.light,borderBottom:`1px solid ${C.border}`}}>
              <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",
                letterSpacing:"0.07em"}}>Active Staff — {hired.length}</p>
            </div>
            {hired.map(app=>(
              <div key={app.id}
                style={{padding:"11px 16px",cursor:"pointer",borderBottom:`1px solid ${C.border}`,
                  background:terminating===app.id?`${C.red}08`:"#fff",
                  borderLeft:terminating===app.id?`3px solid ${C.red}`:"3px solid transparent"}}
                onClick={()=>{setTerminating(app.id);setSelected(null);setTermDate("");setTermReason("");}}>
                <p style={{fontWeight:600,color:C.navy,fontSize:13}}>{app.name}</p>
                <p style={{fontSize:10,color:C.mid,marginTop:2}}>Active · hired</p>
              </div>
            ))}
          </div>
        )}

        {/* Terminated records */}
        <div>
          <div style={{padding:"14px 16px 8px",background:"#FEF2F2",borderBottom:`1px solid ${C.border}`,
            borderTop:`1px solid ${C.border}`}}>
            <p style={{fontSize:10,fontWeight:700,color:C.red,textTransform:"uppercase",
              letterSpacing:"0.07em"}}>Terminated — {terminated.length}</p>
          </div>
          {terminated.length===0&&(
            <p style={{fontSize:12,color:C.mid,padding:"16px",textAlign:"center",fontStyle:"italic"}}>No terminated employees on record.</p>
          )}
          {terminated.map(app=>(
            <div key={app.id}
              style={{padding:"11px 16px",cursor:"pointer",borderBottom:`1px solid ${C.border}`,
                background:selected===app.id?"#FEF2F2":"#fff",
                borderLeft:selected===app.id?`3px solid ${C.red}`:"3px solid transparent"}}
              onClick={()=>{setSelected(app.id);setTerminating(null);}}>
              <p style={{fontWeight:600,color:C.navy,fontSize:13}}>{app.name}</p>
              <p style={{fontSize:10,color:C.red,marginTop:2}}>
                Terminated {app.terminatedDate
                  ? new Date(app.terminatedDate+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})
                  : ""}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div style={{flex:1,overflowY:"auto",padding:28}}>

        {/* Terminate active employee form */}
        {terminating&&!selected&&(()=>{
          const app = applicants.find(a=>a.id===terminating);
          if(!app) return null;
          return (
            <div style={{maxWidth:620}}>
              <h2 style={{fontFamily:H,color:C.navy,fontSize:24,marginBottom:4}}>{app.name}</h2>
              <p style={{color:C.mid,fontSize:12,marginBottom:24}}>{app.email}</p>

              <Card style={{padding:24,borderLeft:`4px solid ${C.red}`}}>
                <p style={{fontWeight:700,color:C.red,fontSize:16,marginBottom:6}}>Terminate Employee</p>
                <p style={{fontSize:13,color:C.mid,lineHeight:1.6,marginBottom:20}}>
                  This will move {app.name.split(" ")[0]} to the terminated records. This action is reversible.
                </p>
                <div style={{display:"flex",flexDirection:"column",gap:16,marginBottom:20}}>
                  <div>
                    <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",
                      letterSpacing:"0.06em",marginBottom:6}}>Termination Date *</p>
                    <input type="date" value={termDate} onChange={e=>setTermDate(e.target.value)}
                      style={{padding:"10px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,
                        fontFamily:"inherit",fontSize:14,color:C.navy,outline:"none",width:"100%"}}
                      onFocus={e=>e.target.style.borderColor=C.red}
                      onBlur={e=>e.target.style.borderColor=C.border}/>
                  </div>
                  <div>
                    <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",
                      letterSpacing:"0.06em",marginBottom:6}}>Reason for Termination *</p>
                    <textarea rows={4} value={termReason} onChange={e=>setTermReason(e.target.value)}
                      placeholder="Voluntary resignation, end of season, performance, other…"
                      style={{width:"100%",padding:"10px 12px",borderRadius:8,
                        border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,
                        color:C.navy,resize:"vertical",outline:"none",lineHeight:1.5}}
                      onFocus={e=>e.target.style.borderColor=C.red}
                      onBlur={e=>e.target.style.borderColor=C.border}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <Btn bg={C.red} color="#fff" disabled={busy||!termDate||!termReason.trim()}
                    onClick={async()=>{
                      if(!termDate){toast("Enter a termination date.","error");return;}
                      if(!termReason.trim()){toast("Enter a reason.","error");return;}
                      setBusy(true);
                      await onUpdate(app.id,{
                        status:"terminated",
                        terminatedDate:termDate,
                        terminatedReason:termReason.trim(),
                        terminatedAt:new Date().toISOString(),
                      });
                      setTerminating(null);
                      setSelected(app.id);
                      toast(`${app.name} marked as terminated.`);
                      setBusy(false);
                    }}>
                    Confirm Termination
                  </Btn>
                  <Btn bg="#E5E7EB" color={C.mid} onClick={()=>setTerminating(null)}>Cancel</Btn>
                </div>
              </Card>
            </div>
          );
        })()}

        {/* Terminated employee record */}
        {selected&&!terminating&&(()=>{
          const app = terminated.find(a=>a.id===selected);
          if(!app) return null;
          return (
            <div style={{maxWidth:620}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",
                marginBottom:24,flexWrap:"wrap",gap:12}}>
                <div>
                  <h2 style={{fontFamily:H,color:C.navy,fontSize:26,marginBottom:4}}>{app.name}</h2>
                  <p style={{color:C.mid,fontSize:12}}>{app.email} · {app.phone}</p>
                </div>
                <div style={{padding:"10px 16px",background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:10}}>
                  <p style={{fontWeight:700,color:C.red,fontSize:13}}>Terminated</p>
                  {app.terminatedDate&&<p style={{fontSize:11,color:C.mid,marginTop:2}}>
                    {new Date(app.terminatedDate+"T00:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
                  </p>}
                </div>
              </div>

              <Card style={{padding:22,marginBottom:16,borderLeft:`4px solid ${C.red}`}}>
                <p style={{fontSize:11,fontWeight:700,color:C.mid,textTransform:"uppercase",
                  letterSpacing:"0.07em",marginBottom:12}}>Termination Record</p>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
                  <div style={{background:C.light,borderRadius:8,padding:"11px 14px"}}>
                    <p style={{fontSize:10,color:C.mid,marginBottom:4}}>Termination Date</p>
                    <p style={{fontSize:14,fontWeight:700,color:C.navy}}>
                      {app.terminatedDate
                        ? new Date(app.terminatedDate+"T00:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})
                        : "—"}
                    </p>
                  </div>
                  <div style={{background:C.light,borderRadius:8,padding:"11px 14px"}}>
                    <p style={{fontSize:10,color:C.mid,marginBottom:4}}>Recorded By</p>
                    <p style={{fontSize:13,fontWeight:600,color:C.navy}}>Admin</p>
                    {app.terminatedAt&&<p style={{fontSize:10,color:C.mid,marginTop:2}}>
                      {new Date(app.terminatedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                    </p>}
                  </div>
                </div>
                <div style={{background:C.light,borderRadius:8,padding:"11px 14px",marginBottom:16}}>
                  <p style={{fontSize:10,color:C.mid,marginBottom:4}}>Reason</p>
                  <p style={{fontSize:13,color:C.navy,lineHeight:1.6,whiteSpace:"pre-wrap"}}>
                    {app.terminatedReason||"—"}
                  </p>
                </div>
                <Btn size="sm" bg={C.green} color="#fff" disabled={busy}
                  onClick={async()=>{
                    setBusy(true);
                    await onUpdate(app.id,{status:"hired",terminatedDate:null,
                      terminatedReason:null,terminatedAt:null});
                    setSelected(null);
                    toast(`${app.name} reinstated as active.`);
                    setBusy(false);
                  }}>
                  ↩ Reinstate as Active Employee
                </Btn>
              </Card>
            </div>
          );
        })()}

        {/* Empty state */}
        {!selected&&!terminating&&(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",
            justifyContent:"center",minHeight:400,gap:12}}>
            <span style={{fontSize:48}}>📁</span>
            <p style={{fontFamily:H,color:C.navy,fontSize:20}}>Termination Records</p>
            <p style={{color:C.mid,fontSize:13,textAlign:"center",maxWidth:320,lineHeight:1.7}}>
              Select an active employee from the left to record a termination,
              or view a terminated employee's record.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// REPORTS TAB — filter & export staff data to Excel
// ═══════════════════════════════════════════════════════════════════════════════
function ReportsTab({ applicants, toast }) {
  // ── Excel filter state ──
  const [statusFilter,  setStatusFilter]  = useState(["hired"]);
  const [includeFields, setIncludeFields] = useState({
    contact:     true,
    payRate:     false,
    onboarding:  true,
    bgCheck:     true,
    maltreatment:true,
    cpr:         true,
    arTraining:  true,
    terminated:  false,
  });
  const [preview, setPreview] = useState(null);

  // ── PDF export state ──
  const [pdfMode,        setPdfMode]        = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedDocs,   setSelectedDocs]   = useState({});
  const [pdfBusy,        setPdfBusy]        = useState(false);
  const [pdfStatus,      setPdfStatus]      = useState("");

  // Build list of all available documents for a person
  function getPersonDocs(app) {
    const docs = [];
    const LEGACY_TYPES2 = ["bg_workflow"];
    const LEGACY_NAMES2 = ["background check","fingerprint","maltreatment","cpr","first aid"];
    const isLeg2 = d => LEGACY_TYPES2.includes(d.docType)||LEGACY_NAMES2.some(n=>(d.name||"").toLowerCase().includes(n));

    // Onboarding docs
    (app.onboardingDocs||[]).filter(d=>!isLeg2(d)&&d.uploadedUrl).forEach(d=>{
      docs.push({key:`onboard_${d.id}`, label:d.name, url:d.uploadedUrl, category:"Onboarding"});
    });

    // Admin result files on onboarding docs
    (app.onboardingDocs||[]).forEach(d=>{
      (d.adminFiles||[]).forEach((f,i)=>{
        docs.push({key:`admin_${d.id}_${i}`, label:`${d.name} — Admin Result: ${f.name}`, url:f.url, category:"Admin Results"});
      });
    });

    // Background check clearance
    if (app.bgCheck?.bgClearanceUrl)
      docs.push({key:"bg_clearance", label:"Background Check Clearance Letter", url:app.bgCheck.bgClearanceUrl, category:"Background Check"});

    // Maltreatment clearance
    if (app.mtCheck?.clearanceUrl)
      docs.push({key:"mt_clearance", label:"Maltreatment Registry Clearance", url:app.mtCheck.clearanceUrl, category:"Maltreatment"});

    // CPR card
    if (app.cprCheck?.cardUrl)
      docs.push({key:"cpr_card", label:"CPR Card", url:app.cprCheck.cardUrl, category:"CPR"});

    // AR Training certificate
    if (app.arTraining?.certUrl)
      docs.push({key:"ar_cert", label:"AR Staff Training Certificate", url:app.arTraining.certUrl, category:"AR Training"});

    return docs;
  }

  function selectPerson(app) {
    setSelectedPerson(app);
    // Default: select all available docs
    const docs = getPersonDocs(app);
    const sel = {};
    docs.forEach(d => { sel[d.key] = true; });
    setSelectedDocs(sel);
    setPdfStatus("");
  }

  async function exportPDF() {
    if (!selectedPerson) return;
    const allDocs = getPersonDocs(selectedPerson);
    const toExport = allDocs.filter(d => selectedDocs[d.key]);
    if (toExport.length === 0) { toast("Select at least one document.", "error"); return; }

    setPdfBusy(true);
    setPdfStatus("Creating PDF...");

    try {
      const { PDFDocument: PDFDoc, rgb: prgb, StandardFonts: SF } = await import("pdf-lib");
      const mergedPdf = await PDFDoc.create();
      const font = await mergedPdf.embedFont(SF.Helvetica);
      const fontBold = await mergedPdf.embedFont(SF.HelveticaBold);

      // Cover page
      const coverPage = mergedPdf.addPage([612, 792]);
      coverPage.drawRectangle({x:0, y:0, width:612, height:792, color:prgb(0.059, 0.125, 0.153)});
      coverPage.drawRectangle({x:0, y:700, width:612, height:4, color:prgb(0.961, 0.620, 0.043)});
      coverPage.drawText("Summer Day Camp", {x:56, y:720, size:28, font:fontBold, color:prgb(0.98,0.95,0.88)});
      coverPage.drawText("Staff Document Package", {x:56, y:690, size:16, font, color:prgb(0.961,0.620,0.043)});
      coverPage.drawText(selectedPerson.name||"", {x:56, y:630, size:22, font:fontBold, color:prgb(1,1,1)});
      if (selectedPerson.email) coverPage.drawText(selectedPerson.email, {x:56, y:605, size:12, font, color:prgb(0.6,0.65,0.7)});
      if (selectedPerson.phone) coverPage.drawText(selectedPerson.phone, {x:56, y:588, size:12, font, color:prgb(0.6,0.65,0.7)});
      coverPage.drawText(`Generated: ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}`, {x:56, y:560, size:11, font, color:prgb(0.5,0.55,0.6)});
      coverPage.drawText(`Documents included (${toExport.length}):`, {x:56, y:520, size:13, font:fontBold, color:prgb(0.8,0.85,0.9)});
      toExport.forEach((d,i) => {
        coverPage.drawText(`${i+1}. ${d.label}`, {x:72, y:498-(i*18), size:11, font, color:prgb(0.75,0.80,0.85)});
      });

      // Merge each document
      for (let i=0; i<toExport.length; i++) {
        const d = toExport[i];
        setPdfStatus(`Fetching ${i+1} of ${toExport.length}: ${d.label}...`);
        try {
          const resp = await fetch(d.url);
          const buf  = await resp.arrayBuffer();
          const ct   = resp.headers.get("content-type")||"";
          const name = d.url.split("?")[0].split("/").pop().toLowerCase();

          // Section divider page
          const divPage = mergedPdf.addPage([612, 792]);
          divPage.drawRectangle({x:0,y:0,width:612,height:792,color:prgb(0.97,0.97,0.98)});
          divPage.drawRectangle({x:0,y:380,width:612,height:3,color:prgb(0.961,0.620,0.043)});
          divPage.drawText(`${i+1} of ${toExport.length}`, {x:56, y:440, size:12, font, color:prgb(0.6,0.6,0.6)});
          divPage.drawText(d.label, {x:56, y:410, size:20, font:fontBold, color:prgb(0.059,0.125,0.153)});
          divPage.drawText(d.category, {x:56, y:360, size:13, font, color:prgb(0.4,0.4,0.5)});

          if (ct.includes("pdf") || name.endsWith(".pdf")) {
            try {
              const srcPdf = await PDFDoc.load(buf);
              const pages  = await mergedPdf.copyPages(srcPdf, srcPdf.getPageIndices());
              pages.forEach(p => mergedPdf.addPage(p));
            } catch(e) {
              // PDF couldn't be merged (encrypted etc) — add note page
              const notePage = mergedPdf.addPage([612, 792]);
              notePage.drawText("Document could not be embedded (may be encrypted).", {x:56, y:400, size:13, font, color:prgb(0.7,0.3,0.3)});
              notePage.drawText("Please view the original file separately.", {x:56, y:378, size:12, font, color:prgb(0.5,0.5,0.5)});
            }
          } else if (ct.includes("image")||name.match(/\.(jpg|jpeg|png)$/)) {
            let img;
            try {
              if (name.endsWith(".png")) img = await mergedPdf.embedPng(buf);
              else img = await mergedPdf.embedJpg(buf);
              const imgPage = mergedPdf.addPage([612, 792]);
              const scaled = img.scaleToFit(500, 680);
              imgPage.drawImage(img, {x:(612-scaled.width)/2, y:(792-scaled.height)/2, width:scaled.width, height:scaled.height});
            } catch(e) {
              const errPage = mergedPdf.addPage([612,792]);
              errPage.drawText("Image could not be embedded.", {x:56, y:400, size:13, font, color:prgb(0.7,0.3,0.3)});
            }
          } else {
            const errPage = mergedPdf.addPage([612,792]);
            errPage.drawText("File type not supported for PDF embedding.", {x:56, y:400, size:13, font, color:prgb(0.5,0.5,0.5)});
            errPage.drawText(`(${name})`, {x:56, y:378, size:11, font, color:prgb(0.6,0.6,0.6)});
          }
        } catch(fetchErr) {
          const errPage = mergedPdf.addPage([612,792]);
          errPage.drawText(`Could not load: ${d.label}`, {x:56, y:400, size:13, font, color:prgb(0.7,0.3,0.3)});
        }
      }

      setPdfStatus("Saving PDF...");
      const pdfBytes = await mergedPdf.save();
      const blob = new Blob([pdfBytes], {type:"application/pdf"});
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      const safeName = (selectedPerson.name||"Staff").replace(/\s+/g,"_");
      a.href = url;
      a.download = `${safeName}_Documents_${new Date().toLocaleDateString("en-US",{month:"2-digit",day:"2-digit",year:"numeric"}).replace(/\//g,"-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setPdfStatus("");
      toast(`✅ PDF exported with ${toExport.length} document${toExport.length>1?"s":""}!`);
    } catch(err) {
      toast("PDF export failed: "+err.message, "error");
      setPdfStatus("");
    }
    setPdfBusy(false);
  }

  const ALL_STATUSES = [
    {val:"hired",       label:"✅ Active / Hired"},
    {val:"terminated",  label:"📁 Terminated"},
    {val:"applied",     label:"📋 Applied"},
    {val:"interviewed", label:"🎤 Interviewed"},
    {val:"offer_sent",  label:"💼 Offer Sent"},
    {val:"offer_accepted",label:"✅ Offer Accepted"},
  ];

  const LEGACY_TYPES = ["bg_workflow"];
  const LEGACY_NAMES = ["background check","fingerprint","maltreatment","cpr","first aid"];
  const isLegacy = d => LEGACY_TYPES.includes(d.docType) || LEGACY_NAMES.some(n=>(d.name||"").toLowerCase().includes(n));

  function toggleStatus(val) {
    setStatusFilter(p => p.includes(val) ? p.filter(s=>s!==val) : [...p,val]);
  }
  function toggleField(key) {
    setIncludeFields(p => ({...p, [key]:!p[key]}));
  }

  // Helper: days remaining text
  function daysText(dateStr) {
    if (!dateStr) return "";
    const days = Math.round((new Date(dateStr+"T00:00:00") - new Date()) / (1000*60*60*24));
    if (days < 0) return `EXPIRED ${Math.abs(days)}d ago`;
    return `${days}d remaining`;
  }

  // Helper: onboarding doc status
  function docStatus(docs, docName) {
    const LEGACY_TYPES2 = ["bg_workflow"];
    const LEGACY_NAMES2 = ["background check","fingerprint","maltreatment","cpr","first aid"];
    const found = (docs||[]).find(d =>
      d.name?.toLowerCase().includes(docName.toLowerCase()) &&
      !LEGACY_TYPES2.includes(d.docType) &&
      !LEGACY_NAMES2.some(n=>(d.name||"").toLowerCase().includes(n))
    );
    if (!found) return "Not in checklist";
    return found.received ? `Received ${found.receivedDate||""}` : "Pending";
  }

  function buildRows() {
    const filtered = applicants.filter(a => statusFilter.includes(a.status));
    return filtered.map(app => {
      const row = {};

      // Always include basics
      row["Name"]   = app.name || "";
      row["Email"]  = app.email || "";
      row["Status"] = app.status || "";

      if (includeFields.contact) {
        row["Phone"]   = app.phone || "";
        row["Address"] = app.address || "";
      }

      if (includeFields.payRate) {
        row["Pay Rate"]      = app.offer?.payRate || "";
        row["Hours/Week"]    = app.offer?.hoursPerWeek || "";
        row["Season"]        = app.offer?.season || "";
        row["Start Date"]    = app.offer?.startDate || app.startDate || "";
      }

      if (includeFields.onboarding) {
        const docs = (app.onboardingDocs||[]).filter(d=>!isLegacy(d));
        const total = docs.length;
        const received = docs.filter(d=>d.received).length;
        row["Onboarding Complete"] = total > 0 ? (received===total?"Yes":"No") : "N/A";
        row["Onboarding Progress"] = total > 0 ? `${received}/${total}` : "N/A";
        // Individual doc status
        docs.forEach(d => {
          row[`Doc: ${d.name}`] = d.received ? `Received${d.receivedDate?" "+d.receivedDate:""}` : "Pending";
        });
      }

      if (includeFields.bgCheck) {
        const bg = app.bgCheck || {};
        const approval = bg.approvalStatus || (bg.fullyCleared?"approved":"pending");
        row["BG Check Status"]    = approval.charAt(0).toUpperCase()+approval.slice(1);
        row["BG Approval Date"]   = bg.approvalDate || "";
        row["BG Expiry Date"]     = bg.expiryDate || "";
        row["BG Days Remaining"]  = bg.expiryDate ? daysText(bg.expiryDate) : "";
        row["BG Transaction ID"]  = bg.transactionId || "";
      }

      if (includeFields.maltreatment) {
        const mt = app.mtCheck || {};
        row["Maltreatment Status"]  = mt.cleared ? "Approved" : mt.declined ? "Declined" : mt.currentStep==="s3"?"Awaiting Decision":mt.currentStep==="s2"?"Submitted to State":"Pending";
        row["Maltreatment Approval Date"] = mt.clearedAt ? new Date(mt.clearedAt).toLocaleDateString() : "";
        row["Maltreatment Expiry Date"]   = mt.expiryDate || "";
        row["Maltreatment Days Remaining"]= mt.expiryDate ? daysText(mt.expiryDate) : "";
      }

      if (includeFields.cpr) {
        const c = app.cprCheck || {};
        row["CPR Card on File"]    = c.cardOnFile ? "Yes" : "No";
        row["CPR Expiry Date"]     = c.expDate || "";
        row["CPR Days Remaining"]  = c.expDate ? daysText(c.expDate) : "";
      }

      if (includeFields.arTraining) {
        const t = app.arTraining || {};
        const expDate = t.certDate ? (() => {
          const d = new Date(t.certDate+"T00:00:00");
          d.setFullYear(d.getFullYear()+3);
          return d.toISOString().split("T")[0];
        })() : null;
        row["AR Training Certified"]  = t.certified ? "Yes" : "No";
        row["AR Training Date"]       = t.certDate || "";
        row["AR Training Renewal Due"]= expDate || "";
        row["AR Training Days Left"]  = expDate ? daysText(expDate) : "";
      }

      if (includeFields.terminated && app.status==="terminated") {
        row["Termination Date"]   = app.terminatedDate || "";
        row["Termination Reason"] = app.terminatedReason || "";
      }

      return row;
    });
  }

  function exportExcel() {
    const rows = buildRows();
    if (rows.length === 0) { toast("No employees match the selected filters.", "error"); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    // Auto-size columns
    const colWidths = Object.keys(rows[0]||{}).map(key => ({
      wch: Math.max(key.length, ...rows.map(r=>(r[key]||"").toString().length)) + 2
    }));
    ws["!cols"] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Staff Report");
    const date = new Date().toLocaleDateString("en-US",{month:"2-digit",day:"2-digit",year:"numeric"}).replace(/\//g,"-");
    XLSX.writeFile(wb, `SummerDayCamp_Staff_Report_${date}.xlsx`);
    toast(`✅ Exported ${rows.length} employees to Excel!`);
  }

  const previewRows = buildRows().slice(0,5);
  const previewCols = previewRows.length > 0 ? Object.keys(previewRows[0]) : [];

  return (
    <div style={{padding:32,maxWidth:960}}>
      <div style={{marginBottom:28}}>
        <h2 style={{fontFamily:H,color:C.navy,fontSize:30,marginBottom:4}}>Staff Reports</h2>
        <p style={{color:C.mid,fontSize:13}}>Export employee data as Excel or compile documents into a single PDF.</p>
      </div>

      {/* ── Mode tabs ── */}
      <div style={{display:"flex",gap:0,marginBottom:28,borderRadius:10,overflow:"hidden",
        border:`1.5px solid ${C.border}`,width:"fit-content"}}>
        {[
          {val:false, icon:"📊", label:"Excel Report"},
          {val:true,  icon:"📄", label:"Document PDF"},
        ].map(m=>(
          <button key={String(m.val)} onClick={()=>{setPdfMode(m.val);setSelectedPerson(null);}}
            style={{padding:"11px 24px",border:"none",cursor:"pointer",fontFamily:B,fontSize:13,
              fontWeight:pdfMode===m.val?700:400,
              background:pdfMode===m.val?C.navy:"#fff",
              color:pdfMode===m.val?"#fff":C.mid}}>
            {m.icon} {m.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════
          PDF DOCUMENT EXPORT MODE
      ══════════════════════════════════════════════════════ */}
      {pdfMode&&(
        <div>
          <div style={{display:"flex",gap:20,minHeight:500}}>

            {/* Left: person picker */}
            <div style={{width:260,flexShrink:0}}>
              <p style={{fontSize:11,fontWeight:700,color:C.mid,textTransform:"uppercase",
                letterSpacing:"0.07em",marginBottom:10}}>Select Employee</p>
              <div style={{background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,
                overflow:"hidden",maxHeight:600,overflowY:"auto"}}>
                {[...applicants].sort((a,b)=>a.name?.localeCompare(b.name)).map(app=>{
                  const docCount = getPersonDocs(app).length;
                  const isSel = selectedPerson?.id===app.id;
                  return (
                    <div key={app.id} onClick={()=>selectPerson(app)}
                      style={{padding:"11px 14px",cursor:"pointer",
                        borderBottom:`1px solid ${C.border}`,
                        background:isSel?`${C.amber}12`:"#fff",
                        borderLeft:isSel?`3px solid ${C.amber}`:"3px solid transparent"}}>
                      <p style={{fontWeight:600,color:C.navy,fontSize:13}}>{app.name}</p>
                      <p style={{fontSize:10,color:C.mid,marginTop:2}}>
                        {docCount} document{docCount!==1?"s":""} available
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: document picker + export */}
            <div style={{flex:1}}>
              {!selectedPerson&&(
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",
                  justifyContent:"center",height:400,background:"#fff",
                  borderRadius:10,border:`1px solid ${C.border}`,gap:12}}>
                  <span style={{fontSize:48}}>📄</span>
                  <p style={{fontFamily:H,color:C.navy,fontSize:18}}>Select an employee</p>
                  <p style={{color:C.mid,fontSize:13}}>to choose which documents to export</p>
                </div>
              )}

              {selectedPerson&&(()=>{
                const allDocs = getPersonDocs(selectedPerson);
                const categories = [...new Set(allDocs.map(d=>d.category))];
                const selectedCount = allDocs.filter(d=>selectedDocs[d.key]).length;

                return (
                  <div>
                    {/* Header */}
                    <div style={{display:"flex",justifyContent:"space-between",
                      alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:10}}>
                      <div>
                        <p style={{fontFamily:H,color:C.navy,fontSize:20,marginBottom:2}}>
                          {selectedPerson.name}
                        </p>
                        <p style={{fontSize:12,color:C.mid}}>{selectedPerson.email}</p>
                      </div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        <button onClick={()=>{
                          const sel={};
                          allDocs.forEach(d=>{sel[d.key]=true;});
                          setSelectedDocs(sel);
                        }} style={{fontSize:11,color:C.sky,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>
                          Select All
                        </button>
                        <button onClick={()=>setSelectedDocs({})}
                          style={{fontSize:11,color:C.mid,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>
                          Clear
                        </button>
                      </div>
                    </div>

                    {allDocs.length===0&&(
                      <div style={{padding:24,background:"#fff",borderRadius:10,
                        border:`1px solid ${C.border}`,textAlign:"center"}}>
                        <p style={{color:C.mid,fontSize:13}}>No uploaded documents found for this employee.</p>
                        <p style={{color:C.mid,fontSize:11,marginTop:6,lineHeight:1.6}}>
                          Documents appear here once files have been uploaded in the onboarding,
                          background check, maltreatment, CPR, or training tabs.
                        </p>
                      </div>
                    )}

                    {categories.map(cat=>{
                      const catDocs = allDocs.filter(d=>d.category===cat);
                      return (
                        <div key={cat} style={{marginBottom:14}}>
                          <p style={{fontSize:10,fontWeight:700,color:C.mid,textTransform:"uppercase",
                            letterSpacing:"0.07em",marginBottom:8}}>{cat}</p>
                          <div style={{background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,overflow:"hidden"}}>
                            {catDocs.map(d=>(
                              <label key={d.key}
                                style={{display:"flex",alignItems:"center",gap:12,
                                  padding:"11px 16px",borderBottom:`1px solid ${C.border}`,
                                  cursor:"pointer",
                                  background:selectedDocs[d.key]?`${C.amber}08`:"#fff"}}>
                                <input type="checkbox" checked={!!selectedDocs[d.key]}
                                  onChange={e=>setSelectedDocs(p=>({...p,[d.key]:e.target.checked}))}
                                  style={{width:15,height:15,accentColor:C.amber,cursor:"pointer",flexShrink:0}}/>
                                <div style={{flex:1,minWidth:0}}>
                                  <p style={{fontSize:13,color:C.navy,fontWeight:500,
                                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                    {d.label}
                                  </p>
                                </div>
                                <a href={d.url} target="_blank" rel="noopener noreferrer"
                                  onClick={e=>e.stopPropagation()}
                                  style={{fontSize:11,color:C.sky,flexShrink:0,textDecoration:"none"}}>
                                  Preview ↗
                                </a>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {allDocs.length>0&&(
                      <div style={{marginTop:8}}>
                        {pdfStatus&&(
                          <div style={{padding:"10px 14px",background:`${C.sky}11`,
                            border:`1px solid ${C.sky}44`,borderRadius:8,marginBottom:12}}>
                            <p style={{fontSize:12,color:"#1D4ED8"}}>{pdfStatus}</p>
                          </div>
                        )}
                        <Btn bg={C.navy} color="#fff" disabled={pdfBusy||selectedCount===0}
                          onClick={exportPDF}
                          style={{width:"100%",padding:"13px",fontSize:14}}>
                          {pdfBusy?"Building PDF…":`📄 Export ${selectedCount} Document${selectedCount!==1?"s":""} as PDF`}
                        </Btn>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          EXCEL REPORT MODE
      ══════════════════════════════════════════════════════ */}
      {!pdfMode&&(
      <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",
        marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div/>
        <Btn bg={C.green} color="#fff" onClick={exportExcel} style={{padding:"12px 24px",fontSize:15}}>
          ⬇️ Export to Excel
        </Btn>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24}}>

        {/* Status filter */}
        <Card style={{padding:20}}>
          <p style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:14}}>Include Staff By Status</p>
          {ALL_STATUSES.map(s=>(
            <label key={s.val} style={{display:"flex",alignItems:"center",gap:10,
              marginBottom:10,cursor:"pointer"}}>
              <input type="checkbox" checked={statusFilter.includes(s.val)}
                onChange={()=>toggleStatus(s.val)}
                style={{width:15,height:15,accentColor:C.amber,cursor:"pointer"}}/>
              <span style={{fontSize:13,color:C.navy}}>{s.label}</span>
            </label>
          ))}
          <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
            <p style={{fontSize:11,fontWeight:600,color:C.mid}}>
              {applicants.filter(a=>statusFilter.includes(a.status)).length} employees match
            </p>
          </div>
        </Card>

        {/* Column filter */}
        <Card style={{padding:20}}>
          <p style={{fontWeight:700,color:C.navy,fontSize:14,marginBottom:14}}>Include Columns</p>
          {[
            {key:"contact",      label:"📞 Contact Info (phone, address)"},
            {key:"payRate",      label:"💰 Pay Rate & Hours"},
            {key:"onboarding",   label:"📋 Onboarding Documents"},
            {key:"bgCheck",      label:"🔍 Background Check"},
            {key:"maltreatment", label:"🛡️ Maltreatment Registry"},
            {key:"cpr",          label:"❤️ CPR Card"},
            {key:"arTraining",   label:"📚 AR Staff Training"},
            {key:"terminated",   label:"📁 Termination Details"},
          ].map(f=>(
            <label key={f.key} style={{display:"flex",alignItems:"center",gap:10,
              marginBottom:10,cursor:"pointer"}}>
              <input type="checkbox" checked={!!includeFields[f.key]}
                onChange={()=>toggleField(f.key)}
                style={{width:15,height:15,accentColor:C.amber,cursor:"pointer"}}/>
              <span style={{fontSize:13,color:C.navy}}>{f.label}</span>
            </label>
          ))}
        </Card>
      </div>

      {/* Preview */}
      {previewRows.length > 0 && (
        <Card style={{padding:20,marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <p style={{fontWeight:700,color:C.navy,fontSize:14}}>
              Preview — first {Math.min(5,previewRows.length)} of {buildRows().length} rows
            </p>
            <p style={{fontSize:11,color:C.mid}}>{previewCols.length} columns</p>
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
              <thead>
                <tr>
                  {previewCols.map(col=>(
                    <th key={col} style={{padding:"6px 10px",textAlign:"left",
                      background:C.light,border:`1px solid ${C.border}`,
                      color:C.mid,fontWeight:700,whiteSpace:"nowrap",
                      textTransform:"uppercase",fontSize:10,letterSpacing:"0.04em"}}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row,i)=>(
                  <tr key={i}>
                    {previewCols.map(col=>{
                      const val = (row[col]||"").toString();
                      const isGood = val.toLowerCase().includes("yes")||val.toLowerCase().includes("received")||val.toLowerCase().includes("approved");
                      const isBad  = val.toLowerCase().includes("pending")||val.toLowerCase().includes("expired")||val.toLowerCase().includes("no");
                      return (
                        <td key={col} style={{padding:"6px 10px",border:`1px solid ${C.border}`,
                          whiteSpace:"nowrap",
                          color:isGood?C.green:isBad?C.red:C.navy}}>
                          {val||"—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {previewRows.length === 0 && (
        <div style={{padding:32,textAlign:"center",background:"#fff",borderRadius:12,border:`1px solid ${C.border}`}}>
          <p style={{fontSize:14,color:C.mid}}>No employees match the selected status filters.</p>
        </div>
      )}

      <div style={{display:"flex",justifyContent:"center",marginTop:8}}>
        <Btn bg={C.green} color="#fff" onClick={exportExcel} style={{padding:"13px 40px",fontSize:15}}>
          ⬇️ Export {buildRows().length} Employees to Excel
        </Btn>
      </div>
      </div>
      )}
    </div>
  );
}

function OnboardingTab({applicants,masterDocs,onUpdate,toast}){
  const [selId,setSelId]=useState(applicants[0]?.id||null);
  const [busy,setBusy]=useState(false);
  const app=applicants.find(a=>a.id===selId);

  async function updateDocField(docId,updates){
    const docs=(app.onboardingDocs||[]).map(d=>d.id===docId?{...d,...updates}:d);
    await onUpdate(app.id,{onboardingDocs:docs});
  }

  async function sendReminder(){
    if(!app)return; setBusy(true);
    const pending=(app.onboardingDocs||[]).filter(d=>!d.received&&!isLeg2(d)).map(d=>`  • ${d.name}`).join("\n");
    const count=(app.docReminderCount||0)+1;
    await sendEmail(app.email,`Onboarding Documents Reminder #${count} — Summer Day Camp`,
      `Hi ${app.name},\n\nWe're waiting on these documents:\n\n${pending}\n\nPlease log in to submit them:\nhttps://sdc-employment.web.app\n\nSummer Day Camp Team`);
    await onUpdate(app.id,{docReminderCount:count,lastReminderAt:new Date().toISOString()});
    toast(`📧 Reminder #${count} sent to ${app.name}!`);
    setBusy(false);
  }

  if(applicants.length===0) return(
    <div style={{padding:32}}><h2 style={{fontFamily:H,color:C.navy,fontSize:30,marginBottom:18}}>Onboarding</h2>
      <Card style={{padding:36,textAlign:"center"}}><p style={{color:C.mid}}>No hired staff yet.</p></Card>
    </div>
  );

  const LTYPES2=["bg_workflow"],LNAMES2=["background check","fingerprint","maltreatment","cpr","first aid"];
  const isLeg2=d=>LTYPES2.includes(d.docType)||LNAMES2.some(n=>(d.name||"").toLowerCase().includes(n));
  const received=(app?.onboardingDocs||[]).filter(d=>d.received&&!isLeg2(d)).length;
  const total=(app?.onboardingDocs||[]).filter(d=>!isLeg2(d)).length;
  const pct=total?Math.round(received/total*100):0;

  return(
    <div style={{padding:32}}>
      <h2 style={{fontFamily:H,color:C.navy,fontSize:30,marginBottom:20}}>Onboarding</h2>
      <div style={{display:"flex",gap:9,flexWrap:"wrap",marginBottom:22}}>
        {applicants.map(a=>{
          const LTYPES3=["bg_workflow"],LNAMES3=["background check","fingerprint","maltreatment","cpr","first aid"];
          const hasPending=(a.onboardingDocs||[]).some(d=>!d.received&&!LTYPES3.includes(d.docType)&&!LNAMES3.some(n=>(d.name||"").toLowerCase().includes(n)));
          return(
            <button key={a.id} onClick={()=>setSelId(a.id)} style={{
              padding:"8px 14px",borderRadius:8,border:`2px solid ${selId===a.id?C.amber:C.border}`,
              background:selId===a.id?`${C.amber}15`:"#fff",color:selId===a.id?C.amber2:C.navy,
              fontFamily:B,fontWeight:selId===a.id?600:400,fontSize:12,display:"flex",alignItems:"center",gap:6}}>
              {a.name}{hasPending&&<span style={{width:7,height:7,borderRadius:"50%",background:C.red}}/>}
            </button>
          );
        })}
      </div>
      {app&&(
        <Card>
          <div style={{padding:"20px 24px",borderBottom:`1px solid ${C.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
              <div>
                <h3 style={{fontFamily:H,color:C.navy,fontSize:19}}>{app.name}</h3>
                <p style={{color:C.mid,fontSize:11}}>{app.email}</p>
                <p style={{color:C.green,fontSize:11,fontWeight:600,marginTop:2}}>{received}/{total} received ({pct}%)</p>
                {app.docReminderCount>0&&<p style={{fontSize:10,color:C.mid}}>Reminders sent: {app.docReminderCount}</p>}
              </div>
              <Btn onClick={sendReminder} bg={C.amber} disabled={busy||received===total}>
                {busy?"Sending…":"📧 Send 72hr Reminder"}
              </Btn>
            </div>
            <div style={{marginTop:12}}><ProgressBar pct={pct}/></div>
          </div>
          <div style={{padding:"16px 24px"}}>
            {masterDocs.map(def=>{
              const state=(app.onboardingDocs||[]).find(d=>d.id===def.id)||{};
              return <AdminDocRow key={def.id} docDef={def} docState={{...state,applicantName:app.name,applicantEmail:app.email}} appId={app.id}
                onUpdated={(id,u)=>updateDocField(id,u)} toast={toast}/>;
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SETTINGS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function SettingsTab({settings,onSave,toast}){
  const [subTab,setSubTab]=useState("docs");
  const [docs,setDocs]=useState(settings.docs||FACTORY_DOCS);
  const [questions,setQuestions]=useState(settings.questions||FACTORY_QUESTIONS);
  const [appQuestions,setAppQuestions]=useState(settings.appQuestions||FACTORY_QUESTIONS);
  const [newDocName,setNewDocName]=useState("");
  const [newDocType,setNewDocType]=useState("upload");
  const [newQ,setNewQ]=useState("");
  const [editingDoc,setEditingDoc]=useState(null); // index being edited
  const [editingQ,setEditingQ]=useState(null);
  const [editQVal,setEditQVal]=useState("");
  const [templateUploads,setTemplateUploads]=useState({});
  const [expandedDoc,setExpandedDoc]=useState(null);
  const fileRefs=useRef({});
  const fieldTypes=[
    {value:"text",     label:"Short Text"},
    {value:"textarea", label:"Paragraph"},
    {value:"email",    label:"Email"},
    {value:"tel",      label:"Phone"},
    {value:"date",     label:"Date"},
    {value:"checkbox", label:"Checkbox (yes/no)"},
    {value:"info",     label:"ℹ️ Info / Instructions"},
    {value:"link",     label:"🔗 Link (clickable URL)"},
  ];

  async function saveDocs(d){ setDocs(d); await onSave({...settings,docs:d}); }
  async function saveQs(q){ setQuestions(q); await onSave({...settings,questions:q}); }
  async function saveAppQs(q){ setAppQuestions(q); await onSave({...settings,appQuestions:q}); }

  // IDs and types that are now handled in their own tabs — should not appear in onboarding
  const EXCLUDED_DOC_TYPES = ["bg_workflow"];
  const EXCLUDED_DOC_NAMES = ["background check","fingerprint","maltreatment","cpr","first aid"];
  const hasLegacyDocs = docs.some(d=>
    EXCLUDED_DOC_TYPES.includes(d.docType) ||
    EXCLUDED_DOC_NAMES.some(n=>d.name?.toLowerCase().includes(n))
  );

  async function removeLegacyDocs(){
    const cleaned = docs.filter(d=>
      !EXCLUDED_DOC_TYPES.includes(d.docType) &&
      !EXCLUDED_DOC_NAMES.some(n=>d.name?.toLowerCase().includes(n))
    );
    await saveDocs(cleaned);
    toast("✅ Legacy docs removed from checklist.");
  }

  async function addDoc(){
    if(!newDocName.trim())return;
    const d=[...docs,{id:`d${Date.now()}`,name:newDocName.trim(),docType:newDocType,templateUrl:null,fields:[],requireSignature:newDocType!=="upload"}];
    await saveDocs(d); setNewDocName(""); toast("Document added.");
  }
  async function removeDoc(i){ await saveDocs(docs.filter((_,j)=>j!==i)); }

  async function uploadTemplate(i,file){
    if(!file)return;
    if(file.size>20*1024*1024){toast("File must be under 20 MB.","error");return;}
    setTemplateUploads(p=>({...p,[i]:0}));
    try{
      const path=`templates/${docs[i].id}_${Date.now()}_${file.name}`;
      const url=await uploadFile(path,file,p=>setTemplateUploads(prev=>({...prev,[i]:p})));
      const d=[...docs]; d[i]={...d[i],templateUrl:url};
      await saveDocs(d); toast("✅ Template uploaded for: "+docs[i].name);
    }catch(e){ toast("Upload failed: "+e.message,"error"); }
    setTemplateUploads(p=>{const n={...p};delete n[i];return n;});
    if(fileRefs.current[i]) fileRefs.current[i].value="";
  }

  // Form field management
  function addField(docIdx){
    const d=[...docs];
    const fields=d[docIdx].fields||[];
    d[docIdx]={...d[docIdx],fields:[...fields,{id:`f${Date.now()}`,label:"New Field",type:"text"}]};
    saveDocs(d);
  }
  function updateField(docIdx,fieldIdx,key,val){
    const d=[...docs];
    const fields=[...d[docIdx].fields];
    fields[fieldIdx]={...fields[fieldIdx],[key]:val};
    d[docIdx]={...d[docIdx],fields};
    saveDocs(d);
  }
  function removeField(docIdx,fieldIdx){
    const d=[...docs];
    d[docIdx]={...d[docIdx],fields:d[docIdx].fields.filter((_,j)=>j!==fieldIdx)};
    saveDocs(d);
  }

  function addQ(){ if(!newQ.trim())return; saveQs([...questions,newQ.trim()]); setNewQ(""); toast("Question added."); }
  function saveQEdit(i){ const q=[...questions];q[i]=editQVal;saveQs(q);setEditingQ(null);toast("Updated."); }

  return(
    <div style={{padding:32}}>
      <h2 style={{fontFamily:H,color:C.navy,fontSize:30,marginBottom:6}}>Settings</h2>
      <p style={{color:C.mid,marginBottom:22}}>Customize required documents, application questions, and interview questions.</p>
      <div style={{display:"flex",gap:5,marginBottom:20}}>
        {[["docs","📋 Required Documents"],["appquestions","📝 Application Questions"],["questions","❓ Interview Questions"]].map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)} style={{
            padding:"9px 18px",borderRadius:8,border:"none",
            background:subTab===id?C.amber:C.border,
            color:subTab===id?C.navy:C.mid,fontWeight:subTab===id?600:400,fontSize:13}}>
            {label}
          </button>
        ))}
      </div>

      {subTab==="docs"&&(
        <div>
          {hasLegacyDocs&&(
            <div style={{marginBottom:16,padding:"14px 18px",background:"#FEF3C7",
              border:"1.5px solid #F59E0B",borderRadius:10,
              display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
              <div>
                <p style={{fontWeight:700,color:"#92400E",fontSize:13,marginBottom:3}}>
                  ⚠️ Legacy documents detected
                </p>
                <p style={{fontSize:12,color:"#92400E",lineHeight:1.6}}>
                  Background Check, Fingerprinting, Maltreatment, and/or CPR docs are still in your
                  checklist. These now have dedicated tabs — click Remove to clean up.
                </p>
              </div>
              <Btn bg="#D97706" color="#fff" onClick={removeLegacyDocs}>
                Remove Legacy Docs
              </Btn>
            </div>
          )}
          <div style={{padding:"11px 14px",background:`${C.sky}11`,border:`1px solid ${C.sky}33`,borderRadius:9,marginBottom:18}}>
            <p style={{fontSize:12,color:C.navy2,lineHeight:1.6}}>
              ℹ️ Three document types are available:<br/>
              <strong>📎 File Upload</strong> — applicant uploads a file (diploma, CPR cert, etc.)<br/>
              <strong>✏️ Typed Form</strong> — applicant fills custom fields on-screen; a signed PDF is auto-generated<br/>
              <strong>📋 Fillable PDF</strong> — upload a PDF template; applicant reviews it in-browser, signs, and saves
            </p>
          </div>
          <Card>
            {docs.map((d,i)=>{
              const meta=DOC_TYPE_META[d.docType]||DOC_TYPE_META.upload;
              const isExpanded=expandedDoc===i;
              return(
                <div key={d.id||i} style={{borderBottom:`1px solid ${C.border}`}}>
                  {/* Doc header row */}
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"14px 20px",cursor:"pointer"}}
                    onClick={()=>setExpandedDoc(isExpanded?null:i)}>
                    <span style={{fontSize:10,fontWeight:600,padding:"2px 8px",borderRadius:20,
                      background:`${meta.color}15`,color:meta.color,whiteSpace:"nowrap"}}>
                      {meta.icon} {meta.label}
                    </span>
                    {editingDoc===i?(
                      <input autoFocus value={docs[i].name}
                        onChange={e=>{const dd=[...docs];dd[i]={...dd[i],name:e.target.value};setDocs(dd);}}
                        onBlur={()=>{saveDocs(docs);setEditingDoc(null);toast("Updated.");}}
                        onKeyDown={e=>e.key==="Enter"&&e.target.blur()}
                        onClick={e=>e.stopPropagation()}
                        style={{flex:1,padding:"5px 10px",borderRadius:6,border:`1.5px solid ${C.amber}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}/>
                    ):(
                      <p style={{flex:1,fontWeight:600,color:C.navy,fontSize:13}}>{d.name}</p>
                    )}
                    <div style={{display:"flex",gap:7,flexShrink:0}} onClick={e=>e.stopPropagation()}>
                      <button onClick={()=>setEditingDoc(i)} style={{background:"none",border:"none",fontSize:14,cursor:"pointer"}}>✏️</button>
                      <button onClick={()=>removeDoc(i)} style={{background:"none",border:"none",fontSize:14,cursor:"pointer"}}>🗑️</button>
                    </div>
                    <span style={{fontSize:14,color:C.mid,transform:isExpanded?"rotate(90deg)":"",transition:"transform .2s"}}>›</span>
                  </div>

                  {/* Expanded doc settings */}
                  {isExpanded&&(
                    <div style={{padding:"0 20px 20px",background:C.light}}>

                      {/* Doc type selector */}
                      <div style={{marginBottom:16}}>
                        <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Document Type</p>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          {Object.entries(DOC_TYPE_META).map(([type,m])=>(
                            <button key={type} onClick={()=>{const dd=[...docs];dd[i]={...dd[i],docType:type};saveDocs(dd);}} style={{
                              padding:"7px 13px",borderRadius:8,border:`2px solid ${d.docType===type?m.color:C.border}`,
                              background:d.docType===type?`${m.color}15`:"#fff",
                              color:d.docType===type?m.color:C.mid,fontFamily:B,fontWeight:600,fontSize:11}}>
                              {m.icon} {m.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Require signature toggle */}
                      {d.docType!=="upload"&&(
                        <label style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,cursor:"pointer"}}>
                          <input type="checkbox" checked={!!d.requireSignature}
                            onChange={e=>{const dd=[...docs];dd[i]={...dd[i],requireSignature:e.target.checked};saveDocs(dd);}}
                            style={{width:15,height:15,accentColor:C.amber,cursor:"pointer"}}/>
                          <span style={{fontSize:12,color:C.navy}}>Require digital signature on this document</span>
                        </label>
                      )}

                      {/* Template upload (for upload + pdf_fill types) */}
                      {(d.docType==="upload"||d.docType==="pdf_fill")&&(
                        <div style={{marginBottom:16}}>
                          <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>
                            {d.docType==="pdf_fill"?"PDF Template (applicant reviews & signs in-browser)":"Instructions / Template PDF (optional download)"}
                          </p>
                          {d.templateUrl?(
                            <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:8}}>
                              <a href={d.templateUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:C.sky}}>📄 View current PDF</a>
                              <button onClick={()=>{const dd=[...docs];dd[i]={...dd[i],templateUrl:null};saveDocs(dd);}}
                                style={{fontSize:11,color:C.red,background:"none",border:"none",cursor:"pointer"}}>Remove</button>
                            </div>
                          ):(
                            <p style={{fontSize:11,color:`${C.mid}88`,marginBottom:8}}>No PDF uploaded yet</p>
                          )}
                          {templateUploads[i]!==undefined&&<div style={{marginBottom:8}}><ProgressBar pct={templateUploads[i]||0}/></div>}
                          <input ref={el=>fileRefs.current[i]=el} type="file" accept=".pdf" style={{display:"none"}}
                            onChange={e=>uploadTemplate(i,e.target.files?.[0])}/>
                          <Btn onClick={()=>fileRefs.current[i]?.click()} size="sm"
                            bg={d.templateUrl?C.navy2:"#fff"} color={d.templateUrl?"#fff":C.mid}
                            style={{border:`1.5px solid ${C.border}`}}
                            disabled={templateUploads[i]!==undefined}>
                            {templateUploads[i]!==undefined?`Uploading ${templateUploads[i]}%…`:d.templateUrl?"Replace PDF":"Upload PDF"}
                          </Btn>
                        </div>
                      )}

                      {/* Signature placement controls — pdf_fill only */}
                      {d.docType==="pdf_fill"&&d.requireSignature&&(
                        <div style={{marginBottom:16}}>
                          <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>
                            Signature Placement on PDF
                          </p>
                          <div style={{padding:"12px 14px",background:`${C.amber}0a`,border:`1px solid ${C.amber}33`,borderRadius:8,marginBottom:10}}>
                            <p style={{fontSize:11,color:C.amber2,lineHeight:1.6}}>
                              💡 PDF coordinates start at the <strong>bottom-left corner</strong> (0,0). For a US Letter page (612×792 pts), the W-4 signature line is approximately <strong>X: 30, Y: 55, Page: 0 (first page)</strong>. Use the defaults and check the output, then adjust here.
                            </p>
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
                            {[
                              {label:"Page #",    key:"signaturePage", placeholder:"0",   note:"0 = first"},
                              {label:"X (left)",  key:"signatureX",    placeholder:"30",  note:"pts from left"},
                              {label:"Y (bottom)",key:"signatureY",    placeholder:"55",  note:"pts from bottom"},
                              {label:"Width",     key:"sigWidth",      placeholder:"200", note:"pts wide"},
                              {label:"Height",    key:"sigHeight",     placeholder:"40",  note:"pts tall"},
                            ].map(({label,key,placeholder,note})=>(
                              <div key={key}>
                                <p style={{fontSize:10,fontWeight:600,color:C.mid,marginBottom:4}}>{label}</p>
                                <input
                                  type="number"
                                  value={d[key]??""} placeholder={placeholder}
                                  onChange={e=>{
                                    const val=e.target.value===""?undefined:Number(e.target.value);
                                    const dd=[...docs]; dd[i]={...dd[i],[key]:val}; saveDocs(dd);
                                  }}
                                  style={{width:"100%",padding:"6px 9px",border:`1.5px solid ${C.border}`,
                                    borderRadius:6,fontFamily:B,fontSize:12,color:C.navy,outline:"none"}}
                                  onFocus={e=>e.target.style.borderColor=C.amber}
                                  onBlur={e=>e.target.style.borderColor=C.border}
                                />
                                <p style={{fontSize:10,color:`${C.mid}88`,marginTop:2}}>{note}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Custom fields editor (form type only) */}
                      {d.docType==="form"&&(
                        <div>
                          <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>Form Fields</p>
                          {(d.fields||[]).map((f,fi)=>(
                            <div key={f.id} style={{display:"flex",gap:8,alignItems:"center",marginBottom:8,background:"#fff",padding:"9px 12px",borderRadius:8,border:`1px solid ${C.border}`,flexWrap:"wrap"}}>
                              <input value={f.label} onChange={e=>updateField(i,fi,"label",e.target.value)}
                                style={{flex:"1 1 160px",padding:"5px 9px",border:`1px solid ${C.border}`,borderRadius:6,fontFamily:B,fontSize:12,color:C.navy,outline:"none"}}
                                onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}
                                placeholder={f.type==="info"?"Instruction text…":f.type==="link"?"Link label (e.g. Employee Handbook)…":"Field label…"}/>
                              {f.type === "link" && (
                                <input value={f.url||""} onChange={e=>updateField(i,fi,"url",e.target.value)}
                                  style={{flex:"1 1 180px",padding:"5px 9px",border:`1px solid ${C.border}`,borderRadius:6,fontFamily:B,fontSize:12,color:C.sky,outline:"none"}}
                                  onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}
                                  placeholder="https://…"/>
                              )}
                              <select value={f.type} onChange={e=>updateField(i,fi,"type",e.target.value)}
                                style={{padding:"5px 8px",border:`1px solid ${C.border}`,borderRadius:6,fontFamily:B,fontSize:11,color:C.navy,outline:"none",flexShrink:0}}>
                                {fieldTypes.map(ft=><option key={ft.value} value={ft.value}>{ft.label}</option>)}
                              </select>
                              <button onClick={()=>removeField(i,fi)}
                                style={{background:"none",border:"none",fontSize:15,color:C.red,cursor:"pointer",flexShrink:0}}>×</button>
                            </div>
                          ))}
                          <button onClick={()=>addField(i)}
                            style={{background:"none",border:`1.5px dashed ${C.border}`,borderRadius:8,padding:"7px 14px",
                              color:C.mid,fontFamily:B,fontSize:12,cursor:"pointer",width:"100%",marginTop:4}}>
                            + Add Field
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add new doc */}
            <div style={{padding:"16px 20px",background:C.light,borderTop:`1px solid ${C.border}`}}>
              <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>Add New Document</p>
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                <input value={newDocName} onChange={e=>setNewDocName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addDoc()}
                  placeholder="Document name…"
                  style={{flex:1,minWidth:160,padding:"9px 13px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:13,color:C.navy,outline:"none"}}
                  onFocus={e=>e.target.style.borderColor=C.amber} onBlur={e=>e.target.style.borderColor=C.border}/>
                <select value={newDocType} onChange={e=>setNewDocType(e.target.value)}
                  style={{padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontFamily:B,fontSize:12,color:C.navy,outline:"none"}}>
                  {Object.entries(DOC_TYPE_META).map(([type,m])=><option key={type} value={type}>{m.icon} {m.label}</option>)}
                </select>
                <Btn onClick={addDoc}>+ Add</Btn>
              </div>
            </div>
          </Card>
        </div>
      )}

      {(subTab==="appquestions"||subTab==="questions")&&(()=>{
        const isApp = subTab==="appquestions";
        const qs = isApp ? (settings.appQuestions||FACTORY_QUESTIONS) : questions;
        const qTypeOpts = [
          {value:"text",     label:"Short Answer"},
          {value:"textarea", label:"Long Answer"},
          {value:"yesno",    label:"Yes / No / Multiple Choice"},
          {value:"upload",   label:"📎 File Upload"},
          {value:"checkbox", label:"Checkbox (agree/acknowledge)"},
        ];
        function saveAppQs(newQs){ onSave({...settings, appQuestions:newQs}); }
        function saveIntQs(newQs){ saveQs(newQs); }
        const save = isApp ? saveAppQs : saveIntQs;

        function updateQ(i, key, val){
          const updated = [...qs]; updated[i]={...updated[i],[key]:val}; save(updated);
        }
        function removeQItem(i){ save(qs.filter((_,j)=>j!==i)); }
        function addQItem(){
          const q={id:`q${Date.now()}`,label:"New question",type:"textarea"};
          save([...qs, q]);
        }
        function moveQ(i, dir){
          const updated=[...qs];
          const swap=i+dir;
          if(swap<0||swap>=updated.length)return;
          [updated[i],updated[swap]]=[updated[swap],updated[i]];
          save(updated);
        }

        return (
          <div>
            <div style={{padding:"11px 14px",background:`${C.sky}11`,border:`1px solid ${C.sky}33`,borderRadius:9,marginBottom:18}}>
              <p style={{fontSize:12,color:C.navy2,lineHeight:1.7}}>
                {isApp
                  ? "These questions appear on the public application form filled out by candidates. Supports short/long text, multiple choice, file uploads, and checkboxes."
                  : "These questions appear in the interview panel for admin staff to record answers. Supports short/long text and multiple choice."}
              </p>
            </div>
            <Card>
              {qs.map((q,i)=>{
                const qMeta = qTypeOpts.find(o=>o.value===q.type)||qTypeOpts[1];
                return (
                  <div key={q.id||i} style={{borderBottom:`1px solid ${C.border}`}}>
                    <div style={{padding:"14px 20px"}}>
                      <div style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:10}}>
                        <span style={{minWidth:22,fontWeight:700,color:C.amber,fontSize:13,paddingTop:8}}>{i+1}.</span>
                        <div style={{flex:1}}>
                          <textarea value={q.label} rows={2}
                            onChange={e=>updateQ(i,"label",e.target.value)}
                            style={{width:"100%",padding:"8px 11px",borderRadius:7,border:`1.5px solid ${C.border}`,
                              fontFamily:B,fontSize:13,color:C.navy,outline:"none",resize:"vertical",lineHeight:1.5}}
                            onFocus={e=>e.target.style.borderColor=C.amber}
                            onBlur={e=>e.target.style.borderColor=C.border}/>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:3,flexShrink:0}}>
                          <button onClick={()=>moveQ(i,-1)} disabled={i===0}
                            style={{background:"none",border:`1px solid ${C.border}`,borderRadius:5,padding:"2px 7px",cursor:"pointer",fontSize:12,color:C.mid,opacity:i===0?0.3:1}}>↑</button>
                          <button onClick={()=>moveQ(i,1)} disabled={i===qs.length-1}
                            style={{background:"none",border:`1px solid ${C.border}`,borderRadius:5,padding:"2px 7px",cursor:"pointer",fontSize:12,color:C.mid,opacity:i===qs.length-1?0.3:1}}>↓</button>
                        </div>
                        <button onClick={()=>removeQItem(i)}
                          style={{background:"none",border:"none",fontSize:18,color:C.red,cursor:"pointer",flexShrink:0,paddingTop:6}}>🗑️</button>
                      </div>
                      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",paddingLeft:30}}>
                        <div>
                          <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>Question Type</p>
                          <select value={q.type} onChange={e=>updateQ(i,"type",e.target.value)}
                            style={{padding:"6px 10px",border:`1.5px solid ${C.border}`,borderRadius:7,fontFamily:B,fontSize:12,color:C.navy,outline:"none"}}>
                            {qTypeOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        {q.type==="yesno"&&(
                          <div style={{flex:1,minWidth:200}}>
                            <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>Choices (comma-separated)</p>
                            <input value={(q.options||["Yes","No"]).join(",")}
                              onChange={e=>updateQ(i,"options",e.target.value.split(",").map(s=>s.trim()).filter(Boolean))}
                              style={{width:"100%",padding:"6px 10px",border:`1.5px solid ${C.border}`,borderRadius:7,fontFamily:B,fontSize:12,color:C.navy,outline:"none"}}
                              onFocus={e=>e.target.style.borderColor=C.amber}
                              onBlur={e=>e.target.style.borderColor=C.border}
                              placeholder="Yes,No,Maybe"/>
                          </div>
                        )}
                        {q.type==="checkbox"&&(
                          <div style={{flex:1,minWidth:200}}>
                            <p style={{fontSize:10,fontWeight:600,color:C.mid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>Checkbox Label</p>
                            <input value={q.checkLabel||q.label}
                              onChange={e=>updateQ(i,"checkLabel",e.target.value)}
                              style={{width:"100%",padding:"6px 10px",border:`1.5px solid ${C.border}`,borderRadius:7,fontFamily:B,fontSize:12,color:C.navy,outline:"none"}}
                              onFocus={e=>e.target.style.borderColor=C.amber}
                              onBlur={e=>e.target.style.borderColor=C.border}
                              placeholder="I agree to…"/>
                          </div>
                        )}
                        <label style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",marginTop:18}}>
                          <input type="checkbox" checked={!!q.required}
                            onChange={e=>updateQ(i,"required",e.target.checked)}
                            style={{width:14,height:14,accentColor:C.amber,cursor:"pointer"}}/>
                          <span style={{fontSize:12,color:C.navy}}>Required</span>
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div style={{padding:"16px 20px",background:C.light}}>
                <button onClick={addQItem}
                  style={{background:"none",border:`1.5px dashed ${C.border}`,borderRadius:8,padding:"9px 18px",
                    color:C.mid,fontFamily:B,fontSize:13,cursor:"pointer",width:"100%"}}>
                  + Add Question
                </button>
              </div>
            </Card>
          </div>
        );
      })()}
    </div>
  );
}

// ─── SETUP TAB ────────────────────────────────────────────────────────────────
function SetupTab({authUser,toast}){
  const [copied,setCopied]=useState("");
  const copy=(text,key)=>{navigator.clipboard.writeText(text).then(()=>{setCopied(key);setTimeout(()=>setCopied(""),2500);});toast("Copied!");};

  const gasScript=`function doPost(e) {
  try {
    var data = JSON.parse(e.postData.contents);
    GmailApp.sendEmail(data.to, data.subject, data.body, {
      name: "Summer Day Camp",
      replyTo: Session.getActiveUser().getEmail(),
    });
    return ContentService.createTextOutput(JSON.stringify({success:true})).setMimeType(ContentService.MimeType.JSON);
  } catch(err) {
    return ContentService.createTextOutput(JSON.stringify({success:false,error:err.toString()})).setMimeType(ContentService.MimeType.JSON);
  }
}
function doGet(e) {
  return ContentService.createTextOutput("Summer Day Camp Email Service — Active").setMimeType(ContentService.MimeType.TEXT);
}`;

  const storageRules=`rules_version = '2';
service firebase.storage {
  match /b/{bucket}/o {
    match /templates/{file} {
      allow read: if request.auth != null;
      allow write: if request.auth != null &&
        firestore.get(/databases/(default)/documents/users/$(request.auth.uid)).data.role == 'admin';
    }
    match /onboarding/{appId}/{file} {
      allow read: if request.auth != null;
      allow write: if request.auth != null && (
        firestore.get(/databases/(default)/documents/users/$(request.auth.uid)).data.role == 'admin' ||
        firestore.get(/databases/(default)/documents/applications/$(appId)).data.uid == request.auth.uid
      );
    }
  }
}`;

  const steps=[
    {title:"1. Activate Admin Account",content:(
      <div>
        <p style={{fontSize:13,color:C.navy,marginBottom:10,lineHeight:1.7}}>
          Your UID: <code style={{background:C.light,padding:"2px 8px",borderRadius:4,fontFamily:"monospace",fontSize:11}}>{authUser.uid}</code>
        </p>
        <ol style={{paddingLeft:18,fontSize:13,color:C.mid,lineHeight:2.2}}>
          <li>Firebase Console → Firestore → <code>users</code> collection</li>
          <li>Find/create document with ID = your UID</li>
          <li>Set field <code>role</code> = <code>"admin"</code></li>
          <li>Sign out and back in</li>
        </ol>
      </div>
    )},
    {title:"2. Enable Firebase Storage",content:(
      <div>
        <ol style={{paddingLeft:18,fontSize:13,color:C.mid,lineHeight:2.2,marginBottom:12}}>
          <li>Firebase Console → Build → Storage → Get started</li>
          <li>Choose a region, start in test mode</li>
          <li>Apply the rules below in the Rules tab</li>
        </ol>
        <div style={{position:"relative"}}>
          <Btn onClick={()=>copy(storageRules,"rules")} size="sm" style={{position:"absolute",top:10,right:10,zIndex:1}}>
            {copied==="rules"?"✓ Copied":"Copy Rules"}
          </Btn>
          <pre style={{background:"#1E293B",color:"#94A3B8",padding:16,borderRadius:9,fontSize:11,fontFamily:"monospace",overflow:"auto",maxHeight:200,lineHeight:1.6}}>{storageRules}</pre>
        </div>
      </div>
    )},
    {title:"3. Google Apps Script (Email)",content:(
      <div>
        <ol style={{paddingLeft:18,fontSize:13,color:C.mid,lineHeight:2.2,marginBottom:12}}>
          <li>Go to <a href="https://script.google.com" target="_blank" rel="noopener noreferrer">script.google.com</a> → New Project</li>
          <li>Paste the script below</li>
          <li>Deploy → New Deployment → Web App · Execute as: Me · Access: Anyone</li>
          <li>URL already configured ✓</li>
        </ol>
        <div style={{position:"relative"}}>
          <Btn onClick={()=>copy(gasScript,"gas")} size="sm" style={{position:"absolute",top:10,right:10,zIndex:1}}>
            {copied==="gas"?"✓ Copied":"Copy Script"}
          </Btn>
          <pre style={{background:"#1E293B",color:"#94A3B8",padding:16,borderRadius:9,fontSize:11,fontFamily:"monospace",overflow:"auto",maxHeight:220,lineHeight:1.6}}>{gasScript}</pre>
        </div>
      </div>
    )},
    {title:"4. Build & Deploy",content:(
      <pre style={{background:"#1E293B",color:"#94A3B8",padding:16,borderRadius:9,fontSize:12,fontFamily:"monospace",lineHeight:1.9}}>
{`npm install
firebase login
npm run build
firebase deploy`}
      </pre>
    )},
    {title:"✅ Checklist",content:(
      <div>
        {["Firebase Auth enabled (Email/Password)",
          "Firestore Database created",
          "Firebase Storage enabled & rules applied",
          "Your user doc has role: 'admin'",
          "GAS URL configured ✓",
          "Admin email set to cody@rogersrecreation.com ✓",
          "npm run build && firebase deploy completed",
        ].map((item,i)=>(
          <div key={i} style={{display:"flex",gap:10,padding:"9px 0",borderBottom:i<6?`1px solid ${C.border}`:"none"}}>
            <span style={{color:C.amber}}>☐</span>
            <span style={{fontSize:13,color:C.navy}}>{item}</span>
          </div>
        ))}
      </div>
    )},
  ];

  return(
    <div style={{padding:32,maxWidth:780}}>
      <h2 style={{fontFamily:H,color:C.navy,fontSize:30,marginBottom:6}}>Setup</h2>
      <p style={{color:C.mid,marginBottom:30}}>Complete these steps to fully activate the portal.</p>
      {steps.map(({title,content})=>(
        <div key={title} style={{marginBottom:26}}>
          <h3 style={{fontFamily:H,color:C.navy,fontSize:18,marginBottom:12}}>{title}</h3>
          <Card style={{padding:22}}>{content}</Card>
        </div>
      ))}
    </div>
  );
}
